/**
 * Shared utility class for managing item selection (checkboxes, select-all, etc.)
 * Used by purchase orders for managing order selection and bulk actions
 */
class SelectionManager {
  constructor(options = {}) {
    this.selectedItems = new Set();
    this.items = [];
    this.selectAllElement = options.selectAllElement;
    this.controlElements = options.controlElements || [];
    this.onSelectionChange = options.onSelectionChange || (() => { });
    this.getItemKey = options.getItemKey || ((item) => item.id);
  }

  setItems(items, options = {}) {
    this.items = items || [];
    if (!options.preserveOffPageSelection) {
      this.validateSelection();
    }
  }

  setSelectAllElement(element) {
    this.selectAllElement = element;
  }

  addControlElement(element) {
    if (element && !this.controlElements.includes(element)) {
      this.controlElements.push(element);
    }
  }

  toggleSelectAll(isSelected) {
    this.selectedItems.clear();

    if (isSelected && this.items.length > 0) {
      this.items.forEach(item => {
        const key = this.getItemKey(item);
        if (key) {
          this.selectedItems.add(key);
        }
      });
    }

    this.updateUI();
    this.onSelectionChange(this.getSelectionState());
  }

  toggleItem(itemKey, isSelected) {
    if (!itemKey) return;

    if (isSelected) {
      this.selectedItems.add(itemKey);
    } else {
      this.selectedItems.delete(itemKey);
    }

    this.updateUI();
    this.onSelectionChange(this.getSelectionState());
  }

  selectItem(itemKey) {
    this.toggleItem(itemKey, true);
  }

  deselectItem(itemKey) {
    this.toggleItem(itemKey, false);
  }

  clearSelection() {
    this.selectedItems.clear();
    this.updateUI();
    this.onSelectionChange(this.getSelectionState());
  }

  getSelectionState() {
    return {
      selectedCount: this.selectedItems.size,
      totalCount: this.items.length,
      selectedItems: Array.from(this.selectedItems),
      hasSelection: this.selectedItems.size > 0,
      isAllSelected: this.selectedItems.size > 0 && this.selectedItems.size === this.items.length
    };
  }

  updateUI() {
    this.syncSelectAllCheckbox();
    this.updateControlStates();
  }

  syncSelectAllCheckbox() {
    if (!this.selectAllElement) return;

    const state = this.getSelectionState();
    const checkbox = this.selectAllElement.querySelector('input[type="checkbox"]');
    if (checkbox) {
      checkbox.checked = state.isAllSelected;
      checkbox.indeterminate = state.hasSelection && !state.isAllSelected;
    }
  }

  updateControlStates() {
    const hasSelection = this.selectedItems.size > 0;

    this.controlElements.forEach(element => {
      if (element.tagName === 'BUTTON') {
        element.disabled = !hasSelection;
      } else if (element.classList.contains('Button')) {
        element.disabled = !hasSelection;
      }
    });
  }

  validateSelection() {
    // Remove selected items that no longer exist in the items array
    const validKeys = new Set(this.items.map(item => this.getItemKey(item)));
    const invalidKeys = Array.from(this.selectedItems).filter(key => !validKeys.has(key));

    invalidKeys.forEach(key => this.selectedItems.delete(key));

    if (invalidKeys.length > 0) {
      this.updateUI();
    }
  }

  static ACCOUNT_CHECKBOX_TEMPLATE_IDS = {
    'checkbox-empty': 'account-checkbox-icon-empty',
    'checkbox-solid': 'account-checkbox-icon-solid',
    'checkbox-mark': 'account-checkbox-icon-mark'
  };

  /**
   * SVG markup from Liquid templates (snippets/account-checkbox-icon-templates.liquid) for string-built HTML.
   */
  static getCheckboxIconHTML(iconType) {
    const templateId = SelectionManager.ACCOUNT_CHECKBOX_TEMPLATE_IDS[iconType];
    if (!templateId) return '';
    const tpl = document.getElementById(templateId);
    if (!tpl?.content) return '';
    const first = tpl.content.firstElementChild;
    if (!first) return '';
    return first.outerHTML;
  }

  /**
   * Clone icon from template#account-checkbox-icon-* and append into target.
   */
  static appendCheckboxIconFromTemplate(targetEl, iconType) {
    const templateId = SelectionManager.ACCOUNT_CHECKBOX_TEMPLATE_IDS[iconType];
    if (!templateId || !targetEl) return;
    const tpl = document.getElementById(templateId);
    if (!tpl?.content) return;
    const first = tpl.content.firstElementChild;
    if (!first) return;
    targetEl.appendChild(first.cloneNode(true));
  }

  // Static method to initialize checkboxes with icons consistently
  static initializeCheckboxIcons(container) {
    if (!container) return;

    const checkboxes = container.querySelectorAll('.custom-checkbox');
    checkboxes.forEach((checkbox) => {
      const emptyIcon = checkbox.querySelector('.checkbox-empty');
      const solidIcon = checkbox.querySelector('.checkbox-solid');

      if (emptyIcon && !emptyIcon.innerHTML.trim()) {
        SelectionManager.appendCheckboxIconFromTemplate(emptyIcon, 'checkbox-empty');
      }

      if (solidIcon && !solidIcon.innerHTML.trim()) {
        SelectionManager.appendCheckboxIconFromTemplate(solidIcon, 'checkbox-solid');
      }
    });
  }
}

class CustomerAuthentication extends HTMLElement {
  constructor() {
    super();
    this.delegateElement = new Delegate(this);
  }

  connectedCallback() {
    this.loginWrapper = this.querySelector('.customer__login');
    this.registerWrapper = this.querySelector('.customer__register');
    this.vendorLoginForm = this.querySelector('#login-form'); // Vendor login form
    this.customerLoginForm = this.querySelector('#login-form-customer'); // Customer login form
    this.loginForm = this.vendorLoginForm || this.customerLoginForm; // Fallback for legacy code
    this.registerForm = this.querySelector('#register-form');
    this.resetPasswordForm = this.querySelector('#reset-password-form');
    this.activateAccountForm = this.querySelector('#activate-account-form');

    // Get login containers
    this.vendorLoginContainer = this.querySelector('.login-container--vendor');
    this.customerLoginContainer = this.querySelector('.login-container--customer');

    // Check view parameter and request path to determine which login to show
    this.handleLoginTypeVisibility();

    this.initValidators();
    this.bindEvents();

    this.updateFormVisibility();
    this.checkForUrlErrors();
    this.handleReturnUrl();
  }

  initValidators() {
    if (this.registerForm) {
      this.registerValidator = new FormValidator(this.registerForm, {
        buttonSelector: '#register-submit-btn',
        disableButton: false,
        fieldSelector: '.field',
        errorClass: 'field--error',
        fieldErrorSelector: '.input-validation',
        activelyValidateAfterSubmit: true
      });

      this.registerValidator.addValidator('register-email', (value, field) => {
        if (field.hasAttribute('required') && (!value || !value.trim())) {
          return TranslationUtils.get('errors.required', 'This field is required.');
        }
        if (value && !this.registerValidator.isValidEmail(value)) {
          return TranslationUtils.get('customer.account.authentication.INVALID_EMAIL_FORMAT', 'Please enter a valid email address.');
        }
        return true;
      });
    }

    if (this.customerLoginForm) {
      this.customerLoginValidator = new FormValidator(this.customerLoginForm, {
        buttonSelector: '#customer-login-submit-btn',
        disableButton: false,
        fieldSelector: '.field',
        errorClass: 'field--error',
        fieldErrorSelector: '.input-validation',
        activelyValidateAfterSubmit: true
      });

      this.customerLoginValidator.addValidator('login-email-customer', (value, field) => {
        if (field.hasAttribute('required') && (!value || !value.trim())) {
          return TranslationUtils.get('errors.required', 'This field is required.');
        }
        if (value && !this.customerLoginValidator.isValidEmail(value)) {
          return TranslationUtils.get('customer.account.authentication.INVALID_EMAIL_FORMAT', 'Please enter a valid email address.');
        }
        return true;
      });
    }

    // Initialize validator for vendor login form (email/password form)
    if (this.vendorLoginForm && this.vendorLoginForm.hasAttribute('data-vendor-login')) {
      this.loginValidator = new FormValidator(this.vendorLoginForm, {
        buttonSelector: '#vendor-login-submit-btn',
        disableButton: false,
        fieldSelector: '.field',
        errorClass: 'field--error',
        fieldErrorSelector: '.input-validation',
        activelyValidateAfterSubmit: true
      });
    }

    // Initialize validator for reset password form
    if (this.resetPasswordForm) {
      this.resetPasswordValidator = new FormValidator(this.resetPasswordForm, {
        buttonSelector: '#reset-password-submit-btn',
        disableButton: false,
        fieldSelector: '.field',
        errorClass: 'field--error',
        fieldErrorSelector: '.input-validation',
        activelyValidateAfterSubmit: true
      });
    }

    // Initialize validator for activate account form
    if (this.activateAccountForm) {
      this.activateAccountValidator = new FormValidator(this.activateAccountForm, {
        buttonSelector: '#activate-account-submit-btn',
        disableButton: false,
        fieldSelector: '.field',
        errorClass: 'field--error',
        fieldErrorSelector: '.input-validation',
        activelyValidateAfterSubmit: true
      });
    }
  }

  bindEvents() {
    this.delegateElement.on('click', '[data-action="toggle-window"]', this.handleToggleWindow.bind(this));
    this.delegateElement.on('click', '[data-action="switch-login-type"]', this.handleSwitchLoginType.bind(this));
  }

  handleLoginTypeVisibility() {
    if (!this.vendorLoginContainer || !this.customerLoginContainer) return;

    // Check URL parameters first (decode before checking)
    const urlParams = new URLSearchParams(window.location.search);
    const modeParam = urlParams.get('mode');
    const decodedModeParam = modeParam ? decodeURIComponent(modeParam) : null;

    // Check return_url parameter and parse its mode param
    const returnUrl = urlParams.get('return_url');
    let returnUrlModeParam = null;
    if (returnUrl) {
      try {
        const decodedReturnUrl = decodeURIComponent(returnUrl);
        // Parse query string from return_url
        const returnUrlParts = decodedReturnUrl.split('?');
        if (returnUrlParts.length > 1) {
          const returnUrlParams = new URLSearchParams(returnUrlParts[1]);
          returnUrlModeParam = returnUrlParams.get('mode');
        }
      } catch (e) {
        console.warn('Failed to parse return_url:', e);
      }
    }

    // Check request path (decode before checking)
    const currentPath = decodeURIComponent(window.location.pathname);
    const pathContainsVendor = currentPath.includes('vendor');

    // Shopify customer_login errors can only originate from the vendor email/password form.
    const hasFormErrors = this.vendorLoginContainer.dataset.hasFormErrors === 'true';
    const showVendorLogin = hasFormErrors ||
      decodedModeParam === 'vendor' ||
      pathContainsVendor ||
      returnUrlModeParam === 'vendor';

    // Show/hide appropriate container using aria-hidden
    if (showVendorLogin) {
      this.vendorLoginContainer.setAttribute('aria-hidden', 'false');
      this.customerLoginContainer.setAttribute('aria-hidden', 'true');

      // Update URL to include /vendor in path if not already there
      if (!pathContainsVendor) {
        const newPath = currentPath.endsWith('/') ? currentPath + 'vendor' : currentPath + '/vendor';
        const searchParams = window.location.search;
        const hash = window.location.hash;
        window.history.replaceState({}, '', newPath + searchParams + hash);
      }
    } else {
      this.vendorLoginContainer.setAttribute('aria-hidden', 'true');
      this.customerLoginContainer.setAttribute('aria-hidden', 'false');
    }
  }

  handleSwitchLoginType(event) {
    event.preventDefault();

    const target = event.target.closest('[data-action="switch-login-type"]');
    const targetType = target?.dataset.target;

    if (targetType === 'customer') {
      // Switch to customer login using aria-hidden
      this.vendorLoginContainer.setAttribute('aria-hidden', 'true');
      this.customerLoginContainer.setAttribute('aria-hidden', 'false');

      // Update URL to remove /vendor
      const currentPath = window.location.pathname;
      const newPath = currentPath.replace(/\/vendor\/?$/, '');
      const searchParams = window.location.search;
      const hash = window.location.hash;
      window.history.replaceState({}, '', (newPath || '/account/login') + searchParams + hash);
    }
  }

  checkForUrlErrors() {
    const urlParams = new URLSearchParams(window.location.search);
    const errorCode = urlParams.get('error');

    if (errorCode) {
      // ErrorUtils.error expects: (namespace, translationKey, fallbackText)
      // So we pass 'customer.account', 'authentication.ERROR_CODE', fallback
      ErrorUtils.error(
        'customer.account',
        `authentication.${errorCode}`,
        'An unexpected error occurred. Please try again.'
      );
    }

    this.clearUrlError();
  }

  handleToggleWindow(event) {
    event.preventDefault();

    const targetUrl = event.target.href;
    if (targetUrl && targetUrl !== window.location.pathname) {
      history.replaceState({ form: targetUrl }, '', targetUrl);
    }

    this.updateFormVisibility();
  }

  updateFormVisibility() {
    if (!this.loginWrapper || !this.registerWrapper) return;

    const currentPath = window.location.pathname;
    const showRegister = currentPath.includes('/register');

    this.loginWrapper.setAttribute('aria-hidden', showRegister ? 'true' : 'false');
    this.registerWrapper.setAttribute('aria-hidden', showRegister ? 'false' : 'true');
  }

  clearUrlError() {
    const url = new URL(window.location);
    if (url.searchParams.has('error')) {
      url.searchParams.delete('error');
      history.replaceState({}, '', url);
    }
  }

  handleReturnUrl() {
    const urlParams = new URLSearchParams(window.location.search);
    const returnUrl = urlParams.get('return_url');

    if (returnUrl) {
      // Check if already encoded (contains % followed by two hex digits)
      const isEncoded = /%[0-9A-Fa-f]{2}/.test(returnUrl);
      const encodedReturnUrl = isEncoded ? returnUrl : encodeURIComponent(returnUrl);

      // Update the hidden inputs in vendor login form
      const vendorLoginReturnUrlInput = this.vendorLoginForm?.querySelector('#login-return-url');
      if (vendorLoginReturnUrlInput) vendorLoginReturnUrlInput.value = encodedReturnUrl;

      // Update the hidden inputs in customer login form
      const customerLoginReturnUrlInput = this.customerLoginForm?.querySelector('#login-return-url');
      if (customerLoginReturnUrlInput) customerLoginReturnUrlInput.value = encodedReturnUrl;

      // Update register form
      const registerReturnUrlInput = this.registerForm?.querySelector('#register-return-url');
      if (registerReturnUrlInput) registerReturnUrlInput.value = encodedReturnUrl;
    }
  }
}

customElements.define("customer-authentication", CustomerAuthentication);

class RedeemTab extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.apiEndpointRedeem = `${window.proxyPath}/redeem`;
  }

  connectedCallback() {
    // Initialize DOM-dependent properties after element is connected
    this.delegateElement = new Delegate(this.element);
    this.notifcationElement = this.element.querySelector('.input-validation');
    this.form = this.element.querySelector('.redeem-form');


    // Set element reference in global voucher utils for DOM operations
    voucherUtils.element = this.element;

    // Log for debugging purposes
    if (!this.notifcationElement) {

    }

    this.initFormValidator();
    this.bindEvents();
  }

  getVoucherCodeFromForm(selector = '[data-action="input-voucher-code"]', fallbackSelector = '#voucherCode') {
    // Override to use this.element instead of global voucherUtils.element
    let input = this.element?.querySelector(selector);
    if (!input && fallbackSelector) {
      input = this.element?.querySelector(fallbackSelector);
    }

    if (input) {
      return input.value.trim();
    }

    console.warn(`Voucher input not found with selectors: ${selector}, ${fallbackSelector}`);
    return null;
  }

  initFormValidator() {
    this.formValidator = new FormValidator(this.form, {
      validateOnInput: false,
      errorClass: 'field--error',
      fieldSelector: '.field',
      buttonSelector: '#redeem-voucher-btn',
      errorOutputSelector: this.notifcationElement,
      showErrorsOnSubmitOnly: false
    });

    const voucherInput = this.form.querySelector('[data-action="input-voucher-code"]');
    if (voucherInput) {
      if (!voucherInput.id) voucherInput.id = 'voucherCode';
      this.formValidator.addValidator(
        voucherInput.id,
        this.validateVoucherCode.bind(this),
        ''
      );
    }
  }

  validateVoucherCode(value) {
    if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
      return TranslationUtils.get('customer.account.redeem.voucher_code_invalid_chars', 'Voucher code can only contain letters and numbers');
    }
    // Validate based on code length without spaces (old vouchers have 10 chars, new ones have more)
    const codeWithoutSpaces = value.replace(/\s/g, '');
    if (codeWithoutSpaces.length < 10) {
      return TranslationUtils.get('customer.account.redeem.voucher_code_too_short', 'Voucher code is too short');
    }
    return true;
  }

  bindEvents() {
    // Bind to both keyup and input events to handle typing and pasting
    this.delegateElement.on('keyup input', '[data-action="input-voucher-code"]', this.handleVoucherCodeInput.bind(this));
    // Close overlay via breadcrumb back inside redeem tab (mobile)
    this.delegateElement.on('click', '.redeem-form__breadcrumbs--fake.not_desktop', this.closeOverlay.bind(this));
    // Close overlay via breadcrumb "Account" link (desktop)
    this.delegateElement.on('click', '.redeem-form__breadcrumbs--fake.not_mobile a[href*="account"]', this.closeOverlay.bind(this));
  }

  handleVoucherCodeInput(event) {
    const input = event.target;
    const value = input.value.trim();

    if (value.length > 0) {
      const formatted = value.replace(/\s/g, '').replace(/(.{4})/g, '$1 ').trim();
      if (formatted !== value) {
        input.value = formatted.toUpperCase();
      }
    }
  }

  // RedeemTab-specific methods

  openGiftPopup(voucherCode) {
    PopupUtils.openGiftPopup(voucherCode);
  }

  openRedeemConfirmPopup() {
    const voucherCode = this.getVoucherCodeFromForm();
    const isGid = voucherCode?.startsWith('gid://shopify/GiftCard/');
    PopupUtils.openRedeemPopup(voucherCode, isGid, this.element);
  }

  closeOverlay(event) {
    event.preventDefault();

    // Clear the voucher code input field
    const voucherInput = this.element.querySelector('[data-action="input-voucher-code"]');
    if (voucherInput) {
      voucherInput.value = '';
    }

    // Clear any validation errors
    if (this.notifcationElement) {
      this.notifcationElement.setAttribute('aria-hidden', 'true');
      this.notifcationElement.innerHTML = '';
    }

    // Remove error class from field
    const field = voucherInput?.closest('.field');
    if (field) {
      field.classList.remove('field--error');
    }

    // Close the overlay
    const root = this.element.closest('.account-page-section') || document.querySelector('.account-page-section');
    if (root) root.classList.remove('redeem-open');
    const redeemSection = document.getElementById('account-redeem-section');
    if (redeemSection) redeemSection.setAttribute('aria-hidden', 'true');
  }
}

customElements.define("redeem-tab", RedeemTab);

// Main account component handles opening redeem overlay per theme patterns
class MainAccount extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegateElement = null;
    this.redeemSection = null;
  }

  connectedCallback() {
    this.delegateElement = new Delegate(this.element);
    this.redeemSection = document.getElementById('account-redeem-section');

    // Set vendor login flag based on account type
    const accountType = this.element.getAttribute('data-account-type');
    if (accountType === 'vendor') {
      localStorage.setItem('isVendorLogin', 'true');

      // Update account links immediately after setting the flag
      if (typeof window.updateVendorAccountLinks === 'function') {
        window.updateVendorAccountLinks();
      }
    }

    this.bindEvents();
  }

  bindEvents() {
    this.delegateElement.on('click', '[data-action="toggle-reedem"]', this.openRedeem.bind(this));
  }

  openRedeem(event) {
    event.preventDefault();
    if (!this.redeemSection) {
      this.redeemSection = document.getElementById('account-redeem-section');
      if (!this.redeemSection) return;
    }
    const root = this.element.closest('.account-page-section') || document.querySelector('.account-page-section');
    if (root) root.classList.add('redeem-open');
    this.redeemSection.setAttribute('aria-hidden', 'false');
  }
}

customElements.define('main-account', MainAccount);

// Shared caching utility class following DRY principles
class ComponentCache {
  constructor(timeout = 5 * 60 * 1000, keyGenerator = null) {
    this.cache = new Map();
    this.cacheTimeout = timeout;
    this.generateKey = keyGenerator || ((...args) => args.join('_'));
  }

  get(key) {
    const cached = this.cache.get(key);
    if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
      return cached.data;
    }
    if (cached) {
      this.cache.delete(key);
    }
    return null;
  }

  set(key, data) {
    this.cache.set(key, {
      data: data,
      timestamp: Date.now()
    });
  }

  clear(pattern = null) {
    if (pattern) {
      const keysToDelete = [];
      for (const [key] of this.cache) {
        if (key.startsWith(pattern + '_')) {
          keysToDelete.push(key);
        }
      }
      keysToDelete.forEach(key => this.cache.delete(key));
    } else {
      this.cache.clear();
    }
  }

  cleanupExpired() {
    const now = Date.now();
    const keysToDelete = [];

    for (const [key, value] of this.cache) {
      if (now - value.timestamp >= this.cacheTimeout) {
        keysToDelete.push(key);
      }
    }

    keysToDelete.forEach(key => this.cache.delete(key));
    return keysToDelete.length;
  }

  getStats() {
    const totalEntries = this.cache.size;
    const now = Date.now();
    let validEntries = 0;
    let expiredEntries = 0;

    for (const [key, value] of this.cache) {
      if (now - value.timestamp < this.cacheTimeout) {
        validEntries++;
      } else {
        expiredEntries++;
      }
    }

    return {
      total: totalEntries,
      valid: validEntries,
      expired: expiredEntries,
      timeout: this.cacheTimeout / 1000
    };
  }
}

// Base class for account sections with tab functionality
class AccountTabsBase extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.tabsContainer = null;
  }

  updateActiveTab(filter) {
    if (!this.tabsContainer) {
      this.tabsContainer = this.element.querySelector('.account-tabs');
    }

    if (this.tabsContainer) {
      this.tabsContainer.querySelectorAll('.account-tab').forEach(t => {
        t.dataset.active = 'false';
      });
      const activeTab = this.tabsContainer.querySelector(`[data-tab="${filter}"]`);
      if (activeTab) {
        activeTab.dataset.active = 'true';
      }
    }
  }
}

// Vendors whose terms & conditions don't cover date changes — customers must
// contact them directly instead of self-cancelling a confirmed booking.
const VENDORS_WITHOUT_CUSTOMER_CANCELLATION = new Set([
  'info@mordmysterium.se'
]);

class AccountVouchers extends AccountTabsBase {
  constructor() {
    super();
    this.cinemaConfig = this.loadCinemaConfig();

    this.detailedPopup = new DetailedProductPopup();

    this.element = this;

    // API configuration
    this.apiEndpoint = `${window.proxyPath}/get-user-gift-cards`;
    this.customerEmail = this.resolveAccountEmailForApi();

    // Cache configuration using shared utility
    // The fourth part is the vendor filter/sort (see getCachedData), so a filtered
    // page never answers for an unfiltered one or the other way round.
    this.cache = new ComponentCache(5 * 60 * 1000, (filter, page, limit, filterSortKey = '') => `${filter}_${page}_${limit}_${filterSortKey}`);

    // Booking status constants
    this.BookingStatus = {
      HIBERNATING: 'HIBERNATING',
      INITIATED: 'INITIATED',
      IN_PROGRESS: 'IN_PROGRESS',
      BOOKED: 'BOOKED',
      COMPLETED: 'COMPLETED',
      CANCELED: 'CANCELED',
      CANCELED_CUSTOMER: 'CANCELED_CUSTOMER',
      CANCELED_VENDOR: 'CANCELED_VENDOR',
    };

    // State management
    this.state = {
      currentFilter: window.account?.isVendor ? 'past-bookings' : 'non-redeemed',
      currentPage: 1,
      loading: false,
      error: null,
      giftCards: [],
      pagination: {},
      hasVoucherAction: false
    };

    this.hasInitialized = false;
    this.currentRequestId = null; // Track current loading request to prevent race conditions
  }

  resolveAccountEmailForApi() {
    if (window.account?.isVendor && window.account?.vendorContactEmail) {
      return window.account.vendorContactEmail;
    }
    return window.account?.customerEmail || window.customerEmail;
  }

  getExcludeSelectors() {
    const excludeSelectors = [
      '.pm__buttons-pay-quantity',
      '.pm__info-note-wrapper',
      '.pdp__variants--grid',
      '.pc__labels-holder'
    ];
    return excludeSelectors;
  }

  // Helper method to check if voucher is in progress
  isVoucherInProgress(card) {
    const status = card.booking_status || card.booking_data?.status;
    return status?.toUpperCase() === this.BookingStatus.IN_PROGRESS;
  }

  isInternalBookingFlow(card) {
    const flow = card?.booking_data?.flow;
    return String(flow || '').toLowerCase() === 'internal';
  }

  getChatWithSupplierLabel() {
    return TranslationUtils.get(
      'customer.account.vouchers.communication.chat_with_supplier',
      'Chat with supplier'
    );
  }

  hasBookingConversation(card) {
    return !!(card?.has_conversation || card?.conversation_id);
  }

  getVendorChatButtonLabel(card) {
    return this.hasBookingConversation(card)
      ? TranslationUtils.get('vendor.vendor_chat.open_chat', 'Open chat')
      : TranslationUtils.get('vendor.vendor_chat.start_chat', 'Start chat');
  }

  getBookingParticipantNames(participant) {
    if (!participant) return [];

    if (Array.isArray(participant)) {
      return participant
        .map((entry) => (typeof entry === 'string' ? entry : entry?.name))
        .filter(Boolean);
    }

    const names = [];
    Object.entries(participant).forEach(([key, value]) => {
      if (/personnummer|personal[_-]?number|personalnumber/i.test(key)) return;
      if (key.startsWith('Label__') && value) {
        names.push(value);
        return;
      }
      if (typeof value === 'string' && value.trim()) {
        names.push(value.trim());
        return;
      }
      if (value && typeof value === 'object' && value.name) {
        names.push(value.name);
      }
    });
    return names;
  }

  getInternalConversationCustomerName(card) {
    const customer = card?.booking_data?.customer;
    const directName = card?.booking_data?.customer_name
      || card?.booking_data?.customerName
      || customer?.name
      || customer?.displayName
      || card?.customer_name
      || card?.customerName;

    if (directName) return directName;

    const participantNames = this.getBookingParticipantNames(card?.booking_data?.participant);
    if (participantNames.length) return participantNames[0];

    return TranslationUtils.get('vendor.vendor_chat.customer_fallback', 'Customer');
  }

  findVoucherByInternalBookingId(bookingId) {
    const id = String(bookingId);
    const giftCards = this.state.giftCards || [];

    for (const item of giftCards) {
      if (item.type === 'giftcard_group' && Array.isArray(item.vouchers)) {
        const found = item.vouchers.find((v) => String(v?.booking_data?.internal_booking_id) === id);
        if (found) return found;
      } else if (String(item?.booking_data?.internal_booking_id) === id) {
        return item;
      }
    }

    return null;
  }

  getInternalConversationDisplayName(card, button = null) {
    const fromButton = button?.dataset?.customerName?.trim();
    if (fromButton) return fromButton;

    if (window.account?.isVendor) {
      return this.getInternalConversationCustomerName(card);
    }
    const vendor = card?.booking_data?.vendor;
    return vendor?.displayName || vendor?.name || card?.productTitle || 'Supplier';
  }

  escapeHtmlAttribute(value) {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/"/g, '&quot;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  getInternalConversationButtonNameAttr(card) {
    if (!card) return '';
    const name = window.account?.isVendor
      ? this.getInternalConversationCustomerName(card)
      : (card?.booking_data?.vendor?.displayName || card?.booking_data?.vendor?.name || card?.productTitle || '');
    if (!name) return '';
    return ` data-customer-name="${this.escapeHtmlAttribute(name)}"`;
  }

  shouldUseBookingRequestCard(card, isInGroup = false) {
    if (isInGroup) return false;

    const isVendor = window.account?.isVendor || false;
    if (isVendor) {
      if (this.state.currentFilter === 'booking-requests') return true;
      if (this.isBookingConfirmed(card) && this.isVendorBookedBookingTab()) return true;
      // The vendor Cancelled tab is served from bookings_external, whose status set
      // includes CANCELED_REQUEST_CUSTOMER. isCompletedOrCanceledStatus omits the
      // request statuses, so those rows fell through to the legacy card.
      if (this.isVendorCompletedCanceledTab()
        && (this.isCompletedOrCanceledStatus(card) || this.isCancellationStatus(card))) return true;
      return false;
    }

    // Customer: all single vouchers use the new card layout
    return true;
  }

  isVendorBookedBookingTab() {
    return ['upcoming-bookings', 'past-bookings'].includes(this.state.currentFilter);
  }

  isVendorCompletedCanceledTab() {
    return ['completed', 'canceled'].includes(this.state.currentFilter);
  }

  getBookingStatusKey(card) {
    return (card?.booking_status || card?.booking_data?.status || '').toUpperCase();
  }

  isCompletedOrCanceledStatus(card) {
    return [
      'COMPLETED',
      'COMPLETED_CARRIED',
      'COMPLETED_CANCELED',
      'CANCELED',
      'CANCELLED',
      'CANCELED_CUSTOMER',
      'CANCELED_VENDOR'
    ].includes(this.getBookingStatusKey(card));
  }

  isCompletedCanceledGroupStatus(status) {
    return [
      'COMPLETED',
      'COMPLETED_CARRIED',
      'COMPLETED_CANCELED',
      'CANCELED',
      'CANCELLED',
      'CANCELED_CUSTOMER',
      'CANCELED_VENDOR',
      'CANCELED_REQUEST_CUSTOMER',
      'CANCELED_REQUEST_VENDOR'
    ].includes(String(status || '').toUpperCase());
  }

  hasConfirmedStyleMeta(card) {
    // A seasonal pause on the experience says nothing about a booking that already
    // exists, so it must not downgrade a confirmed card to the pre-booking layout.
    if (this.isVoucherInProgress(card)) return false;
    return this.isBookingConfirmed(card) || this.isCompletedOrCanceledStatus(card);
  }

  shouldUseBookingRequestGroupCard(group) {
    const groupBookingStatus = group?.booking_status;
    const isGroupBookingStatusNull = groupBookingStatus === null || groupBookingStatus === undefined;

    const isVendor = window.account?.isVendor || false;
    if (isVendor) {
      if (isGroupBookingStatusNull) return false;

      if (this.state.currentFilter === 'booking-requests') {
        const isGroupInProgress = groupBookingStatus === 'IN_PROGRESS' || groupBookingStatus === 'in_progress';
        return isGroupInProgress;
      }

      if (this.isVendorBookedBookingTab()) {
        return String(groupBookingStatus).toUpperCase() === 'BOOKED';
      }

      if (this.isVendorCompletedCanceledTab()) {
        return this.isCompletedCanceledGroupStatus(groupBookingStatus);
      }

      return false;
    }

    // Customer: consolidated card for every group state — Figma covers grouped
    // ready-for-booking (no booking status) as well as booked/completed groups.
    return true;
  }

  getAutomaticBookingLabelHTML(card) {
    const isVendor = window.account?.isVendor || false;
    const isBookingRequestsTab = this.state.currentFilter === 'booking-requests';
    // Vendor-specific button/label rendering is handled by vendor-actions.
    if (this.vendorActions) return '';
    if (!isVendor || !isBookingRequestsTab || !this.isVoucherInProgress(card) || !this.isInternalBookingFlow(card)) {
      return '';
    }
    const label = TranslationUtils.get('customer.account.vouchers.communication.automatic_booking', 'Automatic booking');
    return `<div class="vendor-chat__response-status"><span class="vendor-chat__waiting-text u-p4">${label}</span></div>`;
  }



  connectedCallback() {
    // Initialize DOM-dependent properties after element is connected
    this.delegateElement = new Delegate(this.element);
    this.delegateBody = new Delegate(document.body);
    this.detailedPopup.attachToElement(this.element, this.getExcludeSelectors());
    // DOM elements
    this.tabsContainer = this.element.querySelector('.account-tabs');
    this.contentContainer = this.element.querySelector('.account-tab__content');
    this.vouchersList = this.element.querySelector('.account-vouchers__list');
    this.contentHeader = this.element.querySelector('.account-voucher__content-header');

    // Initialize shared checkbox icons
    SelectionManager.initializeCheckboxIcons(this.element);

    this.bindEvents();
    this.ensureSeasonalityPopup();

    // Listen for tab-activated event to load data only when needed
    this.addEventListener('tab-activated', this.handleTabActivated.bind(this));

    // Only initialize if this tab is currently active
    const isCurrentlyActive = !this.hasAttribute('hidden');
    if (isCurrentlyActive) {
      this.initialize();
    }
  }

  handleTabActivated() {
    if (!this.hasInitialized) {
      this.initialize();
      return;
    }
    if (this.state.loading) {
      return;
    }
    // If already initialized, refresh the view with cached data or load fresh if needed
    this.loadVouchers(this.state.currentPage, 0, this.state.currentFilter);
  }

  bindEvents() {
    // Consolidated event binding pattern
    const eventMappings = [
      ['load-more', this.handleLoadMore],
      ['prev-page', this.handlePrevPage],
      ['next-page', this.handleNextPage],
      ['page-number', this.handlePageNumber],
      ['retry-load', this.handleRetry],
      ['change-tab', this.handleChangeTab],
      ['exchange-experience', this.handleExchangeExperience],
      ['book-experience', this.handleBookExperience],
      ['claim-cinema-code', this.handleClaimCinemaCode],
      ['show-cinema-code', this.handleShowCinemaCode],
      ['cancel-group-request', this.handleCancelGroupRequest],
      ['cancel-group-booking', this.handleCancelGroupBooking],
      ['chat-supplier', this.handleChatWithSupplier],
    ];

    eventMappings.forEach(([action, handler]) => {
      this.delegateElement.on('click', `[data-action="${action}"]`, handler.bind(this));
    });

    // Bind copy-cinema-code to document.body since popup is outside this element
    this.delegateBody.on('click', '[data-action="copy-cinema-code"]', this.handleCopyCinemaCode.bind(this));

    // Same for ungroup: the button exists both on the group card (inside this
    // element) and in the exchange-requires-ungroup popup (outside it). Bound
    // on body only — binding both delegates would fire it twice from the card.
    this.delegateBody.on('click', '[data-action="ungroup-vouchers"]', this.handleUngroupVouchers.bind(this));



    // Handle vendor logout and clear JWT cookies before redirect
    this.delegateElement.on('click', 'a[href*="account/logout"]', (e) => {
      const accountElement = document.querySelector('main-account[data-account-type="vendor"]');
      if (!accountElement) return;

      e.preventDefault();
      const logoutLink = e.target.closest('a');
      const logoutUrl = logoutLink ? logoutLink.getAttribute('href') : '/account/logout';

      const jwtCookies = ['vendor_jwt_token', 'zendesk_jwt_token'];
      jwtCookies.forEach((cookieName) => {
        document.cookie = `${cookieName}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
      });

      window.location.href = logoutUrl;
    });

    // Listen for voucher action completion events from VoucherActionHandler
    // Bind event handlers and store references for proper cleanup
    this.boundHandleVoucherActionCompleted = this.handleVoucherActionCompleted.bind(this);
    this.boundHandleVoucherGroupingUpdate = this.handleVoucherGroupingUpdate.bind(this);
    this.boundHandleVisibilityChange = this.handleVisibilityChange.bind(this);
    this.boundHandlePopState = this.handlePopState.bind(this);

    document.addEventListener('voucher-action-completed', this.boundHandleVoucherActionCompleted);
    document.addEventListener('voucher-grouping-update', this.boundHandleVoucherGroupingUpdate);

    window.globalPopup.onPopupOpen = this.handlePopupOpen.bind(this);
    // Note: Popup close handling is now managed by VoucherActionHandler in voucher-actions.js

    // Listen for page visibility changes to manage cache
    document.addEventListener('visibilitychange', this.boundHandleVisibilityChange);

    // Listen for browser back/forward buttons
    window.addEventListener('popstate', this.boundHandlePopState);
  }

  handlePopupOpen(popup) {
    const popupVoucherActions = ['account-gift-popup', 'redeem-confirm-popup'];

    if (popupVoucherActions.includes(popup.id)) {
      const voucherIdentifier = popup.dataset.voucherIdentifier;
      if (voucherIdentifier) {
        this.setState({ hasVoucherAction: true });
      }
    }
  }

  async initialize() {
    if (!this.customerEmail) {
      console.error('Customer email not available');
      this.showError('Customer email not available');
      return;
    }

    if (!window.apiService) {
      console.error('window.apiService not available');
      this.showError('API service not available');
      return;
    }

    // Check if user is returning from exchange flow (e.g., after Qliro payment)
    try {
      const exchangeData = localStorage.getItem('exchangeGiftCard');
      if (exchangeData) {
        const data = JSON.parse(exchangeData);
        // Check if this is a completed exchange flow (not just initiated)
        const urlParams = new URLSearchParams(window.location.search);
        const currentVoucherFilter = urlParams.get('vouchers');

        // If exchange data exists and we're not already on redeemed tab, redirect
        if (data.exchangeInitiated && currentVoucherFilter !== 'redeemed') {

          localStorage.removeItem('exchangeGiftCard');

          // Notify cache invalidation for gift cards (exchange completed via Qliro)
          const event = new CustomEvent('voucher-action-completed', {
            detail: { action: 'exchange' }
          });
          document.dispatchEvent(event);

          // Set success flag for after redirect (e.g., from Qliro payment completion)
          try {
            sessionStorage.setItem('exchange-success', 'true');
          } catch (e) {
            console.warn('Failed to set exchange success flag:', e);
          }
          window.location.href = '/account?vouchers=redeemed';
          return;
        }

        // If we're already on the redeemed tab, just clear the flag and invalidate cache
        if (data.exchangeInitiated && currentVoucherFilter === 'redeemed') {
          localStorage.removeItem('exchangeGiftCard');

          // Notify cache invalidation for gift cards (already on correct tab)
          const event = new CustomEvent('voucher-action-completed', {
            detail: { action: 'exchange' }
          });
          document.dispatchEvent(event);
        }
      }
    } catch (error) {
      console.warn('Error checking exchange completion:', error);
    }

    // Check if user is returning from extend flow (e.g., after Qliro payment)
    try {
      const extendData = localStorage.getItem('extendGiftCard');
      if (extendData) {
        const data = JSON.parse(extendData);
        // Check if this is a completed extend flow (not just initiated)

        if (data.extendInitiated) {


          // Get the context URL to redirect to
          const contextUrl = data.contextUrl || '/account';

          // Clear the extend data
          localStorage.removeItem('extendGiftCard');

          // Notify cache invalidation for gift cards (extend completed via Qliro)
          const event = new CustomEvent('voucher-action-completed', {
            detail: { action: 'extend' }
          });
          document.dispatchEvent(event);

          // Add success parameter to URL instead of using sessionStorage
          const separator = contextUrl.includes('?') ? '&' : '?';
          const redirectUrl = `${contextUrl}${separator}extend_success=true`;

          // Redirect back to the original context URL with success parameter
          window.location.href = redirectUrl;
          return;
        }
      }
    } catch (error) {
      console.warn('Error checking extend completion:', error);
    }

    // Check for exchange success flag and show success message
    try {
      if (sessionStorage.getItem('exchange-success') === 'true') {
        sessionStorage.removeItem('exchange-success');
        // Show success message after page has loaded
        setTimeout(() => {
          ErrorUtils.success('customer.account.exchange', 'exchange_success', 'Exchange completed successfully! Your voucher has been exchanged for the new product.');
        }, 500);
      }
    } catch (error) {
      console.warn('Error checking exchange success flag:', error);
    }

    // Check for extend success parameter and show success message
    try {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('extend_success') === 'true') {
        // Show success message after page has loaded
        setTimeout(() => {
          ErrorUtils.success('customer.account.extend', 'extend_success', 'Extended protection activated successfully! Your voucher expiration date has been extended.');
        }, 500);

        // Clean up URL by removing the success parameter
        urlParams.delete('extend_success');
        const newUrl = `${window.location.pathname}${urlParams.toString() ? '?' + urlParams.toString() : ''}`;
        history.replaceState({}, '', newUrl);
      }
    } catch (error) {
      console.warn('Error checking extend success parameter:', error);
    }

    // Initialize supplier actions helper only for vendor accounts
    if (window.account?.isVendor && typeof VendorActionsHelper !== 'undefined') {
      this.vendorActions = new VendorActionsHelper(this);

      // Register vendor-specific group action handlers now that vendorActions exists
      this.delegateElement.on('click', '[data-action="vendor-group-cancel"]', this.vendorActions.handleVendorGroupCancel.bind(this.vendorActions));
      this.delegateElement.on('click', '[data-action="vendor-group-complete"]', this.vendorActions.handleVendorGroupComplete.bind(this.vendorActions));

      // Initialize filter/sort manager for vendor accounts
      if (typeof VendorFilterSortManager !== 'undefined') {
        this.vendorFilterSort = new VendorFilterSortManager(this);
      } else {
        this.vendorFilterSort = null;
      }
    } else {
      this.vendorActions = null;
      this.vendorFilterSort = null;
    }

    // Clean up expired cache entries on initialization
    this.cleanupExpiredCache();

    // Clear all cache to ensure fresh data
    this.cache.clear();

    // Check URL for vouchers parameter and set initial filter
    this.initializeFromURL();

    // Mark as initialized but don't load data yet - wait for tab activation
    this.hasInitialized = true;

    // Check if this tab is currently active, if so load data
    const urlParams = new URLSearchParams(window.location.search);
    const currentView = urlParams.get('view') || 'vouchers'; // default to vouchers
    const tabContent = this.closest('[data-tab-content]');
    const isActiveTab = tabContent && tabContent.dataset.tabContent === currentView;

    if (isActiveTab || currentView === 'vouchers' || currentView === 'manage-bookings') {
      await this.loadVouchers(this.state.currentPage, 0, this.state.currentFilter);
    } else {
      // Show initial loading state without actual loading
      this.setState({ loading: false });
    }
  }

  // Initialize filter and page from URL parameters
  initializeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const vouchersFilter = urlParams.get('vouchers');
    const pageParam = parseInt(urlParams.get('page'), 10);
    const initialPage = pageParam > 0 ? pageParam : 1;
    const validFilters = ['non-redeemed', 'redeemed', 'booked', 'expired', 'non_active', 'non-active', 'completed', 'canceled', 'past-bookings', 'upcoming-bookings', 'booking-requests'];

    if (vouchersFilter && validFilters.includes(vouchersFilter)) {
      this.setState({ currentFilter: vouchersFilter, currentPage: initialPage });
      this.updateActiveTab(vouchersFilter);
      this.updateHeaderVisibility(vouchersFilter);

      // Update supplier controls visibility based on URL filter
      if (this.vendorActions) {
        this.vendorActions.updateControlsVisibility();
      }
    } else {
      // Initialize header visibility for default filter
      if (initialPage > 1) {
        this.setState({ currentPage: initialPage });
      }
      this.updateHeaderVisibility(this.state.currentFilter);
    }
  }


  // Cache management methods using shared utility
  getCachedData(filter, page, limit) {
    // Include filter/sort params in cache key for vendor accounts
    let key = this.cache.generateKey(filter, page, limit);
    if (this.vendorFilterSort && window.account?.isVendor) {
      const filterSortParams = this.vendorFilterSort.getQueryParams();
      const filterSortKey = JSON.stringify(filterSortParams);
      key = this.cache.generateKey(filter, page, limit, filterSortKey);
    }
    return this.cache.get(key);
  }

  setCachedData(filter, page, limit, data) {
    // Include filter/sort params in cache key for vendor accounts
    let key = this.cache.generateKey(filter, page, limit);
    if (this.vendorFilterSort && window.account?.isVendor) {
      const filterSortParams = this.vendorFilterSort.getQueryParams();
      const filterSortKey = JSON.stringify(filterSortParams);
      key = this.cache.generateKey(filter, page, limit, filterSortKey);
    }
    this.cache.set(key, data);
  }

  clearCache(pattern = null) {
    this.cache.clear(pattern);
  }

  clearCacheForFilter(filter) {
    this.cache.clear(filter);
  }

  async loadVouchers(page = 1, retryCount = 0, filter = this.state.currentFilter) {
    const limit = 12;

    // Check cache first
    const cachedData = this.getCachedData(filter, page, limit);
    if (cachedData) {
      // Verify filter still matches before updating (in case user switched tabs)
      if (filter === this.state.currentFilter) {
        this.updateStateWithData(cachedData, page, filter);
      }
      return;
    }

    this.setState({ loading: true, error: null });

    // Create a unique request ID for this load operation
    const requestId = `${filter}-${page}-${Date.now()}`;
    this.currentRequestId = requestId;

    try {
      // Build query parameters for GET request
      const params = new URLSearchParams({
        email: this.customerEmail,
        page: page.toString(),
        limit: limit.toString(),
        filter: filter,
        is_supplier: (window.account?.isVendor || false).toString()
      });

      // Add filter and sort parameters for vendor accounts
      if (this.vendorFilterSort && window.account?.isVendor) {
        const filterSortParams = this.vendorFilterSort.getQueryParams();
        Object.keys(filterSortParams).forEach(key => {
          params.append(key, filterSortParams[key]);
        });
      }

      const response = await window.apiService.get(`${this.apiEndpoint}?${params}`);

      // Check if this request is still valid (user hasn't switched tabs)
      if (this.currentRequestId !== requestId) {

        return;
      }

      // Double-check the filter matches current state
      if (filter !== this.state.currentFilter) {

        return;
      }

      if (response && response.giftCards) {
        const giftCards = response.giftCards;

        // Cache the successful response
        this.setCachedData(filter, page, limit, {
          giftCards,
          pagination: response.pagination
        });

        // Store redeemed vouchers in localStorage for booking access
        if (filter === 'redeemed') {
          this.storeRedeemableVouchers(giftCards);
        }

        this.updateStateWithData({
          giftCards,
          pagination: response.pagination
        }, page, filter);
      } else {
        console.error('Invalid response format:', response);
        throw new Error('Invalid response format');
      }
    } catch (error) {
      // Check if request is still valid before handling error
      if (this.currentRequestId !== requestId) {

        return;
      }

      console.error('Error loading vouchers:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        retryCount,
        page,
        filter
      });

      // Retry logic for network errors
      if (retryCount < 2 && (error.message.includes('network') || error.message.includes('fetch'))) {

        setTimeout(() => {
          // Only retry if filter still matches
          if (filter === this.state.currentFilter) {
            this.loadVouchers(page, retryCount + 1, filter);
          }
        }, 1000 * (retryCount + 1)); // Exponential backoff
        return;
      }

      this.handleLoadError(error);
    }
  }

  storeRedeemableVouchers(giftCards) {
    try {
      const vouchersByToken = {};

      giftCards.forEach(item => {
        // Check if this is a grouped voucher
        if (item.type === 'giftcard_group' && item.vouchers) {
          // Handle grouped vouchers
          const groupGiftCardIds = item.vouchers.map(v => v.shopify_gift_card_id);

          item.vouchers.forEach(voucher => {
            const bookingToken = voucher.bookingToken;
            if (bookingToken) {
              vouchersByToken[bookingToken] = {
                productId: voucher.product_id,
                variantId: voucher.product_variant_id,
                giftCardId: voucher.shopify_gift_card_id,
                currentBalance: voucher.current_balance,
                originalVariantPrice: voucher.original_variant_price ?? null,
                campaignSlug: voucher.campaign_slug || null,
                campaignDiscount: voucher.campaign_discount || null,
                extendedProtection: voucher.extended_protection || false,
                expiresAt: voucher.expires_at || item.expires_at || null,
                group_id: item.group_id,
                group_booking: groupGiftCardIds
              };
            }
          });
        } else {
          // Handle individual vouchers
          const bookingToken = item.bookingToken;
          if (bookingToken) {
            vouchersByToken[bookingToken] = {
              productId: item.product_id,
              variantId: item.product_variant_id,
              giftCardId: item.shopify_gift_card_id,
              currentBalance: item.current_balance,
              originalVariantPrice: item.original_variant_price ?? null,
              campaignSlug: item.campaign_slug || null,
              campaignDiscount: item.campaign_discount || null,
              extendedProtection: item.extended_protection || false,
              expiresAt: item.expires_at || null
            };
          }
        }
      });

      localStorage.setItem('redeemable_vouchers', JSON.stringify(vouchersByToken));

    } catch (error) {
      console.error('Failed to store redeemable vouchers in localStorage:', error);
    }
  }

  isCancellationHandled(card) {
    const raw = card?.cancellation_handled ?? card?.cancellationHandled;
    return raw === true || raw === 'true';
  }

  sortCanceledGiftCardsByHandledPriority(giftCards) {
    if (!Array.isArray(giftCards) || giftCards.length === 0) {
      return giftCards;
    }

    const unhandled = [];
    const handled = [];

    giftCards.forEach((item) => {
      const isUnhandled = item.type === 'giftcard_group' && Array.isArray(item.vouchers)
        ? item.vouchers.some((voucher) => !this.isCancellationHandled(voucher))
        : !this.isCancellationHandled(item);

      if (isUnhandled) {
        unhandled.push(item);
      } else {
        handled.push(item);
      }
    });

    // Preserve API sort order within each bucket; only move unhandled to the front.
    return [...unhandled, ...handled];
  }

  updateStateWithData(data, page, filter) {
    let giftCards = data.giftCards;
    if (filter === 'canceled') {
      giftCards = this.sortCanceledGiftCardsByHandledPriority(giftCards);
    }

    this.setState({
      giftCards,
      pagination: data.pagination,
      currentPage: page,
      currentFilter: filter,
      loading: false
    });

    this.render();
  }

  handleLoadError(error) {
    this.setState({
      error: error.message || 'Failed to load vouchers',
      loading: false
    });
    this.showError(this.state.error);
  }

  setState(newState) {
    this.state = { ...this.state, ...newState };
  }

  getPaginationContainer() {
    return this.element.querySelector('.account-vouchers__pagination');
  }

  scrollToTop() {
    // Scroll to the main tabs so the user keeps tab context
    const mainTabs = document.querySelector('.account-main-tabs');
    if (mainTabs) {
      mainTabs.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      this.element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handleChangeTab(event) {
    // Ensure we get the tab element, not a child element like icon or text
    const tab = event.target.closest('[data-action="change-tab"]');
    if (!tab) return;

    const filter = tab.dataset.tab;
    if (!filter) return;

    const previousFilter = this.state.currentFilter;

    // Update state with new filter FIRST
    this.setState({ currentFilter: filter });

    if (this.vendorFilterSort) {
      this.vendorFilterSort.applyTabSortDefaults(filter, { resetFilters: previousFilter !== filter });
    }

    // Update active tab
    this.updateActiveTab(filter);

    // Update header visibility for the new tab
    this.updateHeaderVisibility(filter);

    // Smart controls visibility management during tab switch
    if (this.vendorActions) {
      const filterSortTabs = ['past-bookings', 'upcoming-bookings', 'booking-requests', 'completed', 'canceled'];
      const shouldShowPrevious = filterSortTabs.includes(previousFilter);
      const shouldShowNew = filterSortTabs.includes(filter);



      // Only hide immediately if switching FROM booked TO non-booked
      if (shouldShowPrevious && !shouldShowNew) {
        this.vendorActions.hideControlsDuringLoad();
      }
      // If switching FROM non-booked TO booked, don't show immediately - wait for content
      // If staying on same type (booked->booked or non-booked->non-booked), do nothing here
    }

    // Only clear cache if there's been a voucher action since last tab switch
    // This preserves cache when switching tabs if no actions have occurred
    if (previousFilter !== filter && this.state.hasVoucherAction) {
      this.clearCacheForFilter(previousFilter);
      // Reset the action flag since we've cleared cache
      this.setState({ hasVoucherAction: false });
    }

    // Clear pagination container to remove existing pagination
    const paginationContainer = this.getPaginationContainer();
    if (paginationContainer) {
      paginationContainer.innerHTML = '';
    }

    // Load vouchers for new filter
    this.loadVouchers(1, 0, filter);
    this.updateURL(filter);
  }

  updateHeaderVisibility(filter) {
    // Show the new tab's header straight away. Its content is static, so
    // waiting for the vouchers to load only made the header pop in late.
    const headerContainers = this.element.querySelectorAll('[data-tab-header]');
    headerContainers.forEach((container) => {
      const headerTabContent = container.dataset.tabHeader;
      const isMatch = headerTabContent === filter;

      if (isMatch) {
        container.setAttribute('aria-hidden', 'false');
        container.style.display = '';
      } else {
        container.setAttribute('aria-hidden', 'true');
        container.style.display = 'none';
      }
    });
  }

  handleExchangeExperience(event) {
    event.preventDefault();

    // If extend popup/flow is active, do not initiate exchange
    try {
      const extendPopup = document.querySelector('#extend-voucher-popup');
      const extendActive = extendPopup && (
        extendPopup.dataset?.popupActive === 'true' ||
        extendPopup.getAttribute('aria-expanded') === 'true'
      );
      if (extendActive) {
        console.warn('Extend flow active; skipping exchange initiation');
        return;
      }
    } catch (e) {
      // ignore detection errors
    }

    const button = event.target.closest('button');
    const voucherId = button?.dataset.voucherId;

    if (!voucherId) {
      console.error('No voucher ID found for exchange');
      ErrorUtils.error('customer.account.vouchers', 'notifications.exchange_error', 'Could not start exchange. Please refresh and try again.');
      return;
    }

    // Check if exchange is enabled
    if (!window.account?.enableVoucherExchange) {
      console.error('Voucher exchange is not enabled');
      ErrorUtils.error('customer.account.vouchers', 'notifications.exchange_error', 'Voucher exchange is not available.');
      return;
    }

    if (!window.account?.exchangeCollectionUrl) {
      console.error('Exchange collection URL not configured');
      ErrorUtils.error('customer.account.vouchers', 'notifications.exchange_error', 'Exchange is not configured. Please contact support.');
      return;
    }

    // Find the voucher data from the current loaded vouchers
    const voucherCard = this.findVoucherById(voucherId);
    if (!voucherCard) {
      console.error('Voucher data not found for exchange');
      ErrorUtils.error('customer.account.vouchers', 'notifications.exchange_error', 'Voucher not found. Please refresh the page and try again.');
      return;
    }

    // Exchanging one voucher out of a group would silently leave the rest of
    // the group pointing at a different experience with no way to tell from
    // the group card. Require ungrouping first instead.
    if (voucherCard.group_id) {
      this.openExchangeRequiresUngroupPopup(voucherCard.group_id);
      return;
    }

    // Store voucher info in localStorage for exchange process
    const exchangeData = {
      voucherId: voucherId,
      voucherName: voucherCard.productTitle,
      originalValue: voucherCard.current_balance, // Use current balance for exchange calculations
      variantId: voucherCard.product_variant_id, // Keep as-is from backend
      customerEmail: voucherCard.customer_email,
      timestamp: Date.now(),
      exchangeInitiated: true
    };

    try {
      localStorage.setItem('exchangeGiftCard', JSON.stringify(exchangeData));


      // Dispatch event for any listeners
      try {
        this.dispatchEvent(new CustomEvent('exchange:initialized', { bubbles: true, detail: exchangeData }));
      } catch (e) {
        console.warn('Failed to dispatch exchange:initialized event', e);
      }

      // Redirect to exchange collection
      window.location.href = window.account.exchangeCollectionUrl;
    } catch (error) {
      console.error('Failed to store exchange data in localStorage:', error);
      // You could show an error message to the user here
    }
  }

  openExchangeRequiresUngroupPopup(groupId) {
    const popup = document.querySelector('#exchange-requires-ungroup-popup');
    if (!popup) {
      console.error('Exchange-requires-ungroup popup not found');
      return;
    }

    // handleUngroupVouchers reads the group id off the clicked button itself,
    // so stamp it onto this popup's Ungroup button before opening.
    const ungroupBtn = popup.querySelector('[data-action="ungroup-vouchers"]');
    if (ungroupBtn) {
      ungroupBtn.dataset.groupId = groupId;
    }

    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      window.globalPopup.openPopup(popup);
    } else {
      console.error('Global popup manager not available');
    }
  }

  async handleClaimCinemaCode(event) {
    event.preventDefault();


    const button = event.target.closest('[data-action="claim-cinema-code"]');
    const bookingToken = button?.dataset.bookingToken;
    const voucherId = button?.dataset.voucherId;

    if (!bookingToken) {
      console.error('Missing booking token');
      ErrorUtils.error('customer.account.vouchers', 'notifications.cinema_claim_error', 'Failed to claim cinema code. Please try again.');
      return;
    }

    // Guard against a second claim for the same booking. button.disabled alone
    // is not enough: it lives on an element the list can replace while the
    // request is in flight, and the backend rejects a repeat with
    // CODES_ALREADY_ASSIGNED rather than ignoring it.
    this.claimingCinemaCodes = this.claimingCinemaCodes || new Set();
    if (this.claimingCinemaCodes.has(bookingToken)) {
      return;
    }
    this.claimingCinemaCodes.add(bookingToken);

    // Show loading state
    const originalText = button.querySelector('.ButtonText')?.textContent;
    const buttonText = button.querySelector('.ButtonText');
    if (buttonText) {
      buttonText.textContent = TranslationUtils.get('customer.account.vouchers.claiming_code', 'Claiming code...');
    }
    button.disabled = true;

    try {
      // Call the cinema code claim API with bookingToken only (type is determined by backend from product ID)
      const response = await fetch(`${window.proxyPath}/booking/cinema/claim/${bookingToken}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const result = await response.json();

      if (!response.ok) {
        // Handle specific error case where codes are already assigned
        if (result.error === 'CODES_ALREADY_ASSIGNED' || result.message?.includes('already has') || result.message?.includes('already assigned')) {
          throw new Error(result.message || 'This gift card already has cinema codes assigned. Maximum of 2 codes allowed.');
        }
        throw new Error(result.message || 'Failed to claim cinema code');
      }

      // Success! Show the cinema booking popup with the code


      // Find the voucher card to get product title and gift card ID
      const voucherCard = this.findVoucherById(voucherId);

      // Show the cinema booking popup with both codes
      this.showCinemaBookingPopup(result.data.codes, voucherCard);

      // Immediately update the button to "Show Code" in the DOM
      this.replaceBookExperienceWithShowCode(button, result.data.codes, voucherId);

    } catch (error) {
      console.error('Failed to claim cinema code:', error);

      // Reset button state
      if (buttonText && originalText) {
        buttonText.textContent = originalText;
      }
      button.disabled = false;

      ErrorUtils.error('customer.account.vouchers', 'notifications.cinema_claim_error', error.message || 'Failed to claim cinema code. Please try again.');
      // Released only on failure: after a successful claim the voucher is
      // booked and must not be claimable again in this session.
      this.claimingCinemaCodes.delete(bookingToken);
    }
  }

  showCinemaBookingPopup(cinemaCodes, voucherCard) {
    const popup = document.getElementById('cinema-booking-popup');
    if (!popup) {
      console.error('Cinema booking popup not found');
      return;
    }

    // Populate popup with data
    const voucherNameEl = popup.querySelector('.cinema-booking-popup__voucher-name');
    const giftCardIdEl = popup.querySelector('.cinema-booking-popup__giftcard-id');
    const codeEl1 = popup.querySelector('.cinema-booking-popup__code--1');
    const codeEl2 = popup.querySelector('.cinema-booking-popup__code--2');
    const codeEl3 = popup.querySelector('.cinema-booking-popup__code--3');
    const codeEl4 = popup.querySelector('.cinema-booking-popup__code--4');

    if (voucherNameEl && voucherCard?.productTitle) {
      voucherNameEl.textContent = voucherCard.productTitle;
    }

    if (giftCardIdEl && voucherCard?.shopify_gift_card_id) {
      // Extract numeric ID from GID for display
      const giftCardId = voucherCard.shopify_gift_card_id.split('/').pop();
      giftCardIdEl.textContent = `#${giftCardId}`;
    }

    // Set movie codes (codes 1 and 2)
    if (codeEl1 && cinemaCodes && cinemaCodes[0]) {
      codeEl1.textContent = cinemaCodes[0];
      codeEl1.dataset.cinemaCode = cinemaCodes[0]; // Store for copy functionality
    }

    // Handle second movie code - show if exists, hide if not
    const codeContainer2 = codeEl2?.closest('.cinema-booking-popup__code-container');
    if (codeEl2 && cinemaCodes && cinemaCodes[1]) {
      codeEl2.textContent = cinemaCodes[1];
      codeEl2.dataset.cinemaCode = cinemaCodes[1]; // Store for copy functionality
      // Show second code container
      if (codeContainer2) codeContainer2.style.display = 'flex';
    } else if (codeContainer2) {
      // Hide second code container if only one code
      codeContainer2.style.display = 'none';
    }

    // Handle snack codes (codes 3 and 4)
    const snackCodesSection = popup.querySelector('.cinema-booking-popup__snack-codes');
    const hasSnackCodes = cinemaCodes && (cinemaCodes[2] || cinemaCodes[3]);

    if (hasSnackCodes && snackCodesSection) {
      // Show snack codes section
      snackCodesSection.style.display = 'block';

      // Populate snack code 3
      const codeContainer3 = codeEl3?.closest('.cinema-booking-popup__code-container');
      if (codeEl3 && cinemaCodes[2]) {
        codeEl3.textContent = cinemaCodes[2];
        codeEl3.dataset.cinemaCode = cinemaCodes[2]; // Store for copy functionality
        if (codeContainer3) codeContainer3.style.display = 'flex';
      } else if (codeContainer3) {
        codeContainer3.style.display = 'none';
      }

      // Populate snack code 4
      const codeContainer4 = codeEl4?.closest('.cinema-booking-popup__code-container');
      if (codeEl4 && cinemaCodes[3]) {
        codeEl4.textContent = cinemaCodes[3];
        codeEl4.dataset.cinemaCode = cinemaCodes[3]; // Store for copy functionality
        if (codeContainer4) codeContainer4.style.display = 'flex';
      } else if (codeContainer4) {
        codeContainer4.style.display = 'none';
      }
    } else if (snackCodesSection) {
      // Hide snack codes section if no snack codes
      snackCodesSection.style.display = 'none';
    }

    // Open the popup
    if (window.globalPopup) {
      window.globalPopup.openPopup(popup);
    }
  }

  handleShowCinemaCode(event) {
    event.preventDefault();
    const button = event.target.closest('[data-action="show-cinema-code"]');
    const cinemaCodesStr = button?.dataset.cinemaCodes;
    const voucherId = button?.dataset.voucherId;

    if (!cinemaCodesStr || !voucherId) {
      console.error('Missing cinema codes or voucher ID');
      return;
    }

    // Parse the codes from JSON string
    let cinemaCodes;
    try {
      cinemaCodes = JSON.parse(cinemaCodesStr);
    } catch (error) {
      console.error('Failed to parse cinema codes:', error);
      return;
    }

    // Find the voucher card to get product title and gift card ID
    const voucherCard = this.findVoucherById(voucherId);

    // Show the cinema booking popup with existing codes
    this.showCinemaBookingPopup(cinemaCodes, voucherCard);
  }

  handleCopyCinemaCode(event) {
    event.preventDefault();

    const button = event.target.closest('[data-action="copy-cinema-code"]');
    const codeIndex = button?.dataset.codeIndex;
    const popup = button?.closest('#cinema-booking-popup');

    // Find the specific code element based on index
    // Index 0 = code 1, Index 1 = code 2, Index 2 = code 3, Index 3 = code 4
    let codeEl;
    if (codeIndex === '0') {
      codeEl = popup?.querySelector('.cinema-booking-popup__code--1');
    } else if (codeIndex === '1') {
      codeEl = popup?.querySelector('.cinema-booking-popup__code--2');
    } else if (codeIndex === '2') {
      codeEl = popup?.querySelector('.cinema-booking-popup__code--3');
    } else if (codeIndex === '3') {
      codeEl = popup?.querySelector('.cinema-booking-popup__code--4');
    }

    const cinemaCode = codeEl?.dataset.cinemaCode || codeEl?.textContent?.trim();

    if (!cinemaCode) {
      ErrorUtils.error('customer.account.vouchers', 'notifications.copy_failed', 'No cinema code found.');
      return;
    }

    // Copy to clipboard
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(cinemaCode).then(() => {
        // Show success toast notification
        ErrorUtils.success(
          'customer.account.vouchers',
          'cinema_booking.code_copied_success',
          'Cinema code copied to clipboard!'
        );

        // Show visual feedback on button
        const textSpan = button.querySelector('span');
        if (textSpan) {
          const originalText = textSpan.textContent;
          textSpan.textContent = TranslationUtils.get('customer.account.vouchers.cinema_booking.code_copied', 'Copied!');
          setTimeout(() => {
            if (textSpan) {
              textSpan.textContent = originalText || TranslationUtils.get('customer.account.vouchers.cinema_booking.copy_code', 'Copy code');
            }
          }, 2000);
        }
      }).catch(err => {
        console.error('Failed to copy cinema code:', err);
        ErrorUtils.error('customer.account.vouchers', 'notifications.copy_failed', 'Failed to copy code. Please try again.');
      });
    } else {
      // Fallback for browsers that don't support navigator.clipboard
      const textArea = document.createElement('textarea');
      textArea.value = cinemaCode;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        document.body.removeChild(textArea);

        ErrorUtils.success(
          'customer.account.vouchers',
          'cinema_booking.code_copied_success',
          'Cinema code copied to clipboard!'
        );

        const textSpan = button.querySelector('span');
        if (textSpan) {
          const originalText = textSpan.textContent;
          textSpan.textContent = TranslationUtils.get('customer.account.vouchers.cinema_booking.code_copied', 'Copied!');
          setTimeout(() => {
            if (textSpan) {
              textSpan.textContent = originalText || TranslationUtils.get('customer.account.vouchers.cinema_booking.copy_code', 'Copy code');
            }
          }, 2000);
        }
      } catch (err) {
        document.body.removeChild(textArea);
        console.error('Fallback copy failed:', err);
        ErrorUtils.error('customer.account.vouchers', 'notifications.copy_failed', 'Failed to copy code. Please try again.');
      }
    }
  }

  replaceBookExperienceWithShowCode(button, cinemaCodes, voucherId) {
    if (!button) {
      console.error('Button not found for replacement');
      return;
    }



    // Update button text
    const buttonText = button.querySelector('.ButtonText');
    if (buttonText) {
      buttonText.textContent = TranslationUtils.get('customer.account.vouchers.actions.show_cinema_code', 'Show Codes');
    }

    // Update button attributes
    button.dataset.action = 'show-cinema-code';
    button.dataset.cinemaCodes = JSON.stringify(cinemaCodes); // Store as JSON string
    button.dataset.voucherId = voucherId;

    // Remove old cinema booking attributes
    delete button.dataset.bookingToken;
    delete button.dataset.cinemaType;

    // Re-enable button
    button.disabled = false;

    // Update the voucher card status to "Booked" if it exists
    const cardElement = button.closest('.voucher-card');
    if (cardElement) {
      const statusElements = cardElement.querySelectorAll('.voucher-status--redeemed, .voucher-status--available');
      statusElements.forEach(statusElement => {
        // Update the text using translation
        statusElement.textContent = TranslationUtils.get('customer.account.vouchers.status.booked', 'Booked');
        // Update the class
        statusElement.className = 'u-s3 voucher-status--booked';
      });
    }


  }

  async handleBookExperience(event) {
    event.preventDefault();


    const button = event.target.closest('[data-action="book-experience"]');
    const voucherId = button?.dataset.voucherId;

    if (!voucherId) {
      console.error('No voucher ID found for booking');
      return;
    }

    // Find the voucher data from the current loaded vouchers
    const voucherCard = this.findVoucherById(voucherId);
    if (!voucherCard) {
      console.error('Voucher data not found for booking');
      return;
    }

    // Check if voucher is already grouped - if so, skip grouping logic and go directly to booking
    const isAlreadyGrouped = voucherCard.group_id && voucherCard.group_id !== null && voucherCard.group_id !== '';
    if (isAlreadyGrouped) {

      const bookingUrl = this.buildBookingUrl(voucherCard);
      window.location.href = bookingUrl;
      return;
    }


    // Get the product variant ID from the voucher
    const productVariantId = voucherCard.product_variant_id;
    if (!productVariantId) {
      console.error('No product variant ID found for voucher');
      return;
    }

    // Store original button text for restoration
    const buttonTextElement = button.querySelector('.ButtonText') || button;
    const originalText = buttonTextElement.textContent;

    try {
      // Show loading state
      if (button.tagName === 'BUTTON') {
        button.disabled = true;
      } else {
        button.classList.add('is-disabled');
        button.setAttribute('aria-disabled', 'true');
      }
      buttonTextElement.textContent = TranslationUtils.get('customer.account.vouchers.checking', 'Checking...');

      // Check for similar vouchers using the Find Similar Vouchers API
      const hasOtherVouchers = await this.checkForSimilarVouchers(productVariantId);

      if (hasOtherVouchers.has_similar_vouchers && hasOtherVouchers.data.length > 0) {
        // Open voucher grouping popup if other vouchers exist

        // Restore button state before opening popup (popup will handle its own state)
        if (button.tagName === 'BUTTON') {
          button.disabled = false;
        } else {
          button.classList.remove('is-disabled');
          button.removeAttribute('aria-disabled');
        }
        buttonTextElement.textContent = originalText;
        this.openVoucherGroupingPopup(voucherCard, hasOtherVouchers.data);
      } else {
        // No other vouchers found, proceed with single voucher booking

        const bookingUrl = this.buildBookingUrl(voucherCard);

        // Navigate immediately - don't restore button state as we're leaving the page
        window.location.href = bookingUrl;
        return; // Exit early since we're navigating away
      }
    } catch (error) {
      console.error('Error checking for similar vouchers:', error);
      // Fallback to single voucher booking on error
      const bookingUrl = this.buildBookingUrl(voucherCard);

      // Navigate immediately - don't restore button state as we're leaving the page
      window.location.href = bookingUrl;
      return; // Exit early since we're navigating away
    }
  }

  async handleBookGroupExperience(event) {
    event.preventDefault();

    const button = event.target.closest('button');
    const groupId = button?.dataset.groupId;

    if (!groupId) {
      return;
    }

    // Find the group element in the DOM
    const groupElement = button.closest('.voucher-group');
    if (!groupElement) {
      return;
    }

    // Find the first non-booked voucher card in the group
    const voucherCards = groupElement.querySelectorAll('.voucher-card');
    let primaryVoucherId = null;

    for (const card of voucherCards) {
      const voucherId = card.dataset.voucherId;
      if (voucherId) {
        // Check if this voucher is booked by looking for booking indicators in the DOM
        const isBooked = card.querySelector('.voucher-card__status-text')?.textContent?.toLowerCase().includes('booked');
        if (!isBooked) {
          primaryVoucherId = voucherId;
          break;
        }
      }
    }

    if (!primaryVoucherId) {
      return;
    }

    // Find the voucher data using the existing method
    const primaryVoucher = this.findVoucherById(primaryVoucherId);
    if (!primaryVoucher) {
      return;
    }

    // Use the existing single voucher booking flow with the primary voucher
    // The backend should handle the group booking based on the group_id
    const bookingUrl = this.buildBookingUrl(primaryVoucher);
    window.location.href = bookingUrl;
  }

  async checkForSimilarVouchers(productVariantId) {
    try {


      const requestData = {
        product_id: productVariantId  // Use product ID as-is from backend
      };



      // Use the API service for consistent error handling and request formatting
      const result = await window.apiService.post(`${window.proxyPath}/find-similar-vouchers`, requestData);


      // Validate response structure according to API specification
      if (result && typeof result === 'object' && typeof result.has_similar_vouchers === 'boolean') {
        return result;
      } else {
        console.warn('Invalid API response structure:', result);
        throw new Error('Invalid API response structure');
      }

    } catch (error) {
      console.error('Failed to check for similar vouchers:', error);

      // Handle specific API errors with user notifications for critical failures
      if (error.message && typeof error.message === 'string') {
        if (error.message.includes('401') || error.message.includes('unauthorized')) {
          console.warn('Authentication required for voucher checking');
          if (toastNotifications) {
            toastNotifications.warning('Please log in to check for similar vouchers');
          }
        } else if (error.message.includes('500')) {
          console.warn('Server error checking vouchers');
          if (toastNotifications) {
            toastNotifications.error('Server error while checking vouchers. Please try again.');
          }
        } else if (error.message.includes('network') || error.message.includes('fetch')) {
          console.warn('Network error checking vouchers');
          if (toastNotifications) {
            toastNotifications.error('Network error. Please check your connection and try again.');
          }
        }
        // Don't show toast for 404 or other minor errors as voucher checking is optional
      }

      // Return default response structure on error to allow graceful degradation
      return {
        has_similar_vouchers: false,
        data: []
      };
    }
  }

  initGroupingFormValidator(popup) {
    // Find the form in the grouping popup
    const form = popup.querySelector('voucher-action-handler.collapsible-form');
    if (!form) {
      console.warn('No form found in grouping popup for validation');
      return;
    }

    // Initialize FormValidator with optimal UX: clean initially, helpful after first submit
    const formValidator = new FormValidator(form, {
      validateOnInput: true, // Enable real-time validation AFTER first submit attempt
      validateOnBlur: true, // Enable validation on blur for immediate feedback
      errorClass: 'field--error',
      fieldSelector: '.field',
      buttonSelector: '[data-action="voucher-action"][data-method="init-connect"]',
      errorOutputSelector: form.querySelector('.input-validation'),
      showErrorsOnSubmitOnly: true // Key: no validation until first submit, then real-time kicks in
    });

    // Find the voucher input and add validator
    const voucherInput = form.querySelector('[data-action="input-voucher-code"]');
    if (voucherInput) {
      if (!voucherInput.id) {
        voucherInput.id = 'groupingVoucherCode_' + Date.now(); // Unique ID
      }
      formValidator.addValidator(
        voucherInput.id,
        this.validateVoucherCode.bind(this),
        ''
      );

    }

    // Store validator reference for cleanup
    popup._groupingFormValidator = formValidator;
  }

  validateVoucherCode(value) {
    // Same validation logic as original redeem form
    if (!/^[a-zA-Z0-9\s]+$/.test(value)) {
      return TranslationUtils.get('customer.account.redeem.voucher_code_invalid_chars', 'Voucher code can only contain letters and numbers');
    }
    // Validate based on code length without spaces (old vouchers have 10 chars, new ones have more)
    const codeWithoutSpaces = value.replace(/\s/g, '');
    if (codeWithoutSpaces.length < 10) {
      return TranslationUtils.get('customer.account.redeem.voucher_code_too_short', 'Voucher code is too short');
    }
    return true;
  }

  openVoucherGroupingPopup(primaryVoucher, similarVouchers, hasSimilarVouchers = true) {



    // Prepare variables for the Liquid block popup
    const voucherIdentifier = primaryVoucher.shopify_gift_card_id;
    const productTitle = primaryVoucher.productTitle;

    let similarVouchersList = [];
    if (hasSimilarVouchers) {
      // Filter out vouchers that cannot be grouped
      similarVouchersList = Array.isArray(similarVouchers) ?
        similarVouchers.filter(voucher => {
          // Compare GIDs - both should be in format: gid://shopify/GiftCard/123
          const similarVoucherGid = voucher.gid;
          const primaryVoucherGid = voucherIdentifier.startsWith('gid://') ?
            voucherIdentifier :
            `gid://shopify/GiftCard/${voucherIdentifier}`;

          const isDuplicate = similarVoucherGid === primaryVoucherGid;
          if (isDuplicate) {

            return false;
          }

          // Filter out vouchers that already have a group_id (already grouped)
          const hasGroupId = voucher.group_id && voucher.group_id !== null && voucher.group_id !== '';
          if (hasGroupId) {

            return false;
          }


          return true;
        }) : [];
    }



    // Build booking URL for the primary voucher (needed when coming from Book flow)
    const bookingUrl = this.buildBookingUrl(primaryVoucher);

    // Store voucher grouping data for later use if needed
    const groupingData = {
      primaryVoucherId: voucherIdentifier, // Use as-is from backend
      similarVouchers: similarVouchersList, // Store full voucher objects with gid and lastFour
      productVariantId: primaryVoucher.product_variant_id,
      productTitle: productTitle,
      experienceId: primaryVoucher.product_variant_id, // Use this for backend requests
      bookingUrl: bookingUrl, // Store booking URL for navigation after grouping
      fromBookFlow: true, // Mark that we're coming from Book flow
      timestamp: Date.now()
    };
    try {
      localStorage.setItem('voucherGroupingData', JSON.stringify(groupingData));


      // Use PopupUtils to open the voucher grouping popup block
      if (PopupUtils && typeof PopupUtils.openPopup === 'function') {
        // Use CSS selector to find popup ending with 'voucher-grouping-popup' (handles dynamic block IDs)
        PopupUtils.openPopup('[id$="voucher-grouping-popup"]', voucherIdentifier, (popup) => {
          // Clean up any existing event delegation for this popup to prevent conflicts
          if (popup._voucherGroupingDelegate) {
            popup._voucherGroupingDelegate.destroy();
            delete popup._voucherGroupingDelegate;

          }

          // Initialize form validator for the grouping popup input
          this.initGroupingFormValidator(popup);

          // Populate the popup content dynamically
          const contentElement = popup.querySelector('[data-voucher-grouping-content]');

          if (contentElement) {
            // Find voucher data for each ID to get last_characters
            const primaryVoucherData = this.findVoucherById(voucherIdentifier); // Use original ID for lookup
            const primaryLastFour = primaryVoucherData?.last_characters || voucherIdentifier.slice(-4);

            // Generate primary voucher card HTML using the DRY method
            const primaryVoucherCardHTML = this.generateVoucherGroupingCardHTML(
              voucherIdentifier,
              primaryLastFour,
              true,  // isPrimary
              true,  // isSelected
              primaryVoucherData // voucherData
            );

            // Generate similar vouchers cards HTML using the DRY method
            const similarVoucherCardsHTML = similarVouchersList.map(voucher => {
              // Keep the full GID instead of stripping it
              const voucherGid = voucher.gid; // Keep as GID: gid://shopify/GiftCard/123
              const lastFour = voucher.lastFour;

              // Find full voucher data for validation
              const fullVoucherData = this.findVoucherById(voucherGid);

              return this.generateVoucherGroupingCardHTML(
                voucherGid,
                lastFour,
                false, // isPrimary
                false, // isSelected
                fullVoucherData // voucherData
              );
            }).join('');

            const voucherCardsHTML = `
              <div class="voucher-grouping-popup__sections">
                <div class="voucher-grouping-section">
                  <h4 class="voucher-grouping-section__title u-s3">${TranslationUtils.get('customer.account.vouchers.grouping.primary_voucher', 'Primary Voucher')}</h4>
                  ${primaryVoucherCardHTML}
                </div>
                
                <div class="voucher-grouping-section">
                  <h4 class="voucher-grouping-section__title u-s3">${TranslationUtils.get('customer.account.vouchers.grouping.similar_vouchers', 'Similar Vouchers')}</h4>
                  <div class="voucher-grouping-section__list">
                    ${similarVoucherCardsHTML}
                  </div>
                </div>
              </div>
            `;

            // Store the grouping-voucher-redeem element before resetting content
            const groupingRedeemElement = popup.querySelector('voucher-action-handler.collapsible-form');

            // Reset the content
            contentElement.innerHTML = voucherCardsHTML;

            // Move the grouping-voucher-redeem element to the similar vouchers section as the last item
            const similarVouchersSection = contentElement.querySelector('.voucher-grouping-section:last-child .voucher-grouping-section__list');

            if (groupingRedeemElement && similarVouchersSection) {
              similarVouchersSection.appendChild(groupingRedeemElement);
            }
          }

          // Store data on popup for future use
          popup.dataset.productTitle = productTitle;
          popup.dataset.similarVouchers = JSON.stringify(similarVouchersList);
          popup.dataset.primaryVoucherId = voucherIdentifier;

          // Set up fresh event delegation for this popup specifically and store reference
          popup._voucherGroupingDelegate = new Delegate(popup);
          popup._voucherGroupingDelegate.on('click', '[data-action="toggle-voucher-group"]', this.handleToggleVoucherGroup.bind(this));


          // Update the combine button state since primary voucher is pre-selected
          this.updateCombineButtonState();

          // Set the href for the continue without grouping button
          const continueButton = popup.querySelector('.voucher-grouping-popup__continue-btn');
          if (continueButton) {
            const bookingUrl = this.buildBookingUrl(primaryVoucher);
            continueButton.href = bookingUrl;

          }
        });
      } else {
        console.error('PopupUtils not available for voucher grouping popup');
        // Fallback to single voucher booking
        const bookingUrl = this.buildBookingUrl(primaryVoucher);
        window.location.href = bookingUrl;
      }
    } catch (error) {
      console.error('Failed to store voucher grouping data:', error);
      // Fallback to single voucher booking
      const bookingUrl = this.buildBookingUrl(primaryVoucher);
      window.location.href = bookingUrl;
    }
  }


  handleCancelGroupRequest(event) {
    event.preventDefault();

    const button = event.target.closest('button');
    const groupId = button?.dataset.groupId;

    if (!groupId) {
      console.error('No group ID found for cancel group request');
      return;
    }

    // Find the group in state
    const group = this.state.giftCards?.find(item =>
      item.type === 'giftcard_group' && item.group_id === groupId
    );

    if (!group || !group.vouchers || group.vouchers.length === 0) {
      console.error('No vouchers found in group');
      return;
    }

    // Get all vouchers in the group that are in IN_PROGRESS status
    const cancellableVouchers = group.vouchers.filter(voucher => {
      return this.isVoucherInProgress(voucher);
    });

    if (cancellableVouchers.length === 0) {
      if (window.toastNotifications) {
        window.toastNotifications.error(TranslationUtils.get(
          'customer.account.vouchers.notifications.no_cancellable_vouchers',
          'No vouchers in this group can be canceled at this time.'
        ));
      }
      return;
    }



    // Open the cancel booking popup (not unified)
    const popup = document.querySelector('#cancel-booking-popup');
    if (!popup) {
      console.error('Cancel booking popup not found');
      if (window.toastNotifications) {
        window.toastNotifications.error(TranslationUtils.get(
          'customer.account.vouchers.notifications.popup_not_found',
          'Cancel booking popup not available. Please refresh the page and try again.'
        ));
      }
      return;
    }

    // Store voucher IDs as array for group cancellation
    const voucherIds = cancellableVouchers.map(v => v.shopify_gift_card_id);
    popup.dataset.voucherIds = JSON.stringify(voucherIds);
    popup.dataset.isGroupCancellation = 'true';
    popup.dataset.groupId = groupId;

    // Open the popup
    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      window.globalPopup.openPopup(popup);

    } else {
      console.error('Global popup manager not available');
    }
  }

  handleCancelGroupBooking(event) {
    event.preventDefault();

    const button = event.target.closest('button');
    const groupId = button?.dataset.groupId;

    if (!groupId) {
      console.error('No group ID found for cancel group booking');
      return;
    }

    // Find the group in state
    const group = this.state.giftCards?.find(item =>
      item.type === 'giftcard_group' && item.group_id === groupId
    );

    if (!group || !group.vouchers || group.vouchers.length === 0) {
      console.error('No vouchers found in group');
      return;
    }

    // Get all vouchers in the group that are booked (BOOKED status, not IN_PROGRESS)
    const cancellableVouchers = group.vouchers.filter(voucher => {
      // Include vouchers that are booked and can be cancelled
      const cancellationInfo = this.getCancellationInfo(voucher);
      return voucher.is_booked && cancellationInfo.canCancel;
    });

    if (cancellableVouchers.length === 0) {
      if (window.toastNotifications) {
        window.toastNotifications.error(TranslationUtils.get(
          'customer.account.vouchers.notifications.no_cancellable_vouchers',
          'No vouchers in this group can be canceled at this time.'
        ));
      }
      return;
    }



    // Open the cancel booking popup (not unified)
    const popup = document.querySelector('#cancel-booking-popup');
    if (!popup) {
      console.error('Cancel booking popup not found');
      if (window.toastNotifications) {
        window.toastNotifications.error(TranslationUtils.get(
          'customer.account.vouchers.notifications.popup_not_found',
          'Cancel booking popup not available. Please refresh the page and try again.'
        ));
      }
      return;
    }

    // Store voucher IDs as array for group cancellation
    const voucherIds = cancellableVouchers.map(v => v.shopify_gift_card_id);
    popup.dataset.voucherIds = JSON.stringify(voucherIds);
    popup.dataset.isGroupCancellation = 'true';
    popup.dataset.groupId = groupId;

    // Open the popup
    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      window.globalPopup.openPopup(popup);

    } else {
      console.error('Global popup manager not available');
    }
  }

  async handleUngroupVouchers(event) {
    event.preventDefault();

    const button = event.target.closest('button');
    const groupId = button?.dataset.groupId;

    if (!groupId) {
      console.error('No group ID found for ungroup vouchers');
      return;
    }

    // Disable button and show loading state
    const originalButtonText = button.innerHTML;
    button.disabled = true;
    button.innerHTML = `<span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.group_management.ungroup_processing', 'Ungrouping...')}</span>`;

    try {
      // Find the group in state (same lookup handleCancelGroupBooking uses).
      // The customer-facing group card (getBookingRequestCardHTML) doesn't
      // wrap itself in .voucher-group or carry a data-group-vouchers
      // attribute — that markup belongs to the vendor-side card — so reading
      // vouchers back off the DOM here can never find them.
      const group = this.state.giftCards?.find(item =>
        item.type === 'giftcard_group' && item.group_id === groupId
      );

      const vouchers = group?.vouchers;
      if (!vouchers || vouchers.length === 0) {
        throw new Error('No vouchers found in group');
      }

      // Extract voucher GIDs and validate format (per API spec)
      const voucherGids = vouchers.map(voucher => {
        // Handle both GID format and plain ID format
        let gid = null;
        if (voucher.shopify_gift_card_id) {
          gid = voucher.shopify_gift_card_id.startsWith('gid://')
            ? voucher.shopify_gift_card_id
            : `gid://shopify/GiftCard/${voucher.shopify_gift_card_id}`;
        } else if (voucher.gid) {
          gid = voucher.gid.startsWith('gid://')
            ? voucher.gid
            : `gid://shopify/GiftCard/${voucher.gid}`;
        } else if (voucher.id) {
          gid = `gid://shopify/GiftCard/${voucher.id}`;
        }

        // Validate GID format (per API spec: gid://shopify/GiftCard/[id])
        if (gid && !gid.match(/^gid:\/\/shopify\/GiftCard\/\d+$/)) {
          console.warn('Invalid GID format:', gid);
          return null;
        }

        return gid;
      }).filter(Boolean);

      // Validate voucher count (per API spec: minimum 1, maximum 50)
      if (voucherGids.length === 0) {
        throw new Error('No valid voucher GIDs found');
      }

      if (voucherGids.length > 50) {
        throw new Error('Cannot ungroup more than 50 vouchers at once. Please select fewer vouchers.');
      }



      // Call the ungroup API endpoint (per UNGROUP_VOUCHERS.md spec)
      if (!window.apiService) {
        throw new Error('API service not available');
      }

      // Use the proxy path (typically /apps/proxy, but spec says /api/v1/proxy)
      // The window.proxyPath should handle the correct path
      const endpoint = `${window.proxyPath || '/apps/proxy'}/ungroup-vouchers`;

      const response = await window.apiService.post(endpoint, {
        voucher_gids: voucherGids
      });

      // Handle response according to API spec (UNGROUP_VOUCHERS.md)
      if (response && response.success === true) {
        const totalUngrouped = response.total_ungrouped || voucherGids.length;
        const ungroupedVouchers = response.ungrouped_vouchers || voucherGids;



        // Show success message with count
        if (toastNotifications) {
          const successMessage = TranslationUtils.get(
            'customer.account.vouchers.group_management.ungroup_success',
            'Vouchers ungrouped successfully'
          );
          toastNotifications.success(successMessage);
        }

        // Invalidate cache for all filters since ungrouping changes voucher structure
        // This ensures fresh data is loaded after ungrouping
        this.clearCache();

        // Reload vouchers to reflect the change
        this.loadVouchers(this.state.currentPage, 0, this.state.currentFilter);

        // If Ungroup was triggered from within a popup (e.g. the "exchange
        // requires ungroup" prompt), close it now that it's done.
        const containingPopup = button.closest('.Popup');
        if (containingPopup && window.globalPopup && typeof window.globalPopup.closePopup === 'function') {
          window.globalPopup.closePopup(containingPopup);
        }
      } else {
        // Handle error response according to API spec
        const errorMessage = response?.error || response?.details || 'Failed to ungroup vouchers';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('Error ungrouping vouchers:', error);

      // Show user-friendly error message based on error type
      if (toastNotifications) {
        let errorMessage;

        // Handle specific error types from API spec
        if (error.message?.includes('Validation failed') || error.message?.includes('must contain')) {
          errorMessage = TranslationUtils.get(
            'customer.account.vouchers.group_management.ungroup_validation_error',
            'Invalid request. Please check your selection and try again.'
          );
        } else if (error.message?.includes('authentication') || error.message?.includes('log in')) {
          errorMessage = TranslationUtils.get(
            'customer.account.vouchers.group_management.ungroup_auth_error',
            'Please log in to continue.'
          );
        } else if (error.message?.includes('50')) {
          errorMessage = error.message;
        } else {
          errorMessage = TranslationUtils.get(
            'customer.account.vouchers.group_management.ungroup_error',
            'Failed to ungroup vouchers. Please try again.'
          );
        }

        toastNotifications.error(errorMessage);
      }
    } finally {
      // Restore button state
      button.disabled = false;
      button.innerHTML = originalButtonText;
    }
  }


  handleChatWithSupplier(event) {
    event.preventDefault();
    // TODO: Implement chat with supplier functionality


    // Show notification for now
    if (window.toastNotifications) {
      window.toastNotifications.info(TranslationUtils.get('customer.account.vouchers.communication.chat_unavailable', 'Chat is currently unavailable'));
    }
  }

  handleStartInternalConversation(event) {
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    const button = event.target.closest('button');
    if (!button) return;

    let bookingId = button.dataset.bookingId;
    let card = button.dataset.voucherId ? this.findVoucherById(button.dataset.voucherId) : null;
    if (!card && bookingId) {
      card = this.findVoucherByInternalBookingId(bookingId);
    }
    if (!bookingId && card) {
      bookingId = card?.booking_data?.internal_booking_id;
    }
    if (!bookingId) return;

    const chatPopup = document.querySelector('chat-popup');
    if (!chatPopup?.openInternalDraft) {
      if (window.toastNotifications) {
        window.toastNotifications.error(
          TranslationUtils.get('customer.account.vouchers.communication.message_send_failed', 'Could not open chat. Please refresh the page.')
        );
      }
      return;
    }

    chatPopup.openInternalDraft({
      bookingId: String(bookingId),
      displayName: this.getInternalConversationDisplayName(card, button),
      role: window.account?.isVendor ? 'vendor' : 'customer'
    });
  }

  handleToggleVoucherGroup(event) {
    event.preventDefault();


    // Get the voucher card from the clicked element
    const voucherCard = event.target.closest('.voucher-grouping-card');

    if (!voucherCard) {
      console.error('Could not find voucher grouping card');
      return;
    }

    const voucherId = voucherCard.dataset.voucherId;

    if (!voucherId) {
      console.error('Could not find voucher ID');
      return;
    }

    // Don't allow toggling the primary voucher (it should always be selected)
    if (voucherCard.classList.contains('voucher-grouping-card--primary')) {

      return;
    }

    // Find the button to update its icon and label
    const actionButton = voucherCard.querySelector('.voucher-grouping-plus');
    if (!actionButton) {
      console.error('Could not find action button');
      return;
    }

    // Toggle the selected state
    const isSelected = voucherCard.classList.contains('voucher-grouping-card--selected');

    // Define SVG icons
    const checkIcon = `<svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.4502 7.65311L6.7469 10.9498L11.692 3.53223" stroke="black" stroke-width="0.989011" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
    const plusIcon = `<svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.57107 3.12012V11.3619M11.692 7.241H3.4502" stroke="#131619" stroke-width="0.824176" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;

    if (isSelected) {
      // Deselect
      voucherCard.classList.remove('voucher-grouping-card--selected');
      actionButton.innerHTML = plusIcon;
      actionButton.setAttribute('aria-label', 'Add to group');
    } else {
      // Select
      voucherCard.classList.add('voucher-grouping-card--selected');
      actionButton.innerHTML = checkIcon;
      actionButton.setAttribute('aria-label', 'Remove from group');
    }

    // Update the combine button state
    this.updateCombineButtonState();
  }

  /**
   * Generates HTML for a voucher grouping card
   * @param {string} voucherId - The voucher ID
   * @param {string} lastFour - Last four characters of voucher code
   * @param {boolean} isPrimary - Whether this is the primary voucher
   * @param {boolean} isSelected - Whether this voucher is selected
   * @param {object} voucherData - Full voucher data object for validation
   * @returns {string} HTML string for voucher grouping card
   */
  generateVoucherGroupingCardHTML(voucherId, lastFour, isPrimary = false, isSelected = false, voucherData = null) {
    // Note: Product validation now happens before connect, so we don't need to show error cards
    const primaryClass = isPrimary ? ' voucher-grouping-card--primary' : '';
    const selectedClass = isSelected ? ' voucher-grouping-card--selected' : '';
    const buttonDisabled = isPrimary ? ' disabled' : '';
    const cardAction = isPrimary ? '' : ' data-action="toggle-voucher-group"';
    const cardCursor = isPrimary ? '' : ' style="cursor: pointer;"';
    const buttonIcon = isPrimary || isSelected ? `<svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.4502 7.65311L6.7469 10.9498L11.692 3.53223" stroke="black" stroke-width="0.989011" stroke-linecap="round" stroke-linejoin="round"/>
</svg>` : `<svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M7.57107 3.12012V11.3619M11.692 7.241H3.4502" stroke="#131619" stroke-width="0.824176" stroke-linecap="round" stroke-linejoin="round"/>
</svg>
`;
    const buttonLabel = isPrimary ? 'Selected voucher' : (isSelected ? 'Remove from group' : 'Add to group');

    const fieldId = `voucher-grouping-code-${voucherId}`;
    const fullCode = `**** **** **** ${lastFour}`;
    const label = TranslationUtils.get('customer.account.vouchers.code', 'Code');
    const placeholder = TranslationUtils.get('customer.account.vouchers.code_placeholder', 'Voucher code');

    return `
      <div class="voucher-grouping-card${primaryClass}${selectedClass}" data-voucher-id="${voucherId}"${cardAction}${cardCursor}>
        <div class="voucher-grouping-card__content">
          <div class="field">
            <label for="${fieldId}" class="field-label u-p4">${label}</label>
            <input 
              type="text" 
              id="${fieldId}" 
              name="voucher_code" 
              class="input-placeholder u-p2" 
              value="${fullCode}" 
              placeholder="${placeholder}" 
              readonly 
              data-voucher-id="${voucherId}"
            />
          </div>
          <div class="voucher-grouping-card__action">
          <button class="voucher-grouping-plus" data-voucher-id="${voucherId}" aria-label="${buttonLabel}"${buttonDisabled}>
            ${buttonIcon}
          </button>
        </div>
        </div>
      </div>
    `;
  }

  updateCombineButtonState() {
    const popup = document.querySelector('[id$="voucher-grouping-popup"]');
    if (!popup) return;

    // Count selected cards (including primary and additional selected vouchers)
    const selectedCards = popup.querySelectorAll('.voucher-grouping-card--selected');
    const additionalSelectedCards = popup.querySelectorAll('.voucher-grouping-card--selected:not(.voucher-grouping-card--primary)');
    const combineButton = popup.querySelector('.voucher-grouping-popup__combine-btn');

    if (combineButton) {
      const totalSelectedCount = selectedCards.length;
      const additionalSelectedCount = additionalSelectedCards.length;

      if (additionalSelectedCount >= 1) {
        combineButton.disabled = false;
        const combineLabel = TranslationUtils.get('customer.account.vouchers.combine_vouchers_count', `Confirm and Combine ${totalSelectedCount} Vouchers`);
        // Replace {count} placeholder with actual count
        const finalLabel = combineLabel.replace('{count}', totalSelectedCount);
        const buttonText = combineButton.querySelector('.ButtonText') || combineButton;
        buttonText.textContent = finalLabel;
      } else {
        combineButton.disabled = true;
        const combineLabel = TranslationUtils.get('customer.account.vouchers.combine_vouchers', 'Confirm and Combine Vouchers');
        const buttonText = combineButton.querySelector('.ButtonText') || combineButton;
        buttonText.textContent = combineLabel;
      }
    }
  }

  refreshVoucherList() {
    // Trigger a refresh of the voucher display

    // Clear any cached voucher data
    this.cache.clear();

    // Reload the current page vouchers with existing filter and pagination
    if (this.state && this.state.currentFilter) {
      this.loadVouchers(this.state.currentPage || 1, 0, this.state.currentFilter);
    } else {
      // Fallback to default filter
      this.loadVouchers(1, 0, 'non-redeemed');
    }
  }

  findVoucherById(voucherId) {
    const giftCards = this.state.giftCards || [];
    const normalizedId = String(voucherId);

    for (const item of giftCards) {
      if (item.type === 'giftcard_group' && Array.isArray(item.vouchers)) {
        const found = item.vouchers.find(v => String(v.shopify_gift_card_id) === normalizedId);
        if (found) return found;
      } else if (String(item.shopify_gift_card_id) === normalizedId) {
        return item;
      }
    }

    return null;
  }

  updateURL(filter, page = 1) {
    const url = new URL(window.location);
    url.searchParams.set('vouchers', filter);
    if (page > 1) {
      url.searchParams.set('page', page.toString());
    } else {
      url.searchParams.delete('page');
    }
    window.history.replaceState({ path: url.toString() }, '', url);
  }

  handlePopState(event) {
    // Handle browser back/forward buttons
    const urlParams = new URLSearchParams(window.location.search);
    const vouchersFilter = urlParams.get('vouchers');
    const pageParam = parseInt(urlParams.get('page'), 10);
    const page = pageParam > 0 ? pageParam : 1;
    const validFilters = ['non-redeemed', 'redeemed', 'booked', 'expired', 'past-bookings', 'upcoming-bookings', 'booking-requests', 'completed', 'canceled'];

    if (vouchersFilter && validFilters.includes(vouchersFilter)) {
      if (vouchersFilter !== this.state.currentFilter || page !== this.state.currentPage) {
        this.setState({ currentFilter: vouchersFilter });
        this.updateActiveTab(vouchersFilter);
        this.loadVouchers(page, 0, vouchersFilter);
      }
    } else {
      // If no vouchers parameter, default to non-redeemed
      if (this.state.currentFilter !== 'non-redeemed') {
        this.setState({ currentFilter: 'non-redeemed' });
        this.updateActiveTab('non-redeemed');
        this.loadVouchers(1, 0, 'non-redeemed');
      }
    }
  }

  handlePagination(direction) {
    const pagination = this.state.pagination;
    let targetPage = this.state.currentPage;

    switch (direction) {
      case 'prev':
        if (pagination.hasPrev) {
          targetPage = this.state.currentPage - 1;
        }
        break;
      case 'next':
      case 'load-more':
        if (pagination.hasNext) {
          targetPage = this.state.currentPage + 1;
        }
        break;
      default:
        return; // Invalid direction
    }

    if (targetPage !== this.state.currentPage) {
      this.scrollToTop();
      this.loadVouchers(targetPage, 0, this.state.currentFilter);
      this.updateURL(this.state.currentFilter, targetPage);
    }
  }

  handleLoadMore() {
    this.handlePagination('load-more');
  }

  handlePrevPage() {
    this.handlePagination('prev');
  }

  handleNextPage() {
    this.handlePagination('next');
  }

  handlePageNumber(event) {
    const pageNumber = parseInt(event.target.dataset.page);
    if (pageNumber && pageNumber !== this.state.currentPage) {
      this.scrollToTop();
      this.loadVouchers(pageNumber, 0, this.state.currentFilter);
      this.updateURL(this.state.currentFilter, pageNumber);
    }
  }

  handleRetry() {
    this.loadVouchers(this.state.currentPage, 0, this.state.currentFilter);
  }

  handleVoucherActionCompleted(event) {
    // Invalidate cache and refresh vouchers list when voucher action is completed
    const action = event.detail?.action;


    // For redeem actions, we need to invalidate both non-redeemed and redeemed caches
    // as a voucher moves from one state to another
    if (action === 'redeem') {
      this.clearCacheForFilter('non-redeemed');
      this.clearCacheForFilter('redeemed');
    } else if (action === 'connect') {
      // For connect actions, clear all caches as connection status changes
      this.clearCacheForFilter('non-redeemed');
    } else if (action === 'gift') {
      // For gift actions, only need to refresh the current filter
      this.clearCacheForFilter(this.state.currentFilter);
    } else if (action === 'extend') {
      // For extend actions, clear current filter cache as expiration date changes
      this.clearCacheForFilter(this.state.currentFilter);
      // Also clear expired filter cache as voucher might move out of expired state
      this.clearCacheForFilter('expired');
    } else {
      // For other actions, clear all cache to be safe
      this.clearCache();
    }

    this.setState({ hasVoucherAction: true });
    this.loadVouchers(this.state.currentPage, 0, this.state.currentFilter);
  }

  handleVoucherGroupingUpdate(event) {

    const { redeemedVoucherId } = event.detail;

    if (!redeemedVoucherId) {
      console.error('No redeemed voucher ID provided');
      return;
    }

    this.updateVoucherGroupingPopupState(redeemedVoucherId);
  }

  updateVoucherGroupingPopupState(redeemedVoucherId) {
    const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
    if (!voucherGroupingPopup) {
      console.warn('Voucher grouping popup not found');
      return;
    }



    try {
      // Check if the redeemed voucher already exists in the popup
      const similarVoucherCards = voucherGroupingPopup.querySelectorAll('.voucher-grouping-card:not(.voucher-grouping-card--primary)');
      let voucherFound = false;

      similarVoucherCards.forEach(card => {
        const cardVoucherId = card.dataset.voucherId;

        // Compare voucher IDs (handle both GID formats and plain IDs)
        const isMatchingVoucher = this.compareVoucherIds(cardVoucherId, redeemedVoucherId);

        if (isMatchingVoucher && !card.classList.contains('voucher-grouping-card--selected')) {

          voucherFound = true;

          // Mark the card as selected
          card.classList.add('voucher-grouping-card--selected');

          // Update the button icon and label
          const actionButton = card.querySelector('.voucher-grouping-plus');
          if (actionButton) {
            const checkIcon = `<svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M3.4502 7.65311L6.7469 10.9498L11.692 3.53223" stroke="black" stroke-width="0.989011" stroke-linecap="round" stroke-linejoin="round"/>
</svg>`;
            actionButton.innerHTML = checkIcon;
            actionButton.setAttribute('aria-label', 'Remove from group');
          }
        }
      });

      // If voucher was not found in existing cards, add it as a new card
      if (!voucherFound) {

        this.addNewVoucherToGroupingPopup(redeemedVoucherId, voucherGroupingPopup);
      }

      // Update the combine button state to reflect the new selection
      this.updateCombineButtonState();

      // Ensure continue without grouping button has proper href
      const continueButton = voucherGroupingPopup.querySelector('.voucher-grouping-popup__continue-btn');
      if (continueButton && !continueButton.href) {
        const primaryVoucherId = voucherGroupingPopup.dataset.primaryVoucherId;
        if (primaryVoucherId) {
          const primaryVoucher = this.findVoucherById(primaryVoucherId);
          if (primaryVoucher) {
            const bookingUrl = this.buildBookingUrl(primaryVoucher);
            continueButton.href = bookingUrl;

          }
        }
      }

    } catch (error) {
      console.error('Error updating voucher grouping popup after redeem:', error);
    }
  }

  /**
   * Add a newly redeemed voucher to the grouping popup as a selected card
   * @param {string} redeemedVoucherId - The redeemed voucher ID
   * @param {Element} voucherGroupingPopup - The grouping popup element
   */
  addNewVoucherToGroupingPopup(redeemedVoucherId, voucherGroupingPopup) {
    try {
      // Find the similar vouchers section
      const similarVouchersSection = voucherGroupingPopup.querySelector('.voucher-grouping-section:last-child .voucher-grouping-section__list');
      if (!similarVouchersSection) {
        console.error('Similar vouchers section not found');
        return;
      }

      // Find the input form to insert before it
      const inputForm = similarVouchersSection.querySelector('voucher-action-handler.collapsible-form');
      if (!inputForm) {
        console.error('Input form not found in similar vouchers section');
        return;
      }

      // Get last 4 characters for display
      const lastFour = redeemedVoucherId.slice(-4);

      // Generate the voucher card HTML (preselected)
      const voucherCardHTML = this.generateVoucherGroupingCardHTML(
        redeemedVoucherId,
        lastFour,
        false, // isPrimary
        true,  // isSelected - newly redeemed vouchers should be preselected
        null   // voucherData - we don't have full data, but it's not needed for display
      );

      // Insert the new voucher card before the input form
      inputForm.insertAdjacentHTML('beforebegin', voucherCardHTML);

      // Clear the input field in the form
      const voucherInput = inputForm.querySelector('[name="voucher_code"]');
      if (voucherInput) {
        voucherInput.value = '';
      }

      // Clear any validation messages
      const inputValidation = inputForm.querySelector('.input-validation');
      if (inputValidation) {
        inputValidation.setAttribute('aria-hidden', 'true');
        inputValidation.innerHTML = '';
      }



    } catch (error) {
      console.error('Error adding new voucher to grouping popup:', error);
    }
  }

  compareVoucherIds(id1, id2) {
    // Normalize both IDs to compare them properly
    const normalizeId = (id) => {
      if (id.startsWith('gid://shopify/GiftCard/')) {
        return id.replace('gid://shopify/GiftCard/', '');
      }
      return id;
    };

    const normalizedId1 = normalizeId(id1);
    const normalizedId2 = normalizeId(id2);

    return normalizedId1 === normalizedId2;
  }

  showSuccessMessage(translationKey, fallbackText) {
    const message = TranslationUtils.get(`customer.account.vouchers.${translationKey}`, fallbackText);
    // You can implement a success notification system here

  }

  invalidateCacheAfterAction() {
    // Clear cache for all filters to ensure fresh data after any voucher action
    this.cache.clear();
    // Set flag to indicate voucher action has occurred
    this.setState({ hasVoucherAction: true });
  }

  getCacheStats() {
    return this.cache.getStats();
  }

  cleanupExpiredCache() {
    const cleanedCount = this.cache.cleanupExpired();
  }

  // Method to handle page visibility changes (when user returns to tab)
  handleVisibilityChange() {
    if (!document.hidden) {
      // User returned to the page, clean up expired cache
      this.cleanupExpiredCache();

      // Optionally refresh data if cache is empty or very old
      const stats = this.getCacheStats();
      if (stats.valid === 0) {

        this.loadVouchers(this.state.currentPage, 0, this.state.currentFilter);
      }
    }
  }

  // Cleanup method for when component is disconnected
  disconnectedCallback() {
    // Remove event listeners to prevent memory leaks
    // Note: We need to store bound references to properly remove listeners
    if (this.boundHandleVisibilityChange) {
      document.removeEventListener('visibilitychange', this.boundHandleVisibilityChange);
    }
    if (this.boundHandleVoucherActionCompleted) {
      document.removeEventListener('voucher-action-completed', this.boundHandleVoucherActionCompleted);
    }
    if (this.boundHandleVoucherGroupingUpdate) {
      document.removeEventListener('voucher-grouping-update', this.boundHandleVoucherGroupingUpdate);
    }
    if (this.boundHandlePopState) {
      window.removeEventListener('popstate', this.boundHandlePopState);
    }

    // Clean up delegates
    if (this.delegateBody) {
      this.delegateBody.off();
    }

    // Clean up popup delegates
    const voucherGroupingPopup = document.querySelector('[id$="voucher-grouping-popup"]');
    if (voucherGroupingPopup && voucherGroupingPopup._voucherGroupingDelegate) {
      voucherGroupingPopup._voucherGroupingDelegate.destroy();
      delete voucherGroupingPopup._voucherGroupingDelegate;

    }

    // Clean up popup handlers
    if (window.globalPopup) {
      window.globalPopup.onPopupOpen = null;
      window.globalPopup.onPopupClose = null;
    }

    // Clear cache to free memory
    this.cache.clear();
  }

  render() {
    this.renderContentHeader();
    this.renderVouchersList();
    this.renderPagination();
  }

  renderContentHeader() {
    // The active tab's header stays visible, also on an empty tab: the empty
    // message renders below it, so the vendor still sees which tab they are on.
    const currentHeader = this.element.querySelector(`[data-tab-header="${this.state.currentFilter}"]`);
    if (currentHeader) {
      currentHeader.style.display = '';
    } else {
      console.warn('⚠️ No header found for filter:', this.state.currentFilter);
    }
  }

  renderVouchersList() {
    if (this.state.loading) {
      this.vouchersList.innerHTML = this.getLoadingHTML();
      // Hide controls during loading
      if (this.vendorActions) {
        this.vendorActions.hideControlsDuringLoad();
      }
      return;
    }

    if (this.state.error) {
      this.vouchersList.innerHTML = this.getErrorHTML();
      // Hide controls on error
      if (this.vendorActions) {
        this.vendorActions.hideControlsDuringLoad();
      }
      return;
    }

    if (!this.state.giftCards || this.state.giftCards.length === 0) {
      this.vouchersList.innerHTML = this.getEmptyStateHTML();
      // Update controls visibility for empty state
      if (this.vendorActions) {
        requestAnimationFrame(() => {
          this.vendorActions.updateControlsVisibility();
        });
      }
      return;
    }

    this.vouchersList.innerHTML = this.state.giftCards.map(item => this.renderVoucherItem(item)).join('');

    // Update select all state and bulk action buttons after rendering vouchers
    if (this.vendorActions) {
      requestAnimationFrame(() => {
        this.vendorActions.updateSelectAllState();
        this.vendorActions.updateBulkActionButtons();
        // Update controls visibility after content is rendered
        this.vendorActions.updateControlsVisibility();
        void this.vendorActions.refreshOpenConversationUnreadBadges();
      });
    }
  }

  renderPagination() {
    const pagination = this.state.pagination;
    let paginationContainer = this.getPaginationContainer();
    if (!pagination || pagination.totalPages <= 1) {
      // Clear what the previous result drew: filtering many pages down to one
      // otherwise leaves the old page numbers on screen.
      if (paginationContainer) paginationContainer.innerHTML = '';
      return;
    }

    const paginationHTML = this.generatePaginationHTML(pagination);
    const infoHTML = this.generatePaginationInfo(pagination);

    paginationContainer.innerHTML = `
      <div class="Pagination-Wrapper">
        <nav class="pagination" role="navigation" aria-label="${TranslationUtils.get('pagination.label', 'Pagination')}">
          <ul class="Pagination--List" role="list">
            ${paginationHTML}
          </ul>
        </nav>
        <div class="main-collection__product-count u-s4">
          ${infoHTML}
        </div>
      </div>
    `;
  }

  generatePaginationHTML(pagination) {
    const { page, totalPages } = pagination;
    const pages = this.getVisiblePages(page, totalPages);

    let html = '';

    // Previous button - always show, but disable when not available
    if (pagination.hasPrev) {
      html += `
        <li class="mr-m">
          <button class="pagination-arrow" data-action="prev-page" aria-label="${TranslationUtils.get('pagination.previous', 'Previous')}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.625 13.125L2.5 10M2.5 10L5.625 6.875M2.5 10H17.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </li>`;
    }

    // Page numbers
    pages.forEach((pageNum) => {
      if (pageNum === '...') {
        html += `
        <li class="Pagination--ItemWrapper">
          <span class="Pagination--Item">...</span>
        </li>`;
      } else {
        const isCurrent = pageNum === page;
        if (isCurrent) {
          html += `
        <li class="Pagination--ItemWrapper">
          <span class="Pagination--Item Pagination--ItemCurrent u-p5" aria-current="page" aria-label="${TranslationUtils.get('pagination.page', 'Page')} ${pageNum}">${pageNum}</span>
        </li>`;
        } else {
          html += `
        <li class="Pagination--ItemWrapper">
          <button class="Pagination--Item link u-p5" data-action="page-number" data-page="${pageNum}" aria-label="${TranslationUtils.get('pagination.page', 'Page')} ${pageNum}">${pageNum}</button>
        </li>`;
        }
      }
    });

    // Next button - always show, but disable when not available
    if (pagination.hasNext) {
      html += `
        <li>
          <button class="pagination-arrow" data-action="next-page" aria-label="${TranslationUtils.get('pagination.next', 'Next')}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.375 6.875L17.5 10M17.5 10L14.375 13.125M17.5 10H2.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </li>`;
    }

    return html;
  }

  generatePaginationInfo(pagination) {
    const { page, totalPages, total, limit } = pagination;

    // Handle cases where pagination data might be incomplete
    if (!page || !totalPages) {
      return '';
    }

    // If we have total items, use the translation string
    if (total && limit) {
      const translationKey = TranslationUtils.get('pagination.vouchers_info', '{count1} out of {count2} vouchers');
      const endItem = Math.min(page * limit, total);

      return translationKey
        .replace('{count1}', endItem)
        .replace('{count2}', total);
    }

    // Fallback to simple page info
    const pageInfoKey = TranslationUtils.get('pagination.page_of', 'Page {page} of {totalPages}');
    return pageInfoKey
      .replace('{page}', page)
      .replace('{totalPages}', totalPages);
  }

  getVisiblePages(currentPage, totalPages) {
    const pages = [];
    const maxVisible = 5; // Maximum number of page buttons to show

    if (totalPages <= maxVisible) {
      // Show all pages if total is small
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (currentPage <= 3) {
        // Near the beginning
        pages.push(2, 3);
        if (totalPages > 4) {
          pages.push('...');
        }
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        // Near the end
        pages.push('...');
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        // In the middle
        pages.push('...');
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  }

  renderVoucherItem(item) {
    if (item.type === 'giftcard_group') {
      return this.getVoucherGroupHTML(item);
    }
    return this.getVoucherCardHTML(item);
  }

  // Helper function to check if group cancellation is allowed
  canCancelGroupBooking(group) {
    // Use cancellation rules from the first gift card in the group
    // This ensures consistent cancellation days and extended protection for the whole group
    if (!group.vouchers || group.vouchers.length === 0) {
      return false;
    }

    // Already cancelled groups can't be cancelled again
    const status = group.booking_status?.toUpperCase();
    if (status && (status.includes('CANCELED') || status.includes('COMPLETED'))) {
      return false;
    }

    const firstVoucher = group.vouchers[0];
    const cancelCheck = this.canCancelBooking(firstVoucher);
    return cancelCheck.canCancel;
  }

  // Helper function to check if group completion is allowed
  canCompleteGroupBooking(group) {
    // Check if any voucher in the group can be completed
    return group.vouchers && group.vouchers.some(voucher => {
      const result = this.canCompleteBooking(voucher);
      return result.canComplete;
    });
  }

  getVoucherGroupHTML(group) {
    const voucherCount = group.voucher_count;
    const isNonActive = this.state.currentFilter === 'non_active' || this.state.currentFilter === 'non-active';
    const isSupplier = window.account?.isVendor || false;

    // Check current filter to determine which buttons to show
    const isPastBookingsTab = this.state.currentFilter === 'past-bookings';
    const isUpcomingBookingsTab = this.state.currentFilter === 'upcoming-bookings';
    const isBookingRequestsTab = this.state.currentFilter === 'booking-requests';

    // Check group booking_status - if null, show Book Experience and Ungroup buttons
    const groupBookingStatus = group.booking_status;
    const isGroupBookingStatusNull = groupBookingStatus === null || groupBookingStatus === undefined;

    // Check if group has any non-booked vouchers to show book experience button
    const hasNonBookedVouchers = group.vouchers && group.vouchers.some(voucher => {
      const voucherInProgress = this.isVoucherInProgress(voucher);
      return !voucher.is_booked && !voucherInProgress;
    });

    // Check if any voucher in the group has seasonal pause active
    const isGroupSeasonPaused = group.vouchers && group.vouchers.some(v => this.getSeasonPauseActive(v));

    // Check if this is supplier view and show checkbox for group selection (excluding booking-requests tab)
    const showCheckbox = this.vendorActions && this.vendorActions.isVendorPendingBookingsView() && !isBookingRequestsTab;

    // Determine action methods based on context
    const cancelAction = isSupplier ? 'vendor-group-cancel' : 'cancel-group-booking';
    const completeAction = isSupplier ? 'vendor-group-complete' : '';

    // Count group-level buttons to determine if we should show "About this experience" at group level
    let groupButtonCount = 0;
    if (isGroupBookingStatusNull && hasNonBookedVouchers && !isNonActive && !isGroupSeasonPaused) groupButtonCount++; // Book Experience
    if (isGroupBookingStatusNull && hasNonBookedVouchers && !isNonActive && isGroupSeasonPaused) groupButtonCount++; // Seasonality Info
    if (isGroupBookingStatusNull && !isNonActive) groupButtonCount++; // Ungroup
    if (!isGroupBookingStatusNull && isPastBookingsTab && isSupplier && this.canCompleteGroupBooking(group)) groupButtonCount++; // Complete Group Booking
    if (!isGroupBookingStatusNull && isUpcomingBookingsTab && !isNonActive && this.canCancelGroupBooking(group)) groupButtonCount++; // Cancel Group Booking
    if (!isGroupBookingStatusNull && !isPastBookingsTab && !isUpcomingBookingsTab && !isNonActive && this.canCancelGroupBooking(group) && groupBookingStatus !== 'IN_PROGRESS' && groupBookingStatus !== 'in_progress') groupButtonCount++; // Cancel Group Booking (other tabs)

    // Count edit date/time button for vendor on upcoming booked groups
    if (!isGroupBookingStatusNull && isUpcomingBookingsTab && !isNonActive && isSupplier) {
      const primaryVoucher = group.vouchers?.find(v => v.bookingToken || v.is_booked) || group.vouchers?.[0];
      if (primaryVoucher && primaryVoucher.bookingToken) {
        const hasDateTime = primaryVoucher.booking_date || primaryVoucher.booking_time ||
          primaryVoucher.booking_data?.date || primaryVoucher.booking_data?.time ||
          primaryVoucher.booking_data?.display_data?.date || primaryVoucher.booking_data?.display_data?.time;
        if (hasDateTime) groupButtonCount++; // Edit Date/Time
      }
    }

    // For IN_PROGRESS groups, count buttons from the inline function
    if (!isGroupBookingStatusNull && !isNonActive) {
      const primaryVoucher = group.vouchers?.find(v => v.bookingToken || v.is_booked || this.isVoucherInProgress(v)) || group.vouchers?.[0];
      const isGroupInProgress = groupBookingStatus === 'IN_PROGRESS' || groupBookingStatus === 'in_progress';
      const isVendor = window.account?.isVendor || false;

      if (isGroupInProgress && primaryVoucher) {
        if (isVendor && isBookingRequestsTab && primaryVoucher.bookingToken) groupButtonCount++; // Confirm Booking
        if (isVendor && isBookingRequestsTab && primaryVoucher.bookingToken && !this.isInternalBookingFlow(primaryVoucher)) groupButtonCount++; // Open conversation
        if (!isVendor) groupButtonCount++; // Chat with Supplier
        if (!isPastBookingsTab) groupButtonCount++; // Cancel request
      }
    }

    // Show "About this experience" at group level if there are 0 or 1 buttons
    const showAboutAtGroupLevel = groupButtonCount <= 1;

    // Build group buttons HTML
    let groupButtonsHTML = '';
    if (isGroupBookingStatusNull && hasNonBookedVouchers && !isNonActive && !isGroupSeasonPaused) {
      groupButtonsHTML += `<a href="${this.buildBookingUrlForGroup(group)}" class="Button Button--PrimaryOnDark">
        <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.actions.book_experience', 'Book Experience')}</span>
      </a>`;
    }
    // Show Seasonality Info button at group level for paused experiences
    if (isGroupBookingStatusNull && hasNonBookedVouchers && !isNonActive && isGroupSeasonPaused) {
      const pausedVoucher = group.vouchers.find(v => this.getSeasonPauseActive(v));
      if (pausedVoucher) {
        const seasonInfoLabel = TranslationUtils.get('customer.account.vouchers.seasonality_info_button', 'Season info');
        const popupText = this.buildSeasonalityText(pausedVoucher);
        groupButtonsHTML += `
          <button class="Button Button--PrimaryOnDark" data-action="toggle-popup" aria-controls="seasonality-info-popup" data-seasonality-info="${popupText.replace(/"/g, '&quot;')}">
            <span class="ButtonText u-pb1">${seasonInfoLabel}</span>
          </button>`;
      }
    }
    if (isGroupBookingStatusNull && !isNonActive) {
      groupButtonsHTML += `<button class="Button Button--Secondary" data-action="ungroup-vouchers" data-group-id="${group.group_id}">
        <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.group_management.ungroup', 'Ungroup')}</span>
      </button>`;
    }
    if (!isGroupBookingStatusNull && !isNonActive) {
      const primaryVoucher = group.vouchers?.find(v => v.bookingToken || v.is_booked || this.isVoucherInProgress(v)) || group.vouchers?.[0];
      const primaryVoucherId = primaryVoucher?.shopify_gift_card_id;
      const isGroupInProgress = groupBookingStatus === 'IN_PROGRESS' || groupBookingStatus === 'in_progress';
      const isVendor = window.account?.isVendor || false;
      if (isVendor && isPastBookingsTab) {
        groupButtonsHTML += `<button class="Button Button--PrimaryOnDark" data-action="vendor-group-complete" data-group-id="${group.group_id}">
          <span class="ButtonText u-pb1">${TranslationUtils.get('vendor.bookings.complete_group', 'Complete Group Booking')}</span>
        </button>`;
      }
      if (isGroupInProgress && primaryVoucherId) {
        if (isVendor && isBookingRequestsTab && primaryVoucher.bookingToken) {
          const returnTo = encodeURIComponent(window.location.search);
          const useInternal = this.isInternalBookingFlow(primaryVoucher);
          const confirmSeg = useInternal ? 'booking/internal/confirmation' : 'booking/confirmation';
          const confirmationUrl = `${window.proxyPath || '/apps/proxy'}/${confirmSeg}/${primaryVoucher.bookingToken}?return_to=${returnTo}`;
          groupButtonsHTML += `<a href="${confirmationUrl}" class="Button Button--PrimaryOnDark">
            <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.actions.confirm_booking', 'Confirm Booking')}</span>
          </a>`;
        }
        if (isVendor && isBookingRequestsTab && primaryVoucher.bookingToken && !this.isInternalBookingFlow(primaryVoucher)) {
          const openChatUnreadTooltip = TranslationUtils.get('vendor.bookings.open_conversation_unread_tooltip', 'Unread messages');
          const safeOpenChatTooltip = String(openChatUnreadTooltip).replace(/"/g, '&quot;');
          groupButtonsHTML += `<button class="Button Button--Secondary open-booking-chat-btn" data-action="open-booking-chat" data-booking-token="${primaryVoucher.bookingToken}" data-voucher-id="${primaryVoucherId}">
            <span class="open-booking-chat__unread-badge" data-unread-badge data-tooltip="${safeOpenChatTooltip}" aria-hidden="true"><span class="open-booking-chat__unread-count" data-unread-count aria-hidden="true">0</span></span>
            <span class="ButtonText u-pb1">${this.getVendorChatButtonLabel(primaryVoucher)}</span>
          </button>`;
        }
        if (!isVendor) {
          groupButtonsHTML += `<button class="Button Button--PrimaryOnDark" data-action="toggle-customer-support" data-voucher-id="${primaryVoucherId}">
            <span class="ButtonText u-pb1">${this.getChatWithSupplierLabel()}</span>
          </button>`;
        }
        if (!isPastBookingsTab) {
          groupButtonsHTML += `<button class="Button Button--Secondary" data-action="cancel-group-request" data-group-id="${group.group_id}">
            <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.actions.cancel_request', 'Cancel Request')}</span>
          </button>`;
        }
        groupButtonsHTML += this.getAutomaticBookingLabelHTML(primaryVoucher);
      }
    }
    if (!isGroupBookingStatusNull && isUpcomingBookingsTab && !isNonActive && this.canCancelGroupBooking(group)) {
      groupButtonsHTML += `<button class="Button Button--Secondary" data-action="${cancelAction}" data-group-id="${group.group_id}">
        <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.group_management.cancel_group_booking', 'Cancel Group Booking')}</span>
      </button>`;
    }
    // Show edit date/time button for vendor on upcoming booked group bookings
    if (!isGroupBookingStatusNull && isUpcomingBookingsTab && !isNonActive && isSupplier) {
      const primaryVoucher = group.vouchers?.find(v => v.bookingToken || v.is_booked) || group.vouchers?.[0];
      if (primaryVoucher && primaryVoucher.bookingToken) {
        const hasDateTime = primaryVoucher.booking_date || primaryVoucher.booking_time ||
          primaryVoucher.booking_data?.date || primaryVoucher.booking_data?.time ||
          primaryVoucher.booking_data?.display_data?.date || primaryVoucher.booking_data?.display_data?.time;

        if (hasDateTime) {
          const bookingDate = primaryVoucher.booking_date || primaryVoucher.booking_data?.date || primaryVoucher.booking_data?.display_data?.date || '';
          const bookingTime = primaryVoucher.booking_time || primaryVoucher.booking_data?.time || primaryVoucher.booking_data?.display_data?.time || '';
          const voucherName = primaryVoucher.productTitle || primaryVoucher.product_title || '';
          const voucherDisplayId = primaryVoucher.booking_reference || primaryVoucher.reference_number || primaryVoucher.bookingReference || primaryVoucher.last_characters || primaryVoucher.code_last4 || primaryVoucher.code || '';

          const internalCalAttr = this.isInternalBookingFlow(primaryVoucher) ? ' data-internal-calendar-booking="true"' : '';
          groupButtonsHTML += `<button class="Button Button--Tertiary"
                  data-action="edit-booking-datetime"
                  data-booking-token="${primaryVoucher.bookingToken}"
                  data-current-date="${bookingDate}"
                  data-current-time="${bookingTime}"
                  data-voucher-name="${voucherName}"
                  data-voucher-display-id="${voucherDisplayId}"${internalCalAttr}>
           <span class="ButtonText u-pb1">${TranslationUtils.get('vendor.bookings.actions.edit_datetime', 'Edit Date/Time')}</span>
         </button>`;
        }
      }
    }
    if (!isGroupBookingStatusNull && !isPastBookingsTab && !isUpcomingBookingsTab && !isNonActive && this.canCancelGroupBooking(group) && groupBookingStatus !== 'IN_PROGRESS' && groupBookingStatus !== 'in_progress') {
      groupButtonsHTML += `<button class="Button Button--Secondary" data-action="${cancelAction}" data-group-id="${group.group_id}">
        <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.group_management.cancel_group_booking', 'Cancel Group Booking')}</span>
      </button>`;
    }
    if (showAboutAtGroupLevel && !isSupplier && group.vouchers?.[0]?.productHandle) {
      groupButtonsHTML += `<button class="Button Button--${groupButtonCount === 0 ? 'PrimaryOnDark' : 'Secondary'}" data-action="open-detailed" data-url="/products/${group.vouchers[0].productHandle}">
        <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.about_experience', 'About This Experience')}</span>
      </button>`;
    }

    // Groups with booking status: single new-design card (customer + vendor)
    if (this.shouldUseBookingRequestGroupCard(group)) {
      const primaryVoucher = group.vouchers?.find(v => v.bookingToken || v.is_booked || this.isVoucherInProgress(v)) || group.vouchers?.[0];
      if (primaryVoucher) {
        return this.getBookingRequestCardHTML(primaryVoucher, group);
      }
    }

    return `
      <div class="voucher-group animatedContent ${showCheckbox ? 'account-voucher-item' : ''}" 
           data-group-id="${group.group_id}" 
           data-group-vouchers='${JSON.stringify(group.vouchers).replace(/'/g, '&#39;')}' 
           data-group-info='${JSON.stringify({ productTitle: group.productTitle, voucher_count: group.voucher_count })}'
           data-group-booking-status="${groupBookingStatus || ''}">
        <div class="voucher-group__info">
          <div class="voucher-group__header">
            ${showCheckbox ? `
              <label class="checkbox-wrapper">
                <input type="checkbox" class="booking-card-checkbox group-checkbox" data-group-id="${group.group_id}" id="group-checkbox-${this.sanitizeId(group.group_id)}" style="display: none;">
                <div class="custom-checkbox">
                  <div class="checkbox-empty">${this.getCheckboxIcon('checkbox-empty')}</div>
                  <div class="checkbox-solid">${this.getCheckboxIcon('checkbox-solid')}</div>
                </div>
              </label>
            ` : ''}
            <h4 class="u-s2">${group.productTitle || 'Gift Card Group'} (${TranslationUtils.get('customer.account.vouchers.group_management.group_of', 'Group of {{ count }} vouchers').replace('{{ count }}', voucherCount)})</h4>
          </div>
        </div>
        <div class="voucher-group__vouchers">
          ${group.vouchers.map((voucher, index) => this.getVoucherCardHTML(voucher, true, group, index === 0, showAboutAtGroupLevel, index === 0 ? groupButtonsHTML : '')).join('')}
        </div>
      </div>
    `;
  }

  getBookingRequestButtons(card, group = null) {
    const isVendor = window.account?.isVendor || false;
    const isPaused = this.getSeasonPauseActive(card)
      && !this.isBookingConfirmed(card)
      && !this.isCompletedOrCanceledStatus(card)
      && !this.isCancellationStatus(card);
    const vId = card.shopify_gift_card_id;

    const makeBtn = (cls, action, text, extra = '') =>
      `<button class="Button Button--${cls}" data-action="${action}" data-voucher-id="${vId}" ${extra}>
         <div class="ButtonTextContainer"><span class="ButtonText u-pb1">${text}</span></div>
       </button>`;

    const makeCancelBtn = (labelKey = 'customer.account.vouchers.actions.cancel_request', fallback = 'Cancel request') => {
      const cancelText = TranslationUtils.get(labelKey, fallback);
      if (group?.group_id) {
        return `<button class="Button Button--Secondary" data-action="cancel-group-request" data-group-id="${group.group_id}" data-voucher-id="${vId}">
          <div class="ButtonTextContainer"><span class="ButtonText u-pb1">${cancelText}</span></div>
        </button>`;
      }
      return makeBtn('Secondary', 'voucher-action', cancelText, 'data-method="init-cancel-booking"');
    };

    const seasonLearnMoreBtn = () => {
      this.ensureSeasonalityPopup();
      const learnMoreLabel = TranslationUtils.get('general.learn_more', 'Learn more');
      const popupText = this.buildSeasonalityText(card).replace(/"/g, '&quot;');
      return `<button class="Button Button--PrimaryOnDark" data-action="toggle-popup" aria-controls="seasonality-info-popup" data-seasonality-info="${popupText}" data-voucher-id="${vId}">
        <div class="ButtonTextContainer"><span class="ButtonText u-pb1">${learnMoreLabel}</span></div>
      </button>`;
    };

    // Ungroup — same gate as the legacy group card: only before a booking exists.
    const ungroupBtn = () => {
      const groupStatus = group?.booking_status;
      if (!group?.group_id || (groupStatus !== null && groupStatus !== undefined)) return '';
      const ungroupText = TranslationUtils.get('customer.account.vouchers.group_management.ungroup', 'Ungroup');
      return `<button class="Button Button--Secondary" data-action="ungroup-vouchers" data-group-id="${group.group_id}">
         <div class="ButtonTextContainer"><span class="ButtonText u-pb1">${ungroupText}</span></div>
       </button>`;
    };

    // Completed / canceled cards: no primary actions (Figma)
    if (this.isCompletedOrCanceledStatus(card) || this.isVendorCompletedCanceledTab()) {
      return '';
    }

    if (isVendor) {
      const isUpcomingBookingsTab = this.state.currentFilter === 'upcoming-bookings';
      const isPastBookingsTab = this.state.currentFilter === 'past-bookings';

      if (this.isBookingConfirmed(card) && (isUpcomingBookingsTab || isPastBookingsTab) && this.vendorActions) {
        return this.vendorActions.getVendorBookedBookingRequestButtons(card, group);
      }

      const confirmText = TranslationUtils.get('customer.account.vouchers.actions.confirm_booking', 'Confirm booking');

      const buttons = [];

      // Paused booking request: the pause only stops customers booking. The vendor
      // still confirms and chats, so show the note above the usual buttons.
      if (isPaused) {
        const pauseNote = TranslationUtils.get(
          'customer.account.vouchers.seasonal_pause_booking_note',
          'Booking is temporarily paused.'
        );
        buttons.push(`<p class="booking-request-card__pause-note u-p4 grey-primary-text">${pauseNote}</p>`);
      }

      if (card.bookingToken) {
        const isInternalFlow = String(card?.booking_data?.flow || '').toLowerCase() === 'internal';
        const returnTo = encodeURIComponent(window.location.search ? `?${new URLSearchParams(window.location.search).toString()}` : '');
        const confirmPath = isInternalFlow ? 'booking/internal/confirmation' : 'booking/confirmation';
        const confirmUrl = `${window.proxyPath || ''}/${confirmPath}/${card.bookingToken}?return_to=${returnTo}`;
        buttons.push(`<a class="Button Button--PrimaryOnDark" href="${confirmUrl}">
          <div class="ButtonTextContainer"><span class="ButtonText u-pb1">${confirmText}</span></div>
        </a>`);
      }

      // Cancel before chat per Figma
      buttons.push(makeCancelBtn());

      const chatLabel = this.getVendorChatButtonLabel(card);
      {
        const chatHandle = card.bookingToken;
        const internalAttr = '';
        buttons.push(`<button class="Button Button--Secondary open-booking-chat-btn" data-action="open-booking-chat" data-booking-token="${chatHandle || ''}" ${internalAttr} data-voucher-id="${vId}">
          <span class="open-booking-chat__unread-badge" data-unread-badge aria-hidden="true"><span class="open-booking-chat__unread-count" data-unread-count aria-hidden="true">0</span></span>
          <div class="ButtonTextContainer"><span class="ButtonText u-pb1">${chatLabel}</span></div>
        </button>`);
      }

      return buttons.join('');
    }

    // Customer view
    const isInProgress = this.isVoucherInProgress(card);
    const isNonActive = this.state.currentFilter === 'non_active' || this.state.currentFilter === 'non-active';

    const groupBookExperienceBtn = () => {
      const groupBookingStatus = group?.booking_status;
      if (!group?.vouchers?.length) return '';
      if (!(groupBookingStatus === null || groupBookingStatus === undefined)) return '';
      const hasNonBooked = group.vouchers.some((v) => !v.is_booked && !this.isVoucherInProgress(v));
      if (!hasNonBooked) return '';
      if (group.vouchers.some((v) => this.getSeasonPauseActive(v))) return '';
      return `<a href="${this.buildBookingUrlForGroup(group)}" class="Button Button--PrimaryOnDark">
        <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.actions.book_experience', 'Book Experience')}</span>
      </a>`;
    };

    if (!isInProgress) {
      if (isNonActive || this.isExpired(card)) return '';
      if (isPaused) {
        const pauseNote = TranslationUtils.get(
          'customer.account.vouchers.seasonal_pause_booking_note',
          'Booking is temporarily paused.'
        );
        // getVoucherButtons already prepends Learn more for paused vouchers
        return `
          <p class="booking-request-card__pause-note u-p4 grey-primary-text">${pauseNote}</p>
          ${this.getVoucherButtons(card, !!group?.group_id, group, true, false)}
          ${ungroupBtn()}
        `;
      }
      // getVoucherButtons suppresses Book Experience for grouped vouchers because
      // the old group card rendered a group-level one. This consolidated card is
      // now that group card, so it has to supply the group-level button itself.
      return `${groupBookExperienceBtn()}${this.getVoucherButtons(card, !!group?.group_id, group, true, false)}${ungroupBtn()}`;
    }

    const chatText = this.getChatWithSupplierLabel();
    const customerBtns = [];

    if (isPaused) {
      const pauseNote = TranslationUtils.get(
        'customer.account.vouchers.seasonal_pause_booking_note',
        'Booking is temporarily paused.'
      );
      customerBtns.push(`<p class="booking-request-card__pause-note u-p4 grey-primary-text">${pauseNote}</p>`);
      customerBtns.push(seasonLearnMoreBtn());
    }

    const cancelCheck = this.canCancelBooking(card);
    if (cancelCheck.canCancel) {
      customerBtns.push(makeCancelBtn());
    } else if (cancelCheck.reason === 'vendor_cancellation_disabled') {
      // Chat button is pushed right below regardless, so just the note here.
      const contactText = TranslationUtils.get(
        'customer.account.vouchers.notifications.cancellation_contact_vendor',
        'To change your date, please contact the vendor via chat.'
      );
      customerBtns.push(`<p class="booking-request-card__pause-note u-p4 grey-primary-text">${contactText}</p>`);
    }

    customerBtns.push(makeBtn('Secondary', 'toggle-customer-support', chatText));

    return customerBtns.join('');
  }

  getGroupVouchersOrdered(group, fallbackCard) {
    if (group?.vouchers?.length) return group.vouchers;
    return fallbackCard ? [fallbackCard] : [];
  }

  getGroupVoucherCodes(vouchers) {
    return vouchers
      .map((voucher) => (voucher.last_characters ? `****${voucher.last_characters}` : null))
      .filter(Boolean);
  }

  getGroupParticipantVouchers(vouchers) {
    // Every voucher in a group resolves to the same booking metaobject, so they
    // normally carry an identical participant payload. Collapse duplicates so
    // names and counts are not multiplied by the number of vouchers. Legacy
    // payloads that differ per voucher are all kept.
    const seen = new Set();
    return vouchers.filter((voucher) => {
      const key = JSON.stringify(voucher.booking_data?.participant ?? null);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  getGroupParticipantNames(vouchers) {
    const names = [];
    vouchers.forEach((voucher) => {
      names.push(...this.getBookingParticipantNames(voucher.booking_data?.participant));
    });
    return names;
  }

  getBookingRequestCardHTML(card, group = null) {
    const isVendor = window.account?.isVendor || false;
    const groupVouchers = this.getGroupVouchersOrdered(group, card);
    const isGroupCard = groupVouchers.length > 1;
    const groupAttr = group?.group_id
      ? ` data-group-id="${group.group_id}" data-group-vouchers='${JSON.stringify(groupVouchers).replace(/'/g, '&#39;')}'`
      : '';

    // Bulk-select checkbox — same gate and markup as the legacy card, so select-all
    // and the Cancel all / Complete all controls keep working on the redesigned card.
    const showCheckbox = !!this.vendorActions
      && this.vendorActions.isVendorPendingBookingsView()
      && this.state.currentFilter !== 'booking-requests'
      && (group?.group_id ? true : (card.is_booked || this.isVoucherInProgress(card)));
    const checkboxId = this.sanitizeId(group?.group_id || card.shopify_gift_card_id);
    const checkboxHTML = showCheckbox ? `
          <label class="checkbox-wrapper">
            <input type="checkbox" class="booking-card-checkbox${group?.group_id ? ' group-checkbox' : ''}" data-voucher-id="${card.shopify_gift_card_id}"${group?.group_id ? ` data-group-id="${group.group_id}"` : ''} id="checkbox-${checkboxId}" style="display: none;">
            <div class="custom-checkbox">
              <div class="checkbox-empty">${this.getCheckboxIcon('checkbox-empty')}</div>
              <div class="checkbox-solid">${this.getCheckboxIcon('checkbox-solid')}</div>
            </div>
          </label>` : '';
    const title = isGroupCard
      ? `${card.productTitle || ''} (${TranslationUtils.get('customer.account.vouchers.group_management.group_of', 'Group of {{ count }} vouchers').replace('{{ count }}', group.voucher_count || groupVouchers.length)})`
      : (card.productTitle || '');

    // ── Image ──────────────────────────────────────────────────
    const imageHTML = card.productImageUrl
      ? `<img src="${card.productImageUrl}" alt="${card.productTitle || ''}" loading="lazy">`
      : '';

    // ── Status (pause / completed / canceled / carried / default) ─
    const statusDisplay = this.getBookingRequestStatusDisplay(card);
    const isPaused = !!statusDisplay.isPaused;
    const statusLabel = TranslationUtils.get('customer.account.vouchers.status', 'Status');
    const statusText = statusDisplay.text;
    const statusClass = statusDisplay.className;
    let statusInfoBtn = statusDisplay.iconHTML || '';
    let statusNote = '';

    if (statusDisplay.note) {
      statusNote = `<span class="booking-request-card__status-note u-p4 grey-primary-text">(${statusDisplay.note})</span>`;
    } else if (!isPaused && this.isVoucherInProgress(card)) {
      // IN_PROGRESS conversation notes
      const hasConversation = card.has_conversation || !!card.conversation_id;
      const unread = card.unread_messages_count || 0;
      // Who sent the last message: 'user' = customer, 'business' = supplier. This is
      // what decides whose turn it is; unread counts reset the moment the recipient
      // opens the chat, so they cannot answer that on their own.
      const lastAuthor = card.last_message_author_type;
      let noteKey;
      if (card.status_note) {
        noteKey = card.status_note;
      } else if (!hasConversation) {
        noteKey = 'request_sent';
      } else if (lastAuthor) {
        const awaitingVendor = lastAuthor === 'user';
        if (isVendor) {
          noteKey = awaitingVendor ? 'waiting_you' : 'waiting_customer';
        } else if (!awaitingVendor) {
          noteKey = 'waiting_you';
        }
      } else if (unread > 0) {
        noteKey = 'waiting_you';
      } else if (isVendor) {
        noteKey = 'waiting_customer';
      }
      if (noteKey) {
        const noteText = TranslationUtils.get(
          `customer.account.vouchers.status_note_${noteKey}`,
          noteKey.replace(/_/g, ' ')
        );
        statusNote = `<span class="booking-request-card__status-note u-p4 grey-primary-text">(${noteText})</span>`;
      }
    }

    // ── Meta rows ──────────────────────────────────────────────
    const isConfirmed = this.hasConfirmedStyleMeta(card);
    const validUntilLabel = TranslationUtils.get('customer.account.vouchers.valid_until', 'Valid until');
    const locationLabel = TranslationUtils.get('customer.account.vouchers.location', 'Location');
    const bookingMeta = this.getBookingRequestMeta(card);
    const expiresDate = card.expires_at ? this.formatDate(card.expires_at) : '—';
    const isExpiringSoon = this.isExpiringSoon(card);
    const expiringLabel = TranslationUtils.get('customer.account.vouchers.expiring', 'Expiring');
    const expiringHTML = isExpiringSoon
      ? `<span class="u-p4 booking-request-card__expiring">(${expiringLabel})</span>`
      : '';
    const location = this.getVoucherLocationDisplay(card);

    // ── Participant count ──────────────────────────────────────
    const participantVouchers = this.getGroupParticipantVouchers(groupVouchers);
    const groupParticipantTotal = participantVouchers.reduce((sum, voucher) => {
      const count = voucher.booking_data?.display_data?.participant_count
        || voucher.booking_data?.participant_count
        || 0;
      return sum + (Number(count) || 0);
    }, 0);
    const participantNames = this.getGroupParticipantNames(participantVouchers);
    const explicitParticipantCount = groupParticipantTotal
      || card.booking_data?.display_data?.participant_count
      || card.booking_data?.participant_count
      || 0;
    // The quantity selector can under-state the party size, so never show fewer
    // people than we have participant names for (mirrors resolveParticipantCount).
    const participantCount = Math.max(Number(explicitParticipantCount) || 0, participantNames.length) || '';
    const participantCountLabel = TranslationUtils.get('customer.account.vouchers.participant_count', 'Participant count');

    // ── "More information" detail rows ─────────────────────────
    const moreInfoLabel = TranslationUtils.get('customer.account.vouchers.more_information', 'More information');
    const voucherType = card.is_gift
      ? TranslationUtils.get('customer.account.vouchers.gift', 'Gift')
      : TranslationUtils.get('customer.account.vouchers.purchased', 'Purchase');

    const referenceNumber = this.formatGiftCardNumber(card.shopify_gift_card_id);
    const voucherCodes = this.getGroupVoucherCodes(groupVouchers);

    let detailRowsHTML = `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.type', 'Purchase/Gift')}:</span>
        <span class="u-s3">${voucherType}</span>
      </div>`;

    if (card.productVariantTitle) {
      detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.variant_name', 'Variant')}:</span>
        <span class="u-s3">${card.productVariantTitle}</span>
      </div>`;
    }

    if (referenceNumber) {
      detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.purchase_number', 'Reference number')}:</span>
        <span class="u-s3">${referenceNumber}</span>
      </div>`;
    }

    if (voucherCodes.length) {
      detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.code', 'Voucher ID')}:</span>
        <span class="u-s3">${voucherCodes.join(', ')}</span>
      </div>`;
    }

    if (card.extended_protection != null) {
      detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.extended_protection', 'Cancel protection')}:</span>
        <span class="u-s3">${card.extended_protection
          ? TranslationUtils.get('customer.account.vouchers.extended_protection_active', 'Active')
          : TranslationUtils.get('customer.account.vouchers.extended_protection_inactive', 'None')}</span>
      </div>`;
    }

    if (card.booking_data?.supplier_name) {
      detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.supplier', 'Supplier')}:</span>
        <span class="u-s3">${card.booking_data.supplier_name}</span>
      </div>`;
    }

    if (isVendor && card.booking_data?.customer) {
      const c = card.booking_data.customer;
      if (c.email) {
        detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.customer_email', 'Customer Email')}:</span>
        <span class="u-s3">${c.email}</span>
      </div>`;
      }
      if (c.phone) {
        detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.customer_phone', 'Customer Phone')}:</span>
        <span class="u-s3">${c.phone}</span>
      </div>`;
      }
    }

    if (participantNames.length) {
      detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.participant_names', 'Name of participant')}:</span>
        <span class="u-s3">${participantNames.join(', ')}</span>
      </div>`;
    }

    // The legacy card showed this and the cancelled tab now routes here, so keep it
    // rather than drop the cancellation date in the conversion.
    if (card.canceled_at) {
      detailRowsHTML += `
      <div class="booking-request-card__detail-row">
        <span class="u-p3">${TranslationUtils.get('customer.account.vouchers.cancelled_at', 'Cancelled')}:</span>
        <span class="u-s3">${this.formatDate(card.canceled_at)}</span>
      </div>`;
    }

    if (isVendor && this.isVendorCancelledVoucher(card)) {
      detailRowsHTML += this.getCancellationHandledHTML(card);
    }

    // ── Action buttons — rendered directly for correct text + Figma styles ──
    const rawButtonsHTML = this.getBookingRequestButtons(card, group);
    const groupInfoNote = isGroupCard
      ? `<p class="booking-request-card__group-note u-p4 grey-primary-text">${TranslationUtils.get('customer.account.vouchers.group_management.info_applies_to_all', 'Information applies to all grouped vouchers.')}</p>`
      : '';

    // ── Helper sub-templates ────────────────────────────────────
    const buildMetaPill = (label, value, valueExtra = '') => `
      <div class="booking-request-card__meta-item">
        <span class="u-p4 booking-request-card__meta-label">${label}</span>
        ${valueExtra
          ? `<div class="booking-request-card__meta-value-group"><span class="u-s3">${value}</span>${valueExtra}</div>`
          : `<span class="u-s3">${value}</span>`}
      </div>`;

    const buildExtraItem = (label, value, valueExtra = '') => `
      <div class="booking-request-card__extra-item">
        <span class="u-p4 grey-primary-text">${label}${label.endsWith(':') ? '' : ':'}</span>
        ${valueExtra
          ? `<div class="booking-request-card__meta-value-group"><span class="u-s3">${value}</span>${valueExtra}</div>`
          : `<span class="u-s3">${value}</span>`}
      </div>`;

    // Booking history is the completed tab. Other tabs keep the extras row as it was.
    const invoiceNumbers = this.state.currentFilter === 'completed'
      ? this.getInvoiceNumbers(groupVouchers)
      : [];
    const invoiceLabel = TranslationUtils.get('customer.account.vouchers.invoice_number', 'Invoice number');
    const invoiceExtra = invoiceNumbers.length
      ? buildExtraItem(invoiceLabel, invoiceNumbers.map((number) => this.escapeHtmlAttribute(number)).join(', '))
      : '';

    let metaContentHTML;
    let extrasHTML = '';

    if (isPaused) {
      // Pause: location pill only, then participant + valid until as extras
      const metaItems = [
        location ? buildMetaPill(locationLabel, location) : ''
      ].filter(Boolean).join('');

      metaContentHTML = metaItems
        ? `<div class="booking-request-card__meta">${metaItems}</div>`
        : '';

      const extras = [
        participantCount ? buildExtraItem(participantCountLabel, participantCount) : '',
        buildExtraItem(validUntilLabel, expiresDate, expiringHTML),
        invoiceExtra
      ].filter(Boolean).join('');

      if (extras) {
        extrasHTML = `
          <hr class="booking-request-card__divider">
          <div class="booking-request-card__extras">${extras}</div>
          <hr class="booking-request-card__divider">`;
      }
    } else if (isConfirmed) {
      const experienceDateLabel = TranslationUtils.get('customer.account.vouchers.experience_date', 'Experience date');
      const experienceTimeLabel = TranslationUtils.get('customer.account.vouchers.experience_time', 'Experience time');
      const experienceDate = this.getBookingExperienceDate(card);
      const experienceTime = this.getBookingExperienceTime(card);

      // Pills sit beside the image on mobile; participant/valid-until live below the
      // divider as plain full-width columns (Figma BookingRequest/375 + /1440).
      const metaItems = [
        experienceDate ? buildMetaPill(experienceDateLabel, experienceDate) : '',
        experienceTime ? buildMetaPill(experienceTimeLabel, experienceTime) : '',
        location ? buildMetaPill(locationLabel, location) : ''
      ].filter(Boolean).join('');

      metaContentHTML = metaItems
        ? `<div class="booking-request-card__meta">${metaItems}</div>`
        : '';

      const extras = [
        participantCount ? buildExtraItem(participantCountLabel, participantCount) : '',
        buildExtraItem(validUntilLabel, expiresDate, expiringHTML),
        invoiceExtra
      ].filter(Boolean).join('');

      if (extras) {
        extrasHTML = `
          <hr class="booking-request-card__divider">
          <div class="booking-request-card__extras">${extras}</div>
          <hr class="booking-request-card__divider">`;
      }
    } else {
      const metaItems = [
        bookingMeta ? buildMetaPill(bookingMeta.label, bookingMeta.date) : '',
        buildMetaPill(validUntilLabel, expiresDate, expiringHTML),
        location ? buildMetaPill(locationLabel, location) : ''
      ].filter(Boolean).join('');

      metaContentHTML = `<div class="booking-request-card__meta">${metaItems}</div>`;

      const participantExtra = participantCount ? `
            <div class="booking-request-card__extra-item">
              <span class="u-p4 grey-primary-text">${participantCountLabel}</span>
              <span class="u-s3">${participantCount}</span>
            </div>` : '';

      if (participantExtra || invoiceExtra) {
        extrasHTML = `
          <hr class="booking-request-card__divider">
          <div class="booking-request-card__extras">${participantExtra}${invoiceExtra}</div>
          <hr class="booking-request-card__divider">`;
      }
    }

    const bookingCreatedLabel = TranslationUtils.get('customer.account.vouchers.booking_created', 'Booking created');
    const confirmedAt = this.getBookingConfirmedAt(card) || this.getVendorBookingRequestedAt(card);
    const showBookingCreated = Boolean(confirmedAt) && (isConfirmed || isPaused || this.isCompletedOrCanceledStatus(card));
    const bookingCreatedHTML = showBookingCreated ? `
          <div class="booking-request-card__detail-row booking-request-card__created-row">
            <span class="u-p3">${bookingCreatedLabel}:</span>
            <span class="u-s3">${this.formatDate(confirmedAt)}</span>
          </div>` : '';

    return `
      <div class="booking-request-card animatedContent${showCheckbox ? ' account-voucher-item' : ''}" data-voucher-id="${card.shopify_gift_card_id}"${groupAttr}>
        ${checkboxHTML}
        <!-- Desktop: image as direct flex sibling -->
        <div class="booking-request-card__image booking-request-card__image--desktop">${imageHTML}</div>

        <div class="booking-request-card__body">

          <!-- Header (always visible) -->
          <div class="booking-request-card__header">
            <h4 class="booking-request-card__title u-s2">${title}</h4>
            <div class="booking-request-card__status-row">
              <span class="u-p3 grey-primary-text">${statusLabel}:</span>
              <span class="voucher-card__seasonal-pause-value booking-request-card__status">
                <span class="u-s3 ${statusClass}">${statusText}</span>
                ${statusInfoBtn}
              </span>
              ${statusNote}
            </div>
          </div>

          <!-- Mobile: image + stacked meta -->
          <div class="booking-request-card__mobile-image-meta">
            <div class="booking-request-card__image">${imageHTML}</div>
            <div class="booking-request-card__meta booking-request-card__meta--stacked">${metaContentHTML}</div>
          </div>

          <!-- Desktop: meta row(s) -->
          <div class="booking-request-card__desktop-meta">
            ${metaContentHTML}
          </div>

          ${extrasHTML ? extrasHTML : '<hr class="booking-request-card__divider">'}

          <!-- "More information" — project Dropdown pattern -->
          <div class="Dropdown booking-request-card__more-dropdown">
            <button type="button" class="Dropdown--Button booking-request-card__more-btn u-s3" data-action="toggle-dropdown" aria-expanded="false">
              ${moreInfoLabel}
              <svg class="booking-request-card__chevron" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M5 7.5L10 12.5L15 7.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
              </svg>
            </button>
            <div class="Dropdown--Content booking-request-card__more-content" aria-hidden="true">
              <div class="booking-request-card__more-inner">
                ${detailRowsHTML}
              </div>
            </div>
          </div>

          ${bookingCreatedHTML}
          ${groupInfoNote}

        </div>

        ${rawButtonsHTML ? `
        <voucher-action-handler class="booking-request-card__actions">
          ${rawButtonsHTML}
        </voucher-action-handler>` : ''}

      </div>
    `;
  }

  getBookingRequestStatusDisplay(card) {
    // A seasonal pause on the experience says nothing about a booking that already
    // exists — booked, finished or cancelled — so it must not mask the real status.
    const isSettled = this.isBookingConfirmed(card)
      || this.isCompletedOrCanceledStatus(card)
      || this.isCancellationStatus(card);
    if (this.getSeasonPauseActive(card) && !isSettled) {
      this.ensureSeasonalityPopup();
      const pausePopupText = this.buildSeasonalityText(card).replace(/"/g, '&quot;');
      return {
        text: TranslationUtils.get('customer.account.vouchers.seasonal_pause', 'Temporarily paused'),
        className: 'voucher-status--paused',
        note: null,
        isPaused: true,
        iconHTML: `<button type="button" class="voucher-card__seasonal-pause-info" data-action="toggle-popup" aria-controls="seasonality-info-popup" data-seasonality-info="${pausePopupText}" aria-label="${TranslationUtils.get('general.learn_more', 'Learn more')}">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" focusable="false">
            <circle cx="10" cy="10" r="7.25" stroke="currentColor" stroke-width="1.5"/>
            <path d="M8 7.5V12.5M12 7.5V12.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
          </svg>
        </button>`
      };
    }

    const status = this.getBookingStatusKey(card);

    if (status === 'COMPLETED') {
      return {
        text: TranslationUtils.get('customer.account.vouchers.booking.completed', 'Completed'),
        className: 'voucher-status--completed',
        note: TranslationUtils.get('customer.account.vouchers.status_note_experience_finished', 'Experience finished')
      };
    }

    if (status === 'COMPLETED_CARRIED') {
      return {
        text: TranslationUtils.get('customer.account.vouchers.booking_status.carried', 'Carried'),
        className: 'voucher-status--carried',
        note: TranslationUtils.get('customer.account.vouchers.status_note_past_booking_date', 'Past booking date')
      };
    }

    if (status === 'CANCELED_CUSTOMER') {
      return {
        text: TranslationUtils.get('customer.account.vouchers.booking_status.canceled_customer', 'Customer canceled'),
        className: 'voucher-status--canceled-customer',
        note: null
      };
    }

    if (status === 'CANCELED_VENDOR') {
      return {
        text: TranslationUtils.get('customer.account.vouchers.booking_status.canceled_vendor', 'Supplier canceled'),
        className: 'voucher-status--canceled-vendor',
        note: null
      };
    }

    if (['CANCELED', 'CANCELLED', 'COMPLETED_CANCELED'].includes(status)) {
      return {
        text: TranslationUtils.get('customer.account.vouchers.booking.canceled', 'Canceled'),
        className: 'voucher-status--canceled',
        note: TranslationUtils.get('customer.account.vouchers.status_note_past_booking_date', 'Past booking date')
      };
    }

    // A booked experience is still a booking once the voucher's own validity
    // runs out: the voucher was used to book it. Without this, getStatusText
    // checks expiry first and past bookings awaiting confirmation read
    // "Expired" instead of booked.
    if (status === 'BOOKED') {
      return {
        text: VoucherUtils.getBookingStatusText(status),
        className: VoucherUtils.getBookingStatusClass(status),
        note: null
      };
    }

    return {
      text: this.getStatusText(card),
      className: this.getStatusClass(card),
      note: null
    };
  }

  getVoucherCardHTML(card, isInGroup = false, group = null, isFirstInGroup = false, hideAboutExperience = false, groupButtonsHTML = '') {
    const isRedeemed = card.is_redeemed;
    const isExpired = this.isExpired(card);
    const isNonActive = this.state.currentFilter === 'non_active' || this.state.currentFilter === 'non-active';
    const voucherType = card.is_gift
      ? TranslationUtils.get('customer.account.vouchers.gift', 'Gift')
      : TranslationUtils.get('customer.account.vouchers.purchased', 'Purchased');

  // Check if this is a supplier viewing pending bookings
  const isInProgress = this.isVoucherInProgress(card);
  const isBookingRequestsTab = this.state.currentFilter === 'booking-requests';

  // New booking-request card: vendor booking-requests tab + all customer vouchers
  if (this.shouldUseBookingRequestCard(card, isInGroup)) {
    return this.getBookingRequestCardHTML(card, group);
  }

  const showCheckbox = this.vendorActions && this.vendorActions.isVendorPendingBookingsView() && (card.is_booked || isInProgress) && !isInGroup && !isBookingRequestsTab;

  // Check if vendor is on tabs where they need to act on bookings even if card is expired
  // - past-bookings: Need to complete/close bookings for experiences already carried out
  // - booking-requests: Customer may have requested while active, but card expired before vendor reviewed
  const isVendorOnActionableTab = this.vendorActions && 
                                  this.vendorActions.isVendorPendingBookingsView() && 
                                  (this.state.currentFilter === 'past-bookings' || 
                                   this.state.currentFilter === 'booking-requests');

  // For group bookings, only show buttons on the first voucher (group buttons)
  // Other vouchers in the group should not have individual buttons
  // Vendors on actionable tabs can see buttons even if expired (to complete/close/respond to bookings)
  const shouldShowButtons = (!isExpired || isVendorOnActionableTab) && !isNonActive && (!isInGroup || (isInGroup && isFirstInGroup));

  // Per-voucher actions like Extend should be available on every voucher in a group, not just the first
  const isVendorCheck = window.account?.isVendor || false;
  const shouldShowPerVoucherExtend = isInGroup && !isFirstInGroup && !isExpired && !isNonActive && !isVendorCheck && card.is_redeemed && window.account?.enableVoucherExtension && window.account?.extensionProduct?.exists;

    return `
      <div class="voucher-card animatedContent ${showCheckbox ? 'account-voucher-item' : ''}" data-voucher-id="${card.shopify_gift_card_id}">
        ${showCheckbox ? `
          <label class="checkbox-wrapper">
            <input type="checkbox" class="booking-card-checkbox" data-voucher-id="${card.shopify_gift_card_id}" id="checkbox-${this.sanitizeId(card.shopify_gift_card_id)}" style="display: none;">
            <div class="custom-checkbox">
              <div class="checkbox-empty">${this.getCheckboxIcon('checkbox-empty')}</div>
              <div class="checkbox-solid">${this.getCheckboxIcon('checkbox-solid')}</div>
            </div>
          </label>
        ` : ''}
        <div class="voucher-card__image">
          ${card.productImageUrl ? `<img src="${card.productImageUrl}" alt="${card.productTitle || 'Gift Card'}" loading="lazy">` : ''}
        </div>
        <div class="voucher-card__content">
          <h4 class="voucher-card__title u-s2">${card.productTitle || 'Gift Card'}</h4>
          ${this.getSeasonalPauseBadgeHTML(card)}
          <div class="voucher-card__details">
            ${this.getVoucherDetailsHTML(card, isRedeemed, voucherType, isInGroup, isFirstInGroup)}
          </div>
        </div>
        ${(isFirstInGroup && groupButtonsHTML) || shouldShowButtons || shouldShowPerVoucherExtend ? `
        <voucher-action-handler class="voucher-card__actions">
          ${isFirstInGroup && groupButtonsHTML ? groupButtonsHTML : ''}
          ${shouldShowButtons ? this.getVoucherButtons(card, isInGroup, group, isFirstInGroup, hideAboutExperience) : ''}
          ${shouldShowPerVoucherExtend ? `
            <button class="Button Button--Tertiary" data-action="voucher-action" data-method="init-extend" data-voucher-id="${card.shopify_gift_card_id}">
              <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.extend_experience', 'Extend Experience')}</span>
            </button>
          ` : ''}
          ${shouldShowButtons && !(isFirstInGroup && groupButtonsHTML) ? this.getAutomaticBookingLabelHTML(card) : ''}
        </voucher-action-handler>
        ` : ''}
      </div>
    `;
  }

  getSeasonPauseActive(card) {
    return card.seasonPauseActive || false;
  }

  getSeasonalityInfo(card) {
    return card.seasonalityInfo || null;
  }

  getSeasonPauseStartDate(card) {
    return card.seasonPauseStartDate || null;
  }

  getSeasonPauseEndDate(card) {
    return card.seasonPauseEndDate || null;
  }

  buildSeasonalityText(card) {
    const info = this.getSeasonalityInfo(card);
    let text = (info && info.trim() !== '')
      ? info
      : TranslationUtils.get(
          'customer.account.vouchers.seasonality_info_fallback',
          'This experience is on seasonal break from {start_date} to {end_date}. Booking will be available again after the break ends.'
        );
    const startDate = this.getSeasonPauseStartDate(card);
    const endDate = this.getSeasonPauseEndDate(card);
    text = text.replace('{start_date}', startDate || '-');
    text = text.replace('{end_date}', endDate || '-');
    return text;
  }

  ensureSeasonalityPopup() {
    if (document.getElementById('seasonality-info-popup')) return;
    const title = TranslationUtils.get('customer.account.vouchers.seasonality_info_title', 'Seasonal information');
    const closeLabel = TranslationUtils.get('customer.account.vouchers.close', 'Close');

    const popup = document.createElement('div');
    popup.id = 'seasonality-info-popup';
    popup.className = 'Popup popup-footer-attached account-popup popup-width seasonality-info-popup';
    popup.setAttribute('data-overlay', 'exchangeOverlay');
    popup.setAttribute('aria-hidden', 'true');
    popup.setAttribute('aria-expanded', 'false');
    popup.style.cssText = 'display:none; --popup-header: 0rem; --popup-footer: 0rem;';
    popup.innerHTML = `
      <div class="popup-content">
        <button type="button" class="close-button not_mobile not_pocket" data-action="toggle-popup" aria-label="${closeLabel}" aria-controls="seasonality-info-popup">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="popup-content__inner" data-scrollable>
          <h3 class="u-s1 seasonality-info-popup__title">${title}</h3>
          <div class="u-p3 seasonality-info-popup__body" id="seasonality-info-popup-body"></div>
        </div>
      </div>
      <div class="popup-footer">
        <button type="button" class="Button Button--Secondary" data-action="toggle-popup" aria-controls="seasonality-info-popup" aria-label="${closeLabel}">
          <span class="ButtonText u-pb1">${closeLabel}</span>
        </button>
      </div>
    `;
    document.body.appendChild(popup);

    document.body.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-seasonality-info]');
      if (!trigger) return;
      const info = trigger.getAttribute('data-seasonality-info');
      const body = document.getElementById('seasonality-info-popup-body');
      if (body && info) body.textContent = info;
    }, true);
  }

  getSeasonalPauseBadgeHTML(card) {
    const isBooked = card.is_booked || this.isVoucherInProgress(card);
    const isCanceled = this.isVendorCancelledVoucher(card);
    if (isBooked || isCanceled) return '';
    if (!this.getSeasonPauseActive(card)) return '';

    const statusLabel = TranslationUtils.get('customer.account.vouchers.status', 'Status');
    const pauseLabel = TranslationUtils.get('customer.account.vouchers.seasonal_pause', 'Paused');
    const popupText = this.buildSeasonalityText(card);
    const safeText = popupText.replace(/"/g, '&quot;');

    return `
      <div class="voucher-card__detail voucher-card__seasonal-pause">
        <span class="grey-primary-text u-p3">${statusLabel}</span>
        <span class="voucher-card__seasonal-pause-value">
          <span class="u-s3 voucher-card__seasonal-pause-label">${pauseLabel}</span>
          <button type="button" class="voucher-card__seasonal-pause-info" data-action="toggle-popup" aria-controls="seasonality-info-popup" data-seasonality-info="${safeText}">
            <svg width="16" height="16" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M9.33333 9.33333L9.36978 9.31556C9.48376 9.25861 9.61167 9.23553 9.73837 9.24903C9.86507 9.26253 9.98524 9.31206 10.0847 9.39174C10.1841 9.47143 10.2586 9.57794 10.2993 9.69866C10.3401 9.81937 10.3454 9.94924 10.3147 10.0729L9.68533 12.5938C9.65435 12.7175 9.6595 12.8475 9.70017 12.9683C9.74084 13.0892 9.81532 13.1959 9.91479 13.2757C10.0143 13.3555 10.1345 13.4051 10.2613 13.4186C10.3881 13.4322 10.5162 13.409 10.6302 13.352L10.6667 13.3333M18 10C18 11.0506 17.7931 12.0909 17.391 13.0615C16.989 14.0321 16.3997 14.914 15.6569 15.6569C14.914 16.3997 14.0321 16.989 13.0615 17.391C12.0909 17.7931 11.0506 18 10 18C8.94943 18 7.90914 17.7931 6.93853 17.391C5.96793 16.989 5.08601 16.3997 4.34315 15.6569C3.60028 14.914 3.011 14.0321 2.60896 13.0615C2.20693 12.0909 2 11.0506 2 10C2 7.87827 2.84285 5.84344 4.34315 4.34315C5.84344 2.84285 7.87827 2 10 2C12.1217 2 14.1566 2.84285 15.6569 4.34315C17.1571 5.84344 18 7.87827 18 10ZM10 6.66667H10.0071V6.67378H10V6.66667Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
            </svg>
          </button>
        </span>
      </div>
    `;
  }

  getVendorBookingRequestedAt(card) {
    return card?.requested_at
      || card?.booking_data?.requested_at
      || null;
  }

  isBookingConfirmed(card) {
    if (this.isVoucherInProgress(card)) return false;

    const status = this.getBookingStatusKey(card);
    return !!(card?.is_booked || ['BOOKED', 'COMPLETED', 'COMPLETED_CARRIED'].includes(status));
  }

  getBookingConfirmedAt(card) {
    const candidates = [
      card?.confirmed_at,
      card?.booking_data?.updated_at,
      card?.booking_data?.confirmed_at,
      card?.updated_at
    ];

    for (const value of candidates) {
      if (value == null) continue;
      if (String(value).trim() === '') continue;
      return value;
    }

    return null;
  }

  getBookingExperienceDate(card) {
    const displayData = card?.booking_data?.display_data;
    const dateStr = card?.booking_date
      || card?.booking_data?.date
      || displayData?.date
      || displayData?.booking_date
      || (displayData?.date_time?.includes('T') ? displayData.date_time.split('T')[0] : displayData?.date_time);

    return dateStr ? this.formatDate(dateStr) : null;
  }

  getBookingExperienceTime(card) {
    const displayData = card?.booking_data?.display_data;
    const timeStr = card?.booking_time
      || card?.booking_data?.time
      || displayData?.time
      || (displayData?.date_time?.includes('T') ? displayData.date_time : null);

    return timeStr ? this.formatBookingTime(timeStr) : null;
  }

  formatBookingTime(timeStr) {
    if (!timeStr) return '';

    const str = String(timeStr).trim();
    const timeMatch = str.match(/^(\d{1,2}):(\d{2})(?::\d{2})?/);
    if (timeMatch) {
      return `${timeMatch[1].padStart(2, '0')}:${timeMatch[2]}h`;
    }

    // Read the wall-clock time straight off the string. Parsing it into a Date would
    // re-interpret it in the viewer's timezone and shift the booking's hour.
    const isoMatch = str.match(/T(\d{2}):(\d{2})/);
    if (isoMatch) {
      return `${isoMatch[1]}:${isoMatch[2]}h`;
    }

    return str.endsWith('h') ? str : `${str}h`;
  }

  getBookingRequestMeta(card) {
    if (this.isBookingConfirmed(card)) return null;

    const isInProgress = this.isVoucherInProgress(card);
    const showBookingMeta = isInProgress || card.is_booked || !!card.booking_data;

    if (!showBookingMeta) return null;

    const requestedAt = this.getVendorBookingRequestedAt(card)
      || card?.booking_data?.created_at
      || null;

    return {
      label: TranslationUtils.get('customer.account.vouchers.booking_requested', 'Booking requested'),
      date: requestedAt ? this.formatDate(requestedAt) : '—'
    };
  }

  getVoucherLocationSource(card) {
    const candidates = [
      card?.location,
      card?.booking_data?.location,
      card?.booking_data?.display_data?.location,
      card?.product_location,
      card?.productLocation
    ];

    for (const candidate of candidates) {
      if (!candidate) continue;

      if (typeof candidate === 'object') {
        return {
          name: candidate.name || candidate.label || '',
          untranslated: candidate.untranslated || candidate.value || candidate.key || ''
        };
      }

      return {
        name: String(candidate),
        untranslated: card?.location_untranslated
          || card?.booking_data?.location_untranslated
          || card?.booking_data?.display_data?.location_untranslated
          || ''
      };
    }

    return { name: '', untranslated: '' };
  }

  isAllLocationUntranslated(value) {
    if (value == null || value === '') return false;
    const normalized = String(value).trim().toLowerCase();
    // Mirror connected-products-picker: skip when untranslated contains 'all'
    return normalized === 'all' || normalized.includes('all');
  }

  isAllLocationDisplayName(locationName) {
    if (!locationName) return false;

    const normalized = String(locationName).trim().toLowerCase();
    if (normalized === 'all') return true;

    const allLocationLabels = [
      TranslationUtils.get('customer.account.orders.order_list.location_filter_picker.all_location', 'All locations'),
      'All locations',
      'Alla orter'
    ].map((label) => label.trim().toLowerCase());

    return allLocationLabels.includes(normalized);
  }

  getVoucherLocationDisplay(card) {
    const { name, untranslated } = this.getVoucherLocationSource(card);
    const locationName = String(name || '').trim();

    if (this.isAllLocationUntranslated(untranslated)) return null;
    if (!locationName) return null;
    if (this.isAllLocationDisplayName(locationName)) return null;

    return locationName;
  }

  getVoucherDetailsHTML(card, isRedeemed, voucherType, isInGroup = false, isFirstInGroup = false) {
    const isExpired = this.isExpired(card);
    const isInProgress = this.isVoucherInProgress(card);
    const isVendor = window.account?.isVendor || false;
    const bookingRequestedAt = isVendor ? this.getVendorBookingRequestedAt(card) : null;
    const voucherLocation = this.getVoucherLocationDisplay(card);

    const details = [
      this.getDetailHTML('type', voucherType),
      // Hide variant for cinema bookings
      !card.cinemaBooking ? this.getDetailHTML('variant_name', VoucherUtils.formatVariantTitle(card.productVariantTitle)) : '',
      voucherLocation ? this.getDetailHTML('location', voucherLocation) : '',
      card.shopify_gift_card_id ? this.getDetailHTML('purchase_number', this.formatGiftCardNumber(card.shopify_gift_card_id)) : '',
      card.last_characters ? this.getDetailHTML('code', `****${card.last_characters}`) : '',
      bookingRequestedAt ? this.getDetailHTML('booking_requested', this.formatDate(bookingRequestedAt)) : '',
      // Cinema code is never displayed in voucher details - only shown in popup
      card.expires_at ? this.getExpirationDetailHTML(card) : '',
      // Add extended_protection info if enabled
      card.extended_protection === true ? this.getDetailHTML('extended_protection', TranslationUtils.get('customer.account.vouchers.extended_protection_active', 'Active')) : ''
    ].filter(Boolean);

    // Add display_data details for booked or in-progress vouchers
    // For vendor view, show display_data whenever available to provide complete booking information
    if (((card.is_booked || isInProgress) || isVendor) && card.booking_data?.display_data) {
      const displayDataDetails = this.getDisplayDataHTML(card.booking_data.display_data);
      details.push(...displayDataDetails);
    }

    // Add customer email and phone for vendor view
    // Show customer information whenever available, regardless of specific booking status
    if (isVendor && card.booking_data?.customer) {
      const customer = card.booking_data.customer;

      if (customer.email) {
        const emailLabel = TranslationUtils.get('customer.account.vouchers.customer_email', 'Customer Email');
        details.push(this.getDetailHTML('customer_email', customer.email, emailLabel));
      }

      if (customer.phone) {
        const phoneLabel = TranslationUtils.get('customer.account.vouchers.customer_phone', 'Customer Phone');
        details.push(this.getDetailHTML('customer_phone', customer.phone, phoneLabel));
      }
    }

    // Show participant information whenever available, for customers and vendors alike.
    const participantNames = this.getBookingParticipantNames(card.booking_data?.participant);
    if (participantNames.length > 0) {
      const participantLabel = TranslationUtils.get('customer.account.vouchers.participant_names', 'Participants');
      details.push(this.getDetailHTML('participant_names', participantNames.join(', '), participantLabel));
    }

    // Legacy cards on booking history only. The redesigned card renders this in extras.
    if (this.state.currentFilter === 'completed') {
      const invoiceNumber = this.formatInvoiceNumber(card.invoice_name);
      if (invoiceNumber) {
        const invoiceLabel = TranslationUtils.get('customer.account.vouchers.invoice_number', 'Invoice number');
        details.push(this.getDetailHTML('invoice_number', this.escapeHtmlAttribute(invoiceNumber), invoiceLabel));
      }
    }
    if (card.invoice_date) {
      const formattedInvoiceDate = this.formatDate(card.invoice_date);
      details.push(this.getDetailHTML('invoice_date', formattedInvoiceDate));
    }

    const isCancellationStatus = this.isCancellationStatus(card);

    // Cancellation history is display-only; the reusable voucher intentionally remains unbooked.
    if ((card.is_booked || isInProgress || isCancellationStatus) && card.booking_status) {
      const translatedStatus = VoucherUtils.getBookingStatusText(card.booking_status);
      const statusClass = VoucherUtils.getBookingStatusClass(card.booking_status);
      details.push(this.getDetailHTMLWithClass('status', translatedStatus, null, statusClass));
    }

    // Add status as the last item (skip when paused — pause badge replaces status)
    if ((isRedeemed || isExpired) && !card.is_booked && !isInProgress && !isCancellationStatus && !this.getSeasonPauseActive(card)) {
      details.push(this.getStatusDetailHTML(card));
    }

    // Show cancelled_at timestamp for cancelled bookings (below status)
    if (card.canceled_at) {
      const cancelledLabel = TranslationUtils.get('customer.account.vouchers.cancelled_at', 'Cancelled');
      const formattedCancelledAt = this.formatDate(card.canceled_at);
      details.push(this.getDetailHTML('cancelled_at', formattedCancelledAt, cancelledLabel));
    }

    if (isVendor && this.isVendorCancelledVoucher(card)) {
      details.push(this.getCancellationHandledHTML(card));
    }

    return details.join('');
  }

  isVendorCancelledVoucher(card) {
    return this.isCancellationStatus(card) || Boolean(card.canceled_at);
  }

  isCancellationStatus(card) {
    const status = card.booking_status?.toUpperCase();
    return Boolean(status && [
      'CANCELED',
      'CANCELED_CUSTOMER',
      'CANCELED_VENDOR',
      'CANCELED_REQUEST_CUSTOMER',
      'CANCELED_REQUEST_VENDOR',
      'COMPLETED_CANCELED'
    ].includes(status));
  }

  getCancellationHandledHTML(card) {
    const cancellationEntryId = Number(card.cancellation_entry_id);
    if (!Number.isInteger(cancellationEntryId) || cancellationEntryId <= 0) {
      return '';
    }

    const handledRaw = card.cancellation_handled ?? card.cancellationHandled;
    const isHandled = handledRaw === true || handledRaw === 'true';
    const label = TranslationUtils.get('vendor.bookings.cancellation_handled', 'Cancellation handled');
    const yesText = TranslationUtils.get('vendor.bookings.cancellation_handled_yes', 'Yes');
    const noText = TranslationUtils.get('vendor.bookings.cancellation_handled_no', 'No');
    const aria = TranslationUtils.get('vendor.bookings.cancellation_handled_aria', 'Mark cancellation as handled');
    const safeAria = String(aria).replace(/"/g, '&quot;');
    const sanitizedId = this.sanitizeId(`cancellation-${cancellationEntryId}`);
    return `
      <div class="voucher-card__detail voucher-card__detail--cancellation-handled">
        <span class="voucher-card__detail-label grey-primary-text u-p3">${label}</span>
        <label class="checkbox-wrapper cancellation-handled-checkbox-wrapper">
          <input type="checkbox" class="cancellation-handled-checkbox" data-cancellation-entry-id="${cancellationEntryId}" id="cancellation-handled-${sanitizedId}" style="display: none;" ${isHandled ? 'checked' : ''} aria-label="${safeAria}" />
          <div class="custom-checkbox">
            <div class="checkbox-empty">${this.getCheckboxIcon('checkbox-empty')}</div>
            <div class="checkbox-solid">${this.getCheckboxIcon('checkbox-mark')}</div>
          </div>
          <span class="u-s3 cancellation-handled-value cancellation-handled-value--yes">${yesText}</span>
          <span class="u-s3 cancellation-handled-value cancellation-handled-value--no">${noText}</span>
        </label>
      </div>
    `;
  }

  getDetailHTML(key, value, customLabel = null) {
    const label = customLabel || TranslationUtils.get(`customer.account.vouchers.${key}`, key);
    return `
      <div class="voucher-card__detail">
        <span class="grey-primary-text u-p3">${label}</span>
        <span class="u-s3">${value}</span>
      </div>
    `;
  }

  formatInvoiceNumber(raw) {
    const value = String(raw || '').trim();
    if (!value) return '';

    const named = value.match(/purchase-invoice-(\d+(?:-\d+)?)/i);
    if (named) return named[1];

    const hashed = value.match(/#\s*(\d+(?:-\d+)?)/);
    if (hashed) return hashed[1];

    const trailing = value.match(/(\d+(?:-\d+)?)(?:\.\w+)?$/);
    if (trailing) return trailing[1];

    return value;
  }

  getInvoiceNumbers(vouchers) {
    const numbers = [];
    for (const voucher of vouchers || []) {
      const number = this.formatInvoiceNumber(voucher?.invoice_name || voucher?.invoice_reference);
      if (number && !numbers.includes(number)) numbers.push(number);
    }
    return numbers;
  }

  formatGiftCardNumber(giftCardGid) {
    if (!giftCardGid) return '';
    const parts = giftCardGid.split('/');
    const idPart = parts[parts.length - 1] || giftCardGid;
    return `#${idPart}`;
  }

  getDetailHTMLWithClass(key, value, customLabel = null, valueClass = '') {
    const label = customLabel || TranslationUtils.get(`customer.account.vouchers.${key}`, key);
    return `
      <div class="voucher-card__detail">
        <span class="grey-primary-text u-p3">${label}</span>
        <span class="u-s3 ${valueClass}">${value}</span>
      </div>
    `;
  }

  getExpirationDetailHTML(card) {
    // Removed expiring soon tag as per request

    return `
      <div class="voucher-card__detail">
        <span class="grey-primary-text u-p3">${TranslationUtils.get('customer.account.vouchers.expires', 'Expires')}</span>
        <span class="u-s3">${this.formatDate(card.expires_at)}</span>
      </div>
    `;
  }

  getStatusDetailHTML(card) {
    const statusText = this.getStatusText(card);
    const statusClass = this.getStatusClass(card);

    return `
      <div class="voucher-card__detail">
        <span class="grey-primary-text u-p3">${TranslationUtils.get('customer.account.vouchers.status', 'Status')}</span>
        <span class="u-s3 ${statusClass}">${statusText}</span>
      </div>
    `;
  }

  getDisplayDataHTML(displayData) {
    const details = [];

    // Handle display_data as object with key-value pairs
    if (displayData && typeof displayData === 'object') {
      for (const [key, value] of Object.entries(displayData)) {
        // Skip status field as it's handled separately with proper translation (case-insensitive)
        const keyLower = key.toLowerCase();
        if (keyLower === 'status' || keyLower === 'booking_status') {
          continue;
        }

        // Skip vendor name fields for vendor view
        if (window.account?.isVendor && (keyLower === 'vendor_name' || keyLower === 'vendor' || keyLower === 'supplier' || keyLower === 'supplier_name')) {
          continue;
        }

        // Location is rendered once from card.location / display_data above
        if (keyLower === 'location') {
          continue;
        }

        if (keyLower === 'participant_count') {
          continue;
        }

        if (value && value !== '') {
          const titleCaseFallback = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
          const label = TranslationUtils.get(`customer.account.vouchers.${keyLower}`, titleCaseFallback);

          // Check if this is a date field or if the value looks like a date
          let displayValue = value;
          const isDateFieldByName = this.isDateField(key);
          const isDateValue = this.isDateLikeValue(value);

          if (isDateFieldByName || isDateValue) {
            // Format the date value
            displayValue = this.formatDate(value);

            // Add "date passed" indicator if the date has passed (only for actual date fields, not times)
            if (isDateFieldByName && this.hasDatePassed(value)) {
              const datePassedText = TranslationUtils.get('customer.account.vouchers.date_passed', '(date passed)');
              displayValue = `${displayValue} <span class="u-p4 grey-primary-text">${datePassedText}</span>`;
            }
          }

          details.push(this.getDetailHTML(keyLower, displayValue, label));
        }
      }
    }

    return details;
  }

  // Helper method to check if a field is a date field
  isDateField(key) {
    const keyLower = key.toLowerCase();
    // Check if field name contains date-related keywords or matches common date field names
    return keyLower.includes('date') ||
      keyLower.includes('time') ||
      keyLower === 'datum' ||
      keyLower === 'tid';
  }

  // Helper method to check if a value looks like a date string
  isDateLikeValue(value) {
    if (typeof value !== 'string') return false;

    // Check for common date patterns:
    // YYYY-MM-DD, YYYY/MM/DD, DD-MM-YYYY, DD/MM/YYYY, ISO 8601 format
    const datePatterns = [
      /^\d{4}[-\/]\d{2}[-\/]\d{2}/, // YYYY-MM-DD or YYYY/MM/DD
      /^\d{2}[-\/]\d{2}[-\/]\d{4}/, // DD-MM-YYYY or DD/MM/YYYY
      /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO 8601 format (YYYY-MM-DDTHH:mm:ss)
      /T\d{2}:\d{2}:\d{2}.*Z$/ // ISO format with timezone (ends with Z)
    ];

    return datePatterns.some(pattern => pattern.test(value));
  }

  // Helper method to check if a date has passed
  hasDatePassed(dateString) {
    if (!dateString) return false;

    try {
      const date = new Date(dateString);
      const today = new Date();

      // Set time to start of day for accurate day comparison
      date.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Return true if the date is in the past
      return date.getTime() < today.getTime();
    } catch (error) {
      console.error('Error checking if date has passed:', error);
      return false;
    }
  }

  getNonRedeemedVoucherButtons(card, hideAboutExperience = false) {
    const isSupplier = window.account?.isVendor || false;


    if (isSupplier) {
      // No buttons for suppliers - they cannot extend vouchers
      return '';
    }

    const buttons = [
      `<button class="Button Button--PrimaryOnDark" data-action="voucher-action" data-method="init-redeem" data-voucher-id="${card.shopify_gift_card_id}">
         <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.redeem', 'Redeem')}</span>
       </button>`
    ];

    if (!card.is_gift) {
      buttons.push(`
        <button class="Button Button--Secondary" data-action="voucher-action" data-method="init-gift" data-voucher-id="${card.shopify_gift_card_id}">
          <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.gift_voucher', 'Gift This Voucher')}</span>
        </button>
      `);
    }

    // Show extension button always (365 days) if not expired (customers only)


    if (!this.isExpired(card) && window.account?.enableVoucherExtension && window.account?.extensionProduct?.exists) {
      buttons.push(`
        <button class="Button Button--Tertiary" data-action="voucher-action" data-method="init-extend" data-voucher-id="${card.shopify_gift_card_id}">
          <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.extend_experience', 'Extend Experience')}</span>
        </button>
      `);
    }

    return buttons.join('');
  }

  // Helper function to check if cancellation is allowed based on booking date and cancellation days
  canCancelBooking(card) {
    const result = this._calculateCancellationEligibility(card);

    // Return format for UI decisions (simple format)
    if (result.canCancel) {
      return { canCancel: true, daysDiff: result.daysDiff, cancellationDays: result.cancellationDays, reason: result.reason };
    } else {
      return { canCancel: false, reason: result.reason, error: result.error };
    }
  }

  // Helper function to check if booking can be completed (on or after booking date)
  canCompleteBooking(card) {
    const isInProgress = this.isVoucherInProgress(card);

    // Must be booked or in progress to have completion option
    if ((!card.is_booked && !isInProgress) || !card.booking_data) {
      return { canComplete: false, reason: 'not_booked' };
    }

    const bookingData = card.booking_data;

    // Get booking date. Must match the fields the card actually renders from
    // (getBookingExperienceDate) — a narrower lookup reports no_date for bookings
    // whose date is present, which silently hides the complete action.
    const bookingDateStr = card.booking_date
      || bookingData.date
      || bookingData.display_data?.booking_date
      || bookingData.display_data?.date
      || bookingData.display_data?.date_time;
    if (!bookingDateStr) {
      return { canComplete: false, reason: 'no_date' };
    }

    try {
      // Parse booking date
      const bookingDate = new Date(bookingDateStr);
      const today = new Date();

      // Set time to start of day for accurate day comparison
      bookingDate.setHours(0, 0, 0, 0);
      today.setHours(0, 0, 0, 0);

      // Calculate days between today and booking date
      const timeDiff = bookingDate.getTime() - today.getTime();
      const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24));

      // Can complete if booking date is today or in the past
      const canComplete = daysDiff <= 0;
      return { canComplete, daysDiff };
    } catch (error) {
      console.error('Error calculating completion eligibility:', error);
      return { canComplete: false, reason: 'date_error', error };
    }
  }

  // Helper function to get cancellation info for user messaging
  getCancellationInfo(card) {
    const result = this._calculateCancellationEligibility(card);

    // Return format for user messaging (enhanced with messages)
    if (result.canCancel) {
      return { canCancel: true, daysDiff: result.daysDiff, cancellationDays: result.cancellationDays, reason: result.reason };
    } else {
      // Add user-friendly messages for customer errors
      if (result.reason === 'too_late') {
        return {
          canCancel: false,
          reason: 'too_late',
          daysDiff: result.daysDiff,
          cancellationDays: result.cancellationDays,
          message: TranslationUtils.get(
            'customer.account.vouchers.notifications.cancellation_too_late',
            'You can only cancel bookings at least {{ days }} days in advance.'
          ).replace('{{ days }}', result.cancellationDays)
        };
      } else if (result.reason === 'vendor_cancellation_disabled') {
        return {
          canCancel: false,
          reason: 'vendor_cancellation_disabled',
          message: TranslationUtils.get(
            'customer.account.vouchers.notifications.cancellation_contact_vendor',
            'To change your date, please contact the vendor via chat.'
          )
        };
      } else {
        return { canCancel: false, reason: result.reason, error: result.error };
      }
    }
  }

  // Core cancellation eligibility calculation (DRY implementation)
  _calculateCancellationEligibility(card) {
    const isInProgress = this.isVoucherInProgress(card);
    const isSupplier = window.account?.isVendor || false;

    // Some vendors' terms don't cover date changes; those customers must
    // contact the vendor directly instead of self-cancelling. Applies to both
    // an in-progress request and a confirmed booking, but not to the vendor's
    // own portal (they can always manage their bookings).
    if (!isSupplier) {
      const vendorEmail = (card.booking_data?.vendor?.email || '').toLowerCase();
      if (VENDORS_WITHOUT_CUSTOMER_CANCELLATION.has(vendorEmail)) {
        return { canCancel: false, reason: 'vendor_cancellation_disabled', vendorEmail };
      }
    }

    // IN_PROGRESS bookings (booking requests) can always be cancelled by both customers and suppliers
    // Check FIRST before other validations since these are not yet confirmed
    if (isInProgress) {
      return { canCancel: true, reason: 'in_progress_request' };
    }

    // Must be booked to have cancellation option
    if (!card.is_booked || !card.booking_data) {
      return { canCancel: false, reason: 'not_booked' };
    }

    const bookingData = card.booking_data;

    // Suppliers can cancel bookings without day restrictions
    if (isSupplier) {
      return { canCancel: true, reason: 'supplier_override', isSupplier: true };
    }

    // Customer cancellation logic with day restrictions
    // Check extended_protection flag to determine required cancellation days
    // Check multiple locations for compatibility
    const extendedProtection = bookingData.booking_data?.extended_protection ||
      bookingData.extended_protection ||
      card.extended_protection ||
      false;

    // If custom cancellation_days is specified, use that; otherwise use default based on extended_protection
    // extended_protection true = 2 days, false = 10 days
    const defaultDays = extendedProtection ? 2 : 10;
    const cancellationDays = bookingData.cancelation_days || bookingData.cancellation_days || defaultDays;



    // Get booking date. Must match the fields the card actually renders from
    // (getBookingExperienceDate) — display_data.date_time can hold only a time
    // ("13:00:00"), which used to win here and hide Cancel as too_late.
    const bookingDateStr = card.booking_date
      || bookingData.date
      || bookingData.display_data?.date
      || bookingData.display_data?.booking_date
      || bookingData.display_data?.date_time;
    if (!bookingDateStr) {
      return { canCancel: false, reason: 'no_date' };
    }

    try {
      // Parse booking date and normalize to start of day
      // Use UTC date components to avoid timezone issues
      const bookingDate = new Date(bookingDateStr);
      if (Number.isNaN(bookingDate.getTime())) {
        return { canCancel: false, reason: 'no_date' };
      }
      const today = new Date();

      // Create dates at midnight UTC for consistent comparison with backend
      const bookingDateUTC = Date.UTC(bookingDate.getFullYear(), bookingDate.getMonth(), bookingDate.getDate());
      const todayUTC = Date.UTC(today.getFullYear(), today.getMonth(), today.getDate());

      // Calculate days between today and booking date
      const timeDiff = bookingDateUTC - todayUTC;
      const daysDiff = Math.floor(timeDiff / (1000 * 3600 * 24));

      // Can cancel if booking date is MORE than cancellation_days away (strictly greater)
      // This matches backend behavior: "must be at least X days away" means > X, not >= X
      const canCancel = daysDiff > cancellationDays;



      if (canCancel) {
        return { canCancel: true, daysDiff, cancellationDays };
      } else {
        return { canCancel: false, reason: 'too_late', daysDiff, cancellationDays };
      }
    } catch (error) {
      console.error('Error calculating cancellation eligibility:', error);
      return { canCancel: false, reason: 'date_error', error };
    }
  }


  getVoucherButtons(card, isInGroup = false, group = null, isFirstInGroup = false, hideAboutExperience = false) {
    const isSupplier = window.account?.isVendor || false;

    if (isSupplier) {
      return this.vendorActions ? this.vendorActions.getVendorVoucherButtons(card, isInGroup, group, isFirstInGroup) : '';
    } else {
      const isRedeemed = card.is_redeemed;
      return isRedeemed ? this.getRedeemedVoucherButtons(card, isInGroup, group, hideAboutExperience) : this.getNonRedeemedVoucherButtons(card, hideAboutExperience);
    }
  }


  getRedeemedVoucherButtons(card, isInGroup = false, group = null, hideAboutExperience = false) {
    const buttonConfigs = [];
    let vendorCancellationNote = '';

    // Special handling for giftbox vouchers that haven't selected a product yet
    if (card.is_giftbox && card.giftbox_selected != true) {
      buttonConfigs.push(['choose_product', 'actions.choose_product', 'PrimaryOnDark', 'voucher-action', 'init-redeem']);

      const buttons = buttonConfigs.map(([key, translationKey, buttonClass, action, handle]) => {
        const fullTranslationPath = `customer.account.vouchers.${translationKey}`;
        const fallbackText = 'Choose Product';
        const buttonText = TranslationUtils.get(fullTranslationPath, fallbackText);

        return `<button class="Button Button--${buttonClass}" data-action="${action}" data-method="${handle}" data-voucher-id="${card.shopify_gift_card_id}">
           <span class="ButtonText u-pb1">${buttonText}</span>
         </button>`;
      });

      return buttons.join('');
    }

    // Special handling for cinema bookings
    if (card.cinemaBooking) {
      // "Show Codes" only once codes have been issued. Until then "Book Experience"
      // stays, since claiming is how the codes are obtained.
      if (card.is_booked && card.cinemaCodePresent) {
        buttonConfigs.push(['show_cinema_code', 'actions.show_cinema_code', 'PrimaryOnDark', 'show-cinema-code']);
      } else if (this.isValidCinemaProduct(card)) {
        buttonConfigs.push(['book_experience', 'actions.book_experience', 'PrimaryOnDark', 'claim-cinema-code']);
      }

      // Always show "About This Experience" button (below primary button)
      if (!hideAboutExperience) {
        buttonConfigs.push(['about_experience', 'about_experience', 'Secondary', 'voucher-action', card.productHandle]);
      }

      const buttons = buttonConfigs.map(([key, translationKey, buttonClass, action, handle]) => {
        const fullTranslationPath = `customer.account.vouchers.${translationKey}`;
        const fallbackText = {
          'about_experience': 'About This Experience',
          'actions.book_experience': 'Book Experience',
          'actions.show_cinema_code': 'Show Codes'
        }[translationKey] || 'Action';
        const buttonText = TranslationUtils.get(fullTranslationPath, fallbackText);

        // For about-experience, use voucher-action with method and product-handle
        if (action === 'voucher-action' && handle && !handle.startsWith('init-')) {
          return `<button class="Button Button--${buttonClass}" data-action="open-detailed" data-url="/products/${handle}" data-voucher-id="${card.shopify_gift_card_id}">
             <span class="ButtonText u-pb1">${buttonText}</span>
           </button>`;
        }

        // For claim-cinema-code action, use special handler
        if (action === 'claim-cinema-code') {
          return `<button class="Button Button--${buttonClass}" data-action="claim-cinema-code" data-booking-token="${card.bookingToken}" data-voucher-id="${card.shopify_gift_card_id}">
             <span class="ButtonText u-pb1">${buttonText}</span>
           </button>`;
        }

        // For show-cinema-code action, open popup with existing codes
        if (action === 'show-cinema-code') {
          return `<button class="Button Button--${buttonClass}" data-action="show-cinema-code" data-cinema-codes='${JSON.stringify(card.cinemaCodes)}' data-voucher-id="${card.shopify_gift_card_id}">
             <span class="ButtonText u-pb1">${buttonText}</span>
           </button>`;
        }

        return `<button class="Button Button--${buttonClass}" data-action="${action}" data-voucher-id="${card.shopify_gift_card_id}">
           <span class="ButtonText u-pb1">${buttonText}</span>
         </button>`;
      });

      return buttons.join('');
    }

    // Check if booking is in progress
    const isInProgress = this.isVoucherInProgress(card);
    // For vouchers in groups with booking in process, also check group booking_status
    const groupBookingStatus = group?.booking_status;
    const isGroupBookingInProcess = isInGroup && groupBookingStatus !== null && groupBookingStatus !== undefined;

    const isVendor = window.account?.isVendor || false;
    const isBookingRequestsTab = this.state.currentFilter === 'booking-requests';

    // Add book or cancel booking button based on booking status
    if (card.is_booked || isInProgress) {
      const isPastBookingsTab = this.state.currentFilter === 'past-bookings';
      const isUpcomingBookingsTab = this.state.currentFilter === 'upcoming-bookings';

      if (isInProgress) {
        // For IN_PROGRESS status: Use different text for vendors on booking-requests tab
        // Only show chat and cancel buttons on individual vouchers if NOT in a group (group shows them on header)
        if (!isInGroup) {
          const shouldShowOpenConversation = !(isVendor && isBookingRequestsTab && this.isInternalBookingFlow(card));
          if (shouldShowOpenConversation) {
            buttonConfigs.push(['chat_with_supplier', 'communication.chat_with_supplier', 'PrimaryOnDark', 'chat-supplier']);
          }
        }

        // Only show "About Experience" button if not a vendor or not on booking-requests tab, and not hidden by group level
        if ((!isVendor || !isBookingRequestsTab) && !hideAboutExperience) {
          buttonConfigs.push(['about_experience', 'about_experience', 'Secondary', 'voucher-action', card.productHandle]);
        }

        // Only show cancel request button if NOT in a group (group shows it on header)
        if (!isInGroup && !isPastBookingsTab) {
          const cancelCheck = this.canCancelBooking(card);
          if (cancelCheck.canCancel) {
            buttonConfigs.push(['cancel_request', 'actions.cancel_request', 'Secondary', 'voucher-action', 'init-cancel-booking']);
          } else if (cancelCheck.reason === 'vendor_cancellation_disabled') {
            // Chat button is already shown above for in-progress requests, so
            // just the note here — no need to duplicate the chat button.
            const contactText = TranslationUtils.get(
              'customer.account.vouchers.notifications.cancellation_contact_vendor',
              'To change your date, please contact the vendor via chat.'
            );
            vendorCancellationNote = `<p class="booking-request-card__pause-note u-p4 grey-primary-text">${contactText}</p>`;
          }
        }
      } else {
        // For confirmed BOOKED status: About Experience (Primary), Cancel Booking (if allowed)
        // Note: Chat button removed as conversation ends when experience is booked
        if (!hideAboutExperience) {
          buttonConfigs.push(['about_experience', 'about_experience', 'PrimaryOnDark', 'voucher-action', card.productHandle]);
        }

        // Show cancel booking button if allowed. A group was booked as one booking,
        // so it is cancelled as one (the grouped card has no separate header for it).
        if ((!isInGroup || group?.group_id) && !isPastBookingsTab) {
          const cancelCheck = this.canCancelBooking(card);
          if (isInGroup ? this.canCancelGroupBooking(group) : cancelCheck.canCancel) {
            buttonConfigs.push(isInGroup
              ? ['cancel_group_booking', 'actions.cancel_booking', 'Secondary', 'cancel-group-booking']
              : ['cancel_booking', 'actions.cancel_booking', 'Secondary', 'voucher-action', 'init-cancel-booking']);
          } else if (cancelCheck.reason === 'vendor_cancellation_disabled') {
            const contactText = TranslationUtils.get(
              'customer.account.vouchers.notifications.cancellation_contact_vendor',
              'To change your date, please contact the vendor via chat.'
            );
            vendorCancellationNote = `
              <p class="booking-request-card__pause-note u-p4 grey-primary-text">
                ${contactText}
                <button type="button" class="vendor-cancellation-note__chat-link" data-action="toggle-customer-support" data-voucher-id="${card.shopify_gift_card_id}">${this.getChatWithSupplierLabel()}</button>
              </p>
            `;
          }
        }

        // Chat with supplier button removed for booked experiences (conversation has ended)
        // buttonConfigs.push(['chat_with_supplier', 'communication.chat_with_supplier', 'Secondary', 'chat-supplier']);
      }
    } else {
      // For non-booked vouchers: Book Experience (Primary), About Experience (Secondary)
      // Only show book experience button for individual vouchers, not grouped ones
      // Hide Book Experience when seasonal pause is active
      if (!isInGroup && !this.getSeasonPauseActive(card)) {
        buttonConfigs.push(['book_experience', 'actions.book_experience', 'PrimaryOnDark', 'book-experience']);
      }
      if (!hideAboutExperience) {
        buttonConfigs.push(['about_experience', 'about_experience', 'Secondary', 'voucher-action', card.productHandle]);
      }
    }

    // Only add exchange button if exchange is enabled, collection is configured, voucher is not booked, and not in progress
    if (!card.is_booked && !isInProgress && window.account?.enableVoucherExchange && window.account?.exchangeCollectionUrl) {
      buttonConfigs.push(['exchange_experience', 'actions.exchange_experience', 'Secondary', 'exchange-experience']);
    }


    const buttons = buttonConfigs.map(([key, translationKey, buttonClass, action, handle]) => {
      // Build the full translation path
      const fullTranslationPath = `customer.account.vouchers.${translationKey}`;

      // Get fallback text based on key for better error handling
      const fallbackText = {
        'about_experience': 'About This Experience',
        'actions.cancel_booking': 'Cancel',
        'actions.cancel_request': 'Cancel Request',
        'communication.chat_with_supplier': 'Chat with supplier',
        'actions.book_experience': 'Book Experience',
        'actions.exchange_experience': 'Exchange Experience',
        'actions.complete_booking': 'Complete Booking'  // Added for supplier
      }[translationKey] || 'Action';

      const buttonText = TranslationUtils.get(fullTranslationPath, fallbackText);

      // For book-experience action, use anchor button with direct booking URL
      if (action === 'book-experience') {
        const bookingUrl = this.buildBookingUrl(card);
        return `<a class="Button Button--${buttonClass}" data-action="book-experience" data-voucher-id="${card.shopify_gift_card_id}" href="${bookingUrl}" role="button">
           <span class="ButtonText u-pb1">${buttonText}</span>
         </a>`;
      }

      // For about-experience, use voucher-action with method and product-handle
      if (action === 'voucher-action' && handle && !handle.startsWith('init-')) {
        return `<button class="Button Button--${buttonClass}" data-action="open-detailed" data-url="/products/${handle}" data-voucher-id="${card.shopify_gift_card_id}">
           <span class="ButtonText u-pb1">${buttonText}</span>
         </button>`;
      }

      // For voucher actions with method (like init-cancel-booking), use data-method
      if (action === 'voucher-action' && handle && handle.startsWith('init-')) {
        return `<button class="Button Button--${buttonClass}" data-action="voucher-action" data-method="${handle}" data-voucher-id="${card.shopify_gift_card_id}">
           <span class="ButtonText u-pb1">${buttonText}</span>
         </button>`;
      }

      if (action === 'cancel-group-booking') {
        return `<button class="Button Button--${buttonClass}" data-action="cancel-group-booking" data-group-id="${group.group_id}">
           <span class="ButtonText u-pb1">${buttonText}</span>
         </button>`;
      }

      // For chat-supplier action, add toggle-customer-support data-action
      if (action === 'chat-supplier') {
        return `<button class="Button Button--${buttonClass}" data-action="toggle-customer-support" data-voucher-id="${card.shopify_gift_card_id}">
           <span class="ButtonText u-pb1">${buttonText}</span>
         </button>`;
      }

      // For other actions, use the original format
      return `<button class="Button Button--${buttonClass}" data-action="${action}"  data-voucher-id="${card.shopify_gift_card_id}">
         <span class="ButtonText u-pb1">${buttonText}</span>
       </button>`;
    });

    // Show extension button always (365 days) if not expired and not a vendor


    if (!isVendor && !this.isExpired(card) && window.account?.enableVoucherExtension && window.account?.extensionProduct?.exists) {
      buttons.push(`
        <button class="Button Button--Tertiary" data-action="voucher-action" data-method="init-extend" data-voucher-id="${card.shopify_gift_card_id}">
          <span class="ButtonText u-pb1">${TranslationUtils.get('customer.account.vouchers.extend_experience', 'Extend Experience')}</span>
        </button>
      `);
    }

    // Show Learn more button for paused experiences (replaces Book Experience)
    if (this.getSeasonPauseActive(card)) {
      this.ensureSeasonalityPopup();
      const learnMoreLabel = TranslationUtils.get('general.learn_more', 'Learn more');
      const popupText = this.buildSeasonalityText(card);
      buttons.unshift(`
        <button class="Button Button--PrimaryOnDark" data-action="toggle-popup" aria-controls="seasonality-info-popup" data-seasonality-info="${popupText.replace(/"/g, '&quot;')}">
          <span class="ButtonText u-pb1">${learnMoreLabel}</span>
        </button>
      `);
    }

    return buttons.join('') + vendorCancellationNote;
  }

  getStatusText(card) {
    const isInProgress = this.isVoucherInProgress(card);

    if (this.isCancellationStatus(card)) {
      return VoucherUtils.getBookingStatusText(card.booking_status);
    } else if (this.isExpired(card)) {
      return TranslationUtils.get('customer.account.vouchers.expired', 'Expired');
    } else if (card.is_redeemed && (card.is_booked || isInProgress)) {
      // Use booking_status as source of truth
      if (card.booking_status) {
        return VoucherUtils.getBookingStatusText(card.booking_status);
      }
      // Fallback to generic 'booked' if no specific status.
      // `vouchers.booked` is the tab label ("Historik") — the status reads from booking.booked.
      return TranslationUtils.get('customer.account.vouchers.booking.booked', 'Booked');
    } else if (card.is_redeemed) {
      // Activated but nothing booked yet. `vouchers.redeemed` is the tab label
      // ("Aktiva värdebevis"), which reads as nonsense on a card status line.
      return TranslationUtils.get('customer.account.vouchers.ready_for_booking', 'Ready for booking');
    } else {
      return TranslationUtils.get('customer.account.vouchers.available', 'Available');
    }
  }

  getStatusClass(card) {
    const isInProgress = this.isVoucherInProgress(card);

    if (this.isCancellationStatus(card)) {
      return VoucherUtils.getBookingStatusClass(card.booking_status);
    } else if (this.isExpired(card)) {
      return 'voucher-status--expired';
    } else if (card.is_redeemed && (card.is_booked || isInProgress)) {
      // Use booking_status as source of truth
      if (card.booking_status) {
        return VoucherUtils.getBookingStatusClass(card.booking_status);
      }
      // Fallback to generic 'booked' if no specific status
      return 'voucher-status--booked';
    } else if (card.is_redeemed) {
      return 'voucher-status--redeemed';
    } else {
      return 'voucher-status--available';
    }
  }

  isExpiringSoon(card) {
    const expiringDays = window.account?.voucherExpiringDays || 30;
    const isExpiring = VoucherUtils.isExpiringSoon(card, expiringDays);

    // Debug logging


    return isExpiring;
  }

  isExpired(card) {
    return VoucherUtils.isExpired(card);
  }

  formatDate(dateString) {
    return VoucherUtils.formatDate(dateString);
  }

  /**
   * Load cinema config from script tags in the page
   */
  loadCinemaConfig() {
    const config = {
      movies_only: null,
      movies_with_snacks: null
    };

    // Load from script tags
    const moviesOnlyScript = document.querySelector('script[data-cinema-config="movies_only"]');
    const moviesSnacksScript = document.querySelector('script[data-cinema-config="movies_with_snacks"]');

    if (moviesOnlyScript) {
      try {
        const data = JSON.parse(moviesOnlyScript.textContent);
        config.movies_only = data.product?.id || null;
      } catch (e) {
        console.error('Failed to parse movies only cinema config:', e);
      }
    }

    if (moviesSnacksScript) {
      try {
        const data = JSON.parse(moviesSnacksScript.textContent);
        config.movies_with_snacks = data.product?.id || null;
      } catch (e) {
        console.error('Failed to parse movies with snacks cinema config:', e);
      }
    }

    return config;
  }

  /**
   * Extract numeric product ID by stripping all non-digit characters
   * @param {string|number} productId - Product ID in any format
   * @returns {string|null} - Numeric product ID or null
   */
  extractNumericProductId(productId) {
    if (!productId) return null;

    // Strip all non-digit characters
    const numericId = String(productId).replace(/\D/g, '');
    return numericId || null;
  }

  /**
   * Validate if a gift card's product ID matches the required cinema product ID
   * @param {Object} card - Gift card object
   * @returns {boolean} - True if product ID matches, false otherwise
   */
  isValidCinemaProduct(card) {
    if (!card.cinemaBooking || !card.cinemaCodeType) {
      return false;
    }

    const cinemaConfig = this.loadCinemaConfig();
    const cardProductId = card.product_id;

    if (!cardProductId) {
      return false;
    }

    // Get required product ID based on cinema type
    let requiredProductId = null;
    if (card.cinemaCodeType === 'movies_only') {
      requiredProductId = cinemaConfig.movies_only;
    } else if (card.cinemaCodeType === 'movies_with_snacks') {
      requiredProductId = cinemaConfig.movies_with_snacks;
    }

    // If no config found, don't show the button (config must exist)
    if (!requiredProductId) {
      return false;
    }

    // Extract numeric parts for comparison
    const cardNumericId = this.extractNumericProductId(cardProductId);
    const requiredNumericId = this.extractNumericProductId(requiredProductId);

    // Compare numeric IDs
    return cardNumericId === requiredNumericId;
  }

  getLoadingHTML() {
    const message = TranslationUtils.get('customer.account.vouchers.loading_message', 'Loading your vouchers...');
    return `
      <div class="vouchers-loading">
        <div class="spinningLoader is-visible">
          <svg width="36" height="37" viewBox="0 0 36 37" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.07706 16.8043C1.71423 20.4881 2.63996 24.1838 4.6965 27.2617C6.75305 30.3395 9.81317 32.6091 13.3555 33.6836C16.8978 34.7582 20.703 34.5712 24.1229 33.1546C27.5429 31.7381 30.3658 29.1795 32.1108 25.9149C33.8557 22.6503 34.4147 18.8817 33.6926 15.2511C32.9704 11.6206 31.0118 8.35272 28.1503 6.0044C25.2889 3.65607 21.7017 2.37256 18 2.37256" stroke="white" stroke-width="4"/>
          </svg>
        </div>
        <p class="u-p2">${message}</p>
      </div>
    `;
  }

  getErrorHTML() {
    const errorTitle = TranslationUtils.get('customer.account.vouchers.error_title', 'Unable to load vouchers');
    const errorMessage = this.state.error || TranslationUtils.get('customer.account.vouchers.error_message', 'Failed to load vouchers');
    return `
      <div class="vouchers-error">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"/>
          <line x1="15" y1="9" x2="9" y2="15"/>
          <line x1="9" y1="9" x2="15" y2="15"/>
        </svg>
        <h4 class="u-p2">${errorTitle}</h4>
        <p class="u-s2">${errorMessage}</p>
        <button class="Button Button--PrimaryOnLight" data-action="retry-load">Try Again</button>
      </div>
    `;
  }

  getEmptyStateHTML() {
    const filterLabels = {
      'non-redeemed': 'available',
      'redeemed': 'redeemed',
      'expired': 'expired'
    };

    const filterLabel = filterLabels[this.state.currentFilter] || '';
    const message = filterLabel
      ? `You don't have any ${filterLabel} vouchers yet.`
      : 'You don\'t have any vouchers yet.';

    const emptyTitle = TranslationUtils.get('customer.account.vouchers.empty_title', 'No vouchers found');
    const emptyMessage = TranslationUtils.get('customer.account.vouchers.empty_message', message);
    const emptySubtitle = TranslationUtils.get('customer.account.vouchers.empty_subtitle', '');

    return `
      <div class="vouchers-empty">
        <h3 class="u-h3">${emptyTitle}</h3>
        <p class="u-p2">${emptyMessage}</p>
        ${emptySubtitle ? `<p class="u-s3">${emptySubtitle}</p>` : ''}
      </div>
    `;
  }

  showError(message) {
    console.error('Vouchers error:', message);

    // Use toast notification for voucher errors
    if (toastNotifications) {
      toastNotifications.error(message);
    }
  }

  sanitizeId(id) {
    // Remove invalid characters and replace with safe alternatives
    return String(id).replace(/[^a-zA-Z0-9_-]/g, '_');
  }

  /** Escape text for HTML attribute / text nodes in voucher markup. */
  escapeHtmlForVoucher(value) {
    if (value === null || value === undefined) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  getCheckboxIcon(iconType) {
    return SelectionManager.getCheckboxIconHTML(iconType);
  }


  buildBookingUrl(card) {
    // Build booking URL: domain + localeRootUrl + /products/ + productHandle + /bookingToken?variant=variant_id
    const domain = window.location.origin;
    const localeRootUrl = window.routes?.localeRootUrl || '';
    const productHandle = card.productHandle;
    const variantId = card.product_variant_id?.replace(/\D/g, '') || '';
    const bookingToken = card.bookingToken;

    // Build the URL
    const bookingUrl = `${domain}${localeRootUrl}/products/${productHandle}/${bookingToken}?variant=${variantId}`;



    return bookingUrl;
  }

  buildBookingUrlForGroup(group) {
    // Find the first non-booked voucher in the group
    if (!group || !group.vouchers || group.vouchers.length === 0) {
      console.error('No vouchers in group for booking URL');
      return '#';
    }

    const primaryVoucher = group.vouchers.find(voucher => !voucher.is_booked);
    if (!primaryVoucher) {
      console.error('No non-booked voucher found in group');
      return '#';
    }

    return this.buildBookingUrl(primaryVoucher);
  }

}

customElements.define("account-vouchers", AccountVouchers);

class AccountPurchaseOrders extends HTMLElement {
  constructor() {
    super();
    this.delegateElement = null;
    this.pageSize = Number(this.dataset.pageSize) || 10;
    this.state = {
      loading: false,
      error: null,
      orders: [],
      // Set from the shared bookings filter popup; only the ongoing subtab sends them
      filters: { search: '', date_from: '', date_to: '' },
      currentSubtab: 'purchase-invoices', // Default subtab
      pagination: {
        page: 1,
        limit: this.pageSize,
        total: 0,
        totalPages: 1,
        hasNext: false,
        hasPrev: false
      }
    };
    this.hasLoadedOnce = false;
    this.handleTabActivated = this.handleTabActivated.bind(this);

    // Cache configuration using shared utility (include subtab + filters in cache key)
    this.cache = new ComponentCache(5 * 60 * 1000, (subtab, page, limit, filterKey = '') => `orders_${subtab}_${page}_${limit}_${filterKey}`);

    // Persists checkbox selection across pagination when creating invoice docs
    this.invoiceSelectionIds = new Set();
    this.invoiceSelectionPoIds = new Set();
    this.invoiceSelectionLabels = new Map();

    // Initialize selection manager
    this.selectionManager = new SelectionManager({
      getItemKey: (order) => this.getOrderKey(order),
      onSelectionChange: (state) => this.handleSelectionChange(state)
    });

    // Initialize vendor exporter (only create if VendorPurchaseOrderExporter class exists)
    this.exporter = typeof VendorPurchaseOrderExporter !== 'undefined'
      ? new VendorPurchaseOrderExporter()
      : null;
  }

  connectedCallback() {

    this.delegateElement = new Delegate(this);
    this.delegateDocument = new Delegate(document.body);
    this.headerCreateButton = document.querySelector('[data-action="create-new-purchase-order"]');
    this.boundHandleCreatePurchaseOrder = this.handleCreatePurchaseOrder.bind(this);
    this.elements = {
      list: this.querySelector('[data-orders-list]'),
      invoicesList: this.querySelector('[data-invoices-list]'),
      pagination: this.querySelector('[data-pagination]'),
      controls: this.querySelector('[data-controls]'),
      selectAll: this.querySelector('[data-action="toggle-select-all"]'),
      createButton: this.headerCreateButton,
      selectionSummary: this.querySelector('[data-selection-summary]'),
      clearSelectionButton: this.querySelector('[data-action="clear-invoice-selection"]')
    };

    this.bindEvents();

    if (!this.elements.list) {
      console.warn('AccountPurchaseOrders: missing [data-orders-list] element.');
      return;
    }

    this.addEventListener('tab-activated', this.handleTabActivated);

    // Handle browser back/forward navigation
    window.addEventListener('popstate', this.handlePopState.bind(this));

    // Clean up expired cache entries on initialization
    this.cleanupExpiredCache();

    // Initialize checkbox icons using shared utility
    SelectionManager.initializeCheckboxIcons(this);

    this.initializePurchaseOrderButton();

    // Check if this element should be visible (for debugging)
    const isHidden = this.hasAttribute('hidden');
    const tabContent = this.dataset.tabContent;
    const urlView = new URL(window.location).searchParams.get('view');
    const shouldBeVisible = urlView === tabContent;


    // Initialize from URL parameter
    this.initializeFromURL();

    // Initialize subtab state
    this.updateSubtabContent(this.state.currentSubtab);
    this.updateActiveSubtab(this.state.currentSubtab);
    this.updateTopHeaderSubtabs(this.state.currentSubtab);

    // Load data if the purchase orders tab is visible on page load
    if (shouldBeVisible && !isHidden) {

      this.loadOrders();
    } else {
      // Show initial loading state but don't load data until tab is activated
      this.renderLoadingState();
    }
  }

  disconnectedCallback() {
    this.removeEventListener('tab-activated', this.handleTabActivated);
    window.removeEventListener('popstate', this.handlePopState.bind(this));
    if (this.delegateDocument) {
      this.delegateDocument.off();
      this.delegateDocument = null;
    }
  }

  bindEvents() {
    this.delegateElement.on('change', '[data-action="toggle-select-all"]', this.handleToggleSelectAll.bind(this));
    this.delegateElement.on('change', '[data-action="toggle-select-order"]', this.handleToggleSelectOrder.bind(this));
    this.delegateElement.on('click', '.purchase-order-card', this.handlePurchaseOrderCardClick.bind(this));
    this.delegateElement.on('click', '.purchase-order-card__checkbox-label', this.handlePurchaseOrderCheckboxLabelClick.bind(this));
    this.delegateElement.on('click', '[data-action="export-selected"]', this.handleExportSelected.bind(this));
    this.delegateElement.on('click', '[data-action="email-selected"]', this.handleEmailSelected.bind(this));
    this.delegateElement.on('click', '[data-action="clear-invoice-selection"]', this.handleClearInvoiceSelection.bind(this));
    this.delegateDocument.on('click', '[data-action="create-new-purchase-order"]', this.boundHandleCreatePurchaseOrder);
    this.delegateElement.on('click', '[data-action="retry-load-orders"]', () => this.loadOrders(this.state.pagination.page));
    this.delegateElement.on('click', '[data-pagination-action]', this.handlePaginationAction.bind(this));
    this.delegateElement.on('click', '[data-pagination-page]', this.handlePaginationPage.bind(this));
    this.delegateElement.on('click', '[data-action="change-tab"]', this.handleChangeSubtab.bind(this));
    this.delegateElement.on('click', '[data-action="download-pdf"]', this.handleDownloadPDF.bind(this));
    this.delegateElement.on('click', '[data-action="view-pdf"]', this.handleViewPDF.bind(this));
    this.delegateElement.on('click', '[data-action="download-xlsx"]', this.handleDownloadXLSX.bind(this));
  }

  getOrdersCacheKey(subtab, page, limit = this.pageSize) {
    const filterKey = subtab === 'upcoming-experiences' ? JSON.stringify(this.state.filters) : '';
    return this.cache.generateKey(subtab, page, limit, filterKey);
  }

  hasActiveFilters() {
    const { search, date_from, date_to } = this.state.filters;
    return !!(search || date_from || date_to);
  }

  // Called by VendorFilterSortManager when its popup was opened from this list
  applyFilters(filters) {
    this.state.filters = {
      search: filters.search || '',
      date_from: filters.date_from || '',
      date_to: filters.date_to || ''
    };
    this.clearInvoiceSelection();
    this.cache.clear();
    this.loadOrders(1);
  }

  clearInvoiceSelection() {
    this.invoiceSelectionIds.clear();
    this.invoiceSelectionPoIds.clear();
    this.invoiceSelectionLabels.clear();
    this.selectionManager.clearSelection();
    this.updateSelectionSummary();
    this.updateControlStates();
  }

  hasCrossPageSelection() {
    return this.invoiceSelectionPoIds.size > 0 || this.invoiceSelectionIds.size > 0;
  }

  getCrossPageSelectionCount() {
    return this.invoiceSelectionPoIds.size || this.invoiceSelectionIds.size;
  }

  getSelectedPurchaseOrderIdsForActions() {
    return this.buildSelectionDetail().purchaseOrderIds;
  }

  trackInvoiceSelection(order, isSelected) {
    const key = this.getOrderKey(order);
    const poId = this.normalizePurchaseOrderId(this.getBackendPurchaseOrderId(order));

    if (key) {
      if (isSelected) {
        this.invoiceSelectionIds.add(key);
      } else {
        this.invoiceSelectionIds.delete(key);
      }
    }

    if (poId) {
      if (isSelected) {
        this.invoiceSelectionPoIds.add(poId);
        const label = this.formatInvoiceSelectionLabel(order);
        if (label) {
          this.invoiceSelectionLabels.set(poId, label);
        }
      } else {
        this.invoiceSelectionPoIds.delete(poId);
        this.invoiceSelectionLabels.delete(poId);
      }
    }
  }

  restoreSelectionState() {
    this.selectionManager.setItems(this.state.orders, { preserveOffPageSelection: true });
    this.selectionManager.selectedItems.clear();

    this.state.orders.forEach((order) => {
      const key = this.getOrderKey(order);
      if (key && this.invoiceSelectionIds.has(key)) {
        this.selectionManager.selectedItems.add(key);
      }
    });

    this.syncIndividualCheckboxes();
    this.syncSelectAll();
    this.updateSelectionSummary();
    this.updateControlStates();
  }

  updateSelectionSummary() {
    if (!this.elements.selectionSummary) {
      return;
    }

    const count = this.invoiceSelectionPoIds.size || this.invoiceSelectionIds.size;
    if (count > 0) {
      const template = TranslationUtils.get(
        'vendor.purchase_orders_page.search.selection_summary',
        '{count} booking(s) selected for invoice documentation'
      );
      this.elements.selectionSummary.textContent = template.replace('{count}', String(count));
    } else {
      this.elements.selectionSummary.textContent = TranslationUtils.get(
        'vendor.purchase_orders_page.search.selection_hint',
        'Selection is kept when you change page. Leave external bookings unchecked.'
      );
    }
  }

  initializePurchaseOrderButton() {
    if (!this.elements.createButton) return;

    const supplierSettings = window.account?.supplierSettings;
    if (!supplierSettings) {
      this.elements.createButton.disabled = true;
      this.elements.createButton.setAttribute('title',
        TranslationUtils.get('vendor.purchase_orders.no_supplier_data', 'No supplier data available')
      );
      return;
    }

    const canCreate = this.calculatePurchaseOrderAvailability(supplierSettings);
    this.elements.createButton.disabled = !canCreate;

    if (!canCreate) {
      const tooltip = TranslationUtils.get(
        'vendor.purchase_orders.create_disabled_tooltip',
        'Purchase order creation is not available based on your last invoice date and threshold settings.'
      );
      this.elements.createButton.setAttribute('title', tooltip);
    } else {
      this.elements.createButton.removeAttribute('title');
    }
  }

  calculatePurchaseOrderAvailability(supplierData) {
    if (!supplierData) {
      return false;
    }

    const thresholdDays = parseInt(supplierData.purchaseInvoiceThreshold, 10);

    // Match Liquid: no last invoice date → creation allowed
    if (!supplierData.lastInvoiceDate) {
      return true;
    }

    if (!Number.isFinite(thresholdDays) || thresholdDays <= 0) {
      return true;
    }

    try {
      const lastInvoiceDate = new Date(supplierData.lastInvoiceDate);

      if (isNaN(lastInvoiceDate.getTime())) {
        return true;
      }

      const today = new Date();
      const daysSinceLastInvoice = Math.floor((today - lastInvoiceDate) / (1000 * 60 * 60 * 24));

      return daysSinceLastInvoice > thresholdDays;
    } catch (error) {
      console.error('AccountPurchaseOrders: Error calculating purchase order availability', error);
      return false;
    }
  }

  canCreateInvoiceDocumentation() {
    const btn = this.headerCreateButton || document.querySelector('[data-action="create-new-purchase-order"]');
    if (btn?.disabled || btn?.getAttribute('aria-disabled') === 'true' || btn?.hasAttribute('data-disabled')) {
      return false;
    }

    const supplierSettings = window.account?.supplierSettings;
    if (supplierSettings) {
      return this.calculatePurchaseOrderAvailability(supplierSettings);
    }

    return document.querySelector('[data-can-create="true"]') !== null;
  }

  getSelectedPurchaseOrderIds() {
    return this.buildSelectionDetail().purchaseOrderIds;
  }

  getInvoiceCreationMode() {
    const selectedCount = this.invoiceSelectionPoIds.size || this.invoiceSelectionIds.size;
    return selectedCount > 0 ? 'selected' : 'all';
  }

  handleCreatePurchaseOrder(event) {
    event.preventDefault();

    if (this.state.currentSubtab !== 'upcoming-experiences') {
      this.state.currentSubtab = 'upcoming-experiences';
      const url = new URL(window.location);
      url.searchParams.set('purchase-orders', 'upcoming-experiences');
      window.history.replaceState({ subtab: 'upcoming-experiences' }, '', url);
      this.updateActiveSubtab('upcoming-experiences');
      this.updateTopHeaderSubtabs('upcoming-experiences');
      this.updateSubtabContent('upcoming-experiences');
      this.loadOrders(1);
    }

    if (!this.canCreateInvoiceDocumentation()) {
      console.warn('AccountPurchaseOrders: Purchase order creation not available');

      if (window.ErrorUtils && window.ErrorUtils.toast) {
        const message = TranslationUtils.get(
          'vendor.purchase_orders.creation_not_available',
          'Purchase order creation is not available at this time.'
        );
        window.ErrorUtils.toast('vendor.purchase_orders', 'creation_not_available', message);
      }
      return;
    }

    const selectionDetail = this.buildSelectionDetail();
    selectionDetail.pendingTotal = this.state.pagination?.total || 0;
    selectionDetail.invoiceMode = this.getInvoiceCreationMode();
    const invoiceMode = selectionDetail.invoiceMode;

    // Open the purchase order confirmation popup
    const popup = document.querySelector('#purchase-order-popup');
    if (!popup) {
      console.error('Purchase order popup not found');
      if (window.ErrorUtils && window.ErrorUtils.error) {
        window.ErrorUtils.error('vendor.purchase_orders', 'popup_not_found', 'Unable to show confirmation popup. Please refresh the page and try again.');
      }
      return;
    }

    popup.dataset.invoiceMode = invoiceMode;
    this.configurePurchaseOrderPopup(popup, selectionDetail);

    // Open the popup using the global popup manager
    if (window.globalPopup && typeof window.globalPopup.openPopup === 'function') {
      window.globalPopup.openPopup(popup);

    } else {
      console.error('Global popup manager not available');
      if (window.ErrorUtils && window.ErrorUtils.error) {
        window.ErrorUtils.error('vendor.purchase_orders', 'popup_manager_unavailable', 'Unable to show confirmation popup. Please refresh the page and try again.');
      }
    }
  }

  formatPurchaseOrderMessage(path, fallback = '', vars = {}) {
    let text = TranslationUtils.get(path, fallback);

    Object.entries(vars).forEach(([key, value]) => {
      const replacement = value === undefined || value === null ? '' : String(value);
      text = text
        .replace(new RegExp(`\\{\\{\\s*${key}\\s*\\}\\}`, 'g'), replacement)
        .replace(new RegExp(`\\{${key}\\}`, 'g'), replacement);
    });

    return text;
  }

  configurePurchaseOrderPopup(popup, selectionDetail = this.buildSelectionDetail()) {
    const purchaseOrderIds = Array.isArray(selectionDetail?.purchaseOrderIds)
      ? selectionDetail.purchaseOrderIds
      : [];
    const selectedCount = purchaseOrderIds.length;
    const pendingTotal = Number(selectionDetail?.pendingTotal) || this.state.pagination?.total || 0;
    const invoiceMode = selectionDetail?.invoiceMode || this.getInvoiceCreationMode();
    const titleElement = popup.querySelector('[data-purchase-order-popup-title]');
    const descriptionElement = popup.querySelector('[data-invoice-popup-description], [data-purchase-order-popup-description]');
    const selectedListElement = popup.querySelector('[data-invoice-selected-list]');

    if (titleElement) {
      titleElement.textContent = this.formatPurchaseOrderMessage(
        'vendor.purchase_orders_page.info_popup.invoice_title',
        'Skapa fakturaunderlag'
      );
    }

    if (descriptionElement) {
      descriptionElement.textContent = invoiceMode === 'selected' && selectedCount > 0
        ? this.formatPurchaseOrderMessage(
          'vendor.purchase_orders_page.info_popup.selected_confirmation',
          'Skapa fakturaunderlag för {count} valda bokning(ar)?',
          { count: selectedCount }
        )
        : this.formatPurchaseOrderMessage(
          'vendor.purchase_orders_page.info_popup.all_pending_confirmation',
          'Skapa fakturaunderlag för alla berättigade väntande bokningar?'
        );
    }

    if (selectedListElement) {
      if (selectedCount > 0) {
        const items = purchaseOrderIds
          .map((id) => {
            const storedLabel = this.invoiceSelectionLabels.get(id);
            if (storedLabel) {
              return storedLabel;
            }
            const order = this.state.orders.find(
              (row) => this.normalizePurchaseOrderId(this.getBackendPurchaseOrderId(row)) === id
            );
            return order ? this.formatInvoiceSelectionLabel(order) : '';
          })
          .filter(Boolean);
        selectedListElement.innerHTML = items.length
          ? `<ul class="purchase-orders__selected-list">${items.map((name) => `<li class="u-p3">${this.escapeHtml(name)}</li>`).join('')}</ul>`
          : '';
        selectedListElement.removeAttribute('hidden');
      } else {
        selectedListElement.innerHTML = '';
        selectedListElement.setAttribute('hidden', '');
      }
    }

    popup.dataset.selectedPurchaseOrderIds = JSON.stringify(purchaseOrderIds);

    const errorElement = popup.querySelector('[data-invoice-popup-error]');
    if (errorElement) {
      errorElement.textContent = '';
      errorElement.setAttribute('hidden', '');
    }
  }

  handlePurchaseOrderCardClick(event) {
    if (this.state.currentSubtab !== 'upcoming-experiences') {
      return;
    }

    const card = event.target.closest('.purchase-order-card');
    if (!card) {
      return;
    }

    if (event.target.closest('button, a, .purchase-order-card__checkbox-label')) {
      return;
    }

    const checkbox = card.querySelector('[data-action="toggle-select-order"]');
    if (!checkbox) {
      return;
    }

    checkbox.checked = !checkbox.checked;
    this.handleToggleSelectOrder({ target: checkbox });
    this.syncSelectAll();
    this.updateControlStates();
  }

  handlePurchaseOrderCheckboxLabelClick(event) {
    if (this.state.currentSubtab !== 'upcoming-experiences') {
      return;
    }

    const label = event.target.closest('.purchase-order-card__checkbox-label');
    if (!label) {
      return;
    }

    const checkbox = label.querySelector('[data-action="toggle-select-order"]');
    if (!checkbox) {
      return;
    }

    requestAnimationFrame(() => {
      const orderId = checkbox.dataset.orderId;
      const order = this.findOrder(orderId);
      const isSelected = checkbox.checked;

      if (order) {
        this.trackInvoiceSelection(order, isSelected);
      } else if (isSelected) {
        this.invoiceSelectionIds.add(orderId);
        const poId = this.normalizePurchaseOrderId(orderId);
        if (poId) {
          this.invoiceSelectionPoIds.add(poId);
        }
      } else {
        this.invoiceSelectionIds.delete(orderId);
        const poId = this.normalizePurchaseOrderId(orderId);
        if (poId) {
          this.invoiceSelectionPoIds.delete(poId);
        }
      }

      if (isSelected) {
        this.selectionManager.selectItem(orderId);
      } else {
        this.selectionManager.deselectItem(orderId);
      }

      this.syncSelectAll();
      this.updateSelectionSummary();
      this.updateControlStates();
    });
  }

  async loadOrders(page = 1) {


    // Check cache first (include currentSubtab and search in cache key)
    const key = this.getOrdersCacheKey(this.state.currentSubtab, page, this.pageSize);
    const cachedData = this.cache.get(key);
    if (cachedData) {

      this.updateStateWithCachedData(cachedData, page);
      return;
    }


    this.state.loading = true;
    this.state.error = null;
    this.renderLoadingState();

    try {
      const data = await this.fetchSupplierPurchaseOrders({ page, limit: this.pageSize });


      const orders = Array.isArray(data?.purchaseOrders) ? data.purchaseOrders : [];
      const pagination = data?.pagination || {};



      const total = Number.isFinite(Number(pagination.total)) ? Number(pagination.total) : orders.length;
      const limit = Number(pagination.limit) || this.pageSize;
      const currentPage = Number(pagination.page) || page;
      const totalPages = Number(pagination.totalPages) || Math.max(1, Math.ceil(total / limit));

      const responseData = {
        orders,
        pagination: {
          page: currentPage,
          limit,
          total,
          totalPages,
          hasNext: pagination.hasNext !== undefined ? Boolean(pagination.hasNext) : currentPage < totalPages,
          hasPrev: pagination.hasPrev !== undefined ? Boolean(pagination.hasPrev) : currentPage > 1
        }
      };

      // Cache the response (include currentSubtab in cache key)
      const key = this.getOrdersCacheKey(this.state.currentSubtab, page, this.pageSize);
      this.cache.set(key, responseData);

      this.state.orders = orders;
      this.state.pagination = responseData.pagination;
      this.state.loading = false; // Set loading to false BEFORE rendering

      this.hasLoadedOnce = true;
      this.renderOrdersView();
    } catch (error) {
      console.error('AccountPurchaseOrders: failed to load purchase orders', error);
      this.state.loading = false; // Set loading to false BEFORE rendering error
      this.state.error = error;
      this.renderErrorState();
    } finally {
      this.updateControlStates();
    }
  }

  async fetchSupplierPurchaseOrders({ page = 1, limit = 10 } = {}) {
    if (!window.apiService) {
      throw new Error('ApiService not available');
    }

    const url = new URL(`${window.location.origin}${window.proxyPath}/supplier-action/purchase-orders`);
    url.searchParams.append('page', page);
    url.searchParams.append('limit', limit);

    // Different parameters based on current subtab


    if (this.state.currentSubtab === 'upcoming-experiences') {
      // Upcoming Experiences: summary data

      url.searchParams.append('invoice_type', 'summary');
      const { search, date_from, date_to } = this.state.filters;
      // The API rejects a search under 2 characters
      if (search.length >= 2) {
        url.searchParams.append('search', search);
      }
      if (date_from) {
        url.searchParams.append('date_from', date_from);
      }
      if (date_to) {
        url.searchParams.append('date_to', date_to);
      }
    } else {
      // Purchase Invoices: invoice data, all orders (both invoiced and not invoiced)

      url.searchParams.append('invoice_type', 'invoice');
    }



    const headers = {};
    if (window.shopifySessionToken) {
      headers.Authorization = `Bearer ${window.shopifySessionToken}`;
    }

    try {
      const data = await window.apiService.get(url.pathname + url.search, null, { headers });
      return data;
    } catch (err) {
      let message = 'Failed to load purchase orders';
      if (err && typeof err.response === 'string') {
        try {
          const parsed = JSON.parse(err.response);
          if (parsed?.message) message = parsed.message;
        } catch (_) { }
      } else if (err?.message) {
        message = err.message;
      }
      throw new Error(message);
    }
  }

  handleTabActivated() {
    if (this.state.loading) {
      return;
    }
    if (!this.hasLoadedOnce) {
      this.loadOrders();
      return;
    }
    this.renderOrdersView();
    if (this.state.currentSubtab === 'upcoming-experiences') {
      this.syncIndividualCheckboxes();
      this.updateSelectionSummary();
    }
  }

  handleToggleSelectAll(event) {
    const isSelected = event.target.checked;

    this.state.orders.forEach((order) => {
      this.trackInvoiceSelection(order, isSelected);
    });

    this.selectionManager.toggleSelectAll(isSelected);
    this.updateSelectionSummary();
  }

  handleToggleSelectOrder(event) {
    const checkbox = event.target;
    const orderId = checkbox.dataset.orderId;
    const order = this.findOrder(orderId);
    const isSelected = checkbox.checked;

    if (order) {
      this.trackInvoiceSelection(order, isSelected);
    } else if (isSelected) {
      this.invoiceSelectionIds.add(orderId);
      const poId = this.normalizePurchaseOrderId(orderId);
      if (poId) {
        this.invoiceSelectionPoIds.add(poId);
      }
    } else {
      this.invoiceSelectionIds.delete(orderId);
      const poId = this.normalizePurchaseOrderId(orderId);
      if (poId) {
        this.invoiceSelectionPoIds.delete(poId);
      }
    }

    this.selectionManager.toggleItem(orderId, isSelected);
    this.updateSelectionSummary();
  }

  handleSelectionChange(selectionState) {
    // This method is called by SelectionManager when selection changes
    // Update individual checkboxes to match selection state
    this.syncIndividualCheckboxes();
    this.syncSelectAll();
    this.updateControlStates();
  }

  syncIndividualCheckboxes() {
    const selectedItems = this.selectionManager.selectedItems;
    const checkboxes = this.querySelectorAll('[data-action="toggle-select-order"]');

    checkboxes.forEach(checkbox => {
      const orderId = checkbox.dataset.orderId;
      checkbox.checked = selectedItems.has(orderId);
    });
  }

  handleClearInvoiceSelection(event) {
    event.preventDefault();
    if (!this.hasCrossPageSelection()) {
      return;
    }
    this.clearInvoiceSelection();
    this.syncIndividualCheckboxes();
    this.syncSelectAll();
  }

  handleExportSelected() {
    const purchaseOrderIds = this.getSelectedPurchaseOrderIdsForActions();
    if (!purchaseOrderIds.length) {
      const message = TranslationUtils.get(
        'vendor.purchase_orders_page.export.no_selection',
        'Please select at least one purchase order to export'
      );
      toastNotifications.warning(message);
      return;
    }

    // Check selection limit (max 50)
    if (purchaseOrderIds.length > 50) {
      const message = TranslationUtils.get(
        'vendor.purchase_orders_page.export.limit_exceeded',
        'You can export a maximum of 50 purchase orders at a time'
      );
      toastNotifications.warning(message);
      return;
    }



    // Use local exporter instance
    if (this.exporter) {
      // Disable buttons during export
      this.setExportButtonsDisabled(true);

      this.exporter.bulkExportPurchaseOrders(purchaseOrderIds)
        .then(() => {
          this.clearInvoiceSelection();
          this.syncIndividualCheckboxes();
          this.syncSelectAll();
        })
        .catch((error) => {
          console.error('Export failed:', error);
        })
        .finally(() => {
          // Re-enable buttons
          this.setExportButtonsDisabled(false);
        });
    } else {
      console.error('VendorPurchaseOrderExporter not available');
      toastNotifications.error('Export functionality not available');
    }
  }

  handleEmailSelected() {
    const purchaseOrderIds = this.getSelectedPurchaseOrderIdsForActions();
    if (!purchaseOrderIds.length) {
      const message = TranslationUtils.get(
        'vendor.purchase_orders_page.export.no_selection',
        'Please select at least one purchase order to export'
      );
      toastNotifications.warning(message);
      return;
    }

    // Check selection limit (max 50)
    if (purchaseOrderIds.length > 50) {
      const message = TranslationUtils.get(
        'vendor.purchase_orders_page.export.limit_exceeded',
        'You can export a maximum of 50 purchase orders at a time'
      );
      toastNotifications.warning(message);
      return;
    }

    // Use local exporter instance

    if (this.exporter) {
      // Disable buttons during email send
      this.setExportButtonsDisabled(true);

      this.exporter.sendToMyEmail(
        purchaseOrderIds,
        purchaseOrderIds.length
      )
        .then(() => {
          this.clearInvoiceSelection();
          this.syncIndividualCheckboxes();
          this.syncSelectAll();
        })
        .catch((error) => {
          console.error('Email send failed:', error);
        })
        .finally(() => {
          // Re-enable buttons
          this.setExportButtonsDisabled(false);
        });
    } else {
      console.error('❌ VendorPurchaseOrderExporter not available');
      toastNotifications.error('Email export functionality not available');
    }
  }

  /**
   * Enable/disable export buttons
   * @param {boolean} disabled - Whether to disable buttons
   */
  setExportButtonsDisabled(disabled) {
    const exportButton = this.querySelector('[data-action="export-selected"]');
    const emailButton = this.querySelector('[data-action="email-selected"]');
    const clearButton = this.querySelector('[data-action="clear-invoice-selection"]');

    if (exportButton) exportButton.disabled = disabled;
    if (emailButton) emailButton.disabled = disabled;
    if (clearButton) clearButton.disabled = disabled || !this.hasCrossPageSelection();
  }

  handleDownloadPDF(event) {
    const button = event.target.closest('[data-action="download-pdf"]');
    if (!button) return;

    const url = button.dataset.url;
    if (!url) {
      console.error('No PDF URL provided');
      return;
    }

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = ''; // This will use the filename from the Content-Disposition header
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  handleViewPDF(event) {
    const button = event.target.closest('[data-action="view-pdf"]');
    if (!button) return;

    const url = button.dataset.url;
    if (!url) {
      console.error('No PDF URL provided');
      return;
    }

    // Open PDF in a new tab
    window.open(url, '_blank');
  }

  handleDownloadXLSX(event) {
    const button = event.target.closest('[data-action="download-xlsx"]');
    if (!button) return;

    const url = button.dataset.url;
    if (!url) {
      console.error('No Excel URL provided');
      return;
    }

    // Create a temporary link and trigger download
    const link = document.createElement('a');
    link.href = url;
    link.download = ''; // This will use the filename from the Content-Disposition header
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  }

  scrollToTop() {
    // Scroll to the top of the orders list
    const listContainer = this.elements?.list;
    if (listContainer) {
      listContainer.scrollIntoView({ behavior: 'smooth', block: 'start' });
    } else {
      // Fallback to scrolling to the element itself
      this.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }

  handlePaginationAction(event) {
    const control = event.target.closest('[data-pagination-action]');
    if (!control || this.state.loading) {
      return;
    }

    const action = control.dataset.paginationAction;
    const { page, totalPages } = this.state.pagination;

    if (action === 'prev' && page > 1) {
      this.scrollToTop();
      this.loadOrders(page - 1);
    }

    if (action === 'next' && page < totalPages) {
      this.scrollToTop();
      this.loadOrders(page + 1);
    }
  }

  handlePaginationPage(event) {
    const button = event.target.closest('[data-pagination-page]');
    if (!button || this.state.loading) {
      return;
    }

    const targetPage = Number(button.dataset.paginationPage);
    if (!Number.isFinite(targetPage) || targetPage === this.state.pagination.page) {
      return;
    }

    this.scrollToTop();
    this.loadOrders(targetPage);
  }

  renderLoadingState() {
    // Use the appropriate list element based on current subtab
    const listElement = this.state.currentSubtab === 'purchase-invoices'
      ? this.elements?.invoicesList
      : this.elements?.list;

    if (!listElement) {
      return;
    }

    const loadingText = TranslationUtils.get('vendor.purchase_orders_page.loading.label', 'Loading purchase orders...');
    listElement.innerHTML = `
      <div class="vouchers-loading" role="status">
        <div class="spinningLoader is-visible">
          <svg width="36" height="37" viewBox="0 0 36 37" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.07706 16.8043C1.71423 20.4881 2.63996 24.1838 4.6965 27.2617C6.75305 30.3395 9.81317 32.6091 13.3555 33.6836C16.8978 34.7582 20.703 34.5712 24.1229 33.1546C27.5429 31.7381 30.3658 29.1795 32.1108 25.9149C33.8557 22.6503 34.4147 18.8817 33.6926 15.2511C32.9704 11.6206 31.0118 8.35272 28.1503 6.0044C25.2889 3.65607 21.7017 2.37256 18 2.37256" stroke="white" stroke-width="4"/>
          </svg>
        </div>
        <p class="u-p2">${this.escapeHtml(loadingText)}</p>
      </div>
    `;
    this.renderPagination(true);
    this.updateControlStates();
  }

  renderOrdersView() {


    // Use the appropriate list element based on current subtab
    const listElement = this.state.currentSubtab === 'purchase-invoices'
      ? this.elements?.invoicesList
      : this.elements?.list;

    if (!listElement) {
      console.error('AccountPurchaseOrders: No list element found for subtab:', this.state.currentSubtab);
      return;
    }

    if (this.state.loading) {

      this.renderLoadingState();
      return;
    }

    if (this.state.error) {

      this.renderErrorState();
      return;
    }

    if (!this.state.orders.length) {

      this.renderEmptyState();
      return;
    }



    const cards = this.state.orders.map((order) => this.renderOrderCard(order)).join('');
    listElement.innerHTML = cards;
    this.renderPagination();

    if (this.state.currentSubtab === 'upcoming-experiences') {
      this.restoreSelectionState();
    } else {
      this.selectionManager.setItems(this.state.orders);
      this.syncSelectAll();
    }
    this.updateControlStates();
  }

  renderErrorState() {
    // Use the appropriate list element based on current subtab
    const listElement = this.state.currentSubtab === 'purchase-invoices'
      ? this.elements?.invoicesList
      : this.elements?.list;

    if (!listElement) {
      return;
    }

    // Get subtab-specific error messages
    const currentSubtab = this.state.currentSubtab;
    let message, retry;

    if (currentSubtab === 'purchase-invoices') {
      message = TranslationUtils.get('vendor.purchase_orders_page.error.purchase_invoices.title', "We couldn't load your invoices.");
      retry = TranslationUtils.get('vendor.purchase_orders_page.error.purchase_invoices.retry', 'Try again');
    } else if (currentSubtab === 'upcoming-experiences') {
      message = TranslationUtils.get('vendor.purchase_orders_page.error.upcoming_experiences.title', "We couldn't load your purchase orders.");
      retry = TranslationUtils.get('vendor.purchase_orders_page.error.upcoming_experiences.retry', 'Try again');
    } else {
      // Fallback to default
      message = TranslationUtils.get('vendor.purchase_orders_page.error.title', "We couldn't load your purchase orders.");
      retry = TranslationUtils.get('vendor.purchase_orders_page.error.retry', 'Try again');
    }

    const errorDiv = document.createElement('div');
    errorDiv.className = 'vouchers-error';

    const messageP = document.createElement('p');
    messageP.className = 'u-p2';
    messageP.textContent = this.decodeHtmlEntities(message);

    const retryButton = document.createElement('button');
    retryButton.className = 'Button Button--Secondary';
    retryButton.type = 'button';
    retryButton.setAttribute('data-action', 'retry-load-orders');

    const buttonText = document.createElement('span');
    buttonText.className = 'ButtonText u-pb1';
    buttonText.textContent = this.decodeHtmlEntities(retry);
    retryButton.appendChild(buttonText);

    errorDiv.appendChild(messageP);
    errorDiv.appendChild(retryButton);

    listElement.innerHTML = '';
    listElement.appendChild(errorDiv);
    this.renderPagination(true);
    this.updateControlStates();
  }

  renderEmptyState() {
    // Use the appropriate list element based on current subtab
    const listElement = this.state.currentSubtab === 'purchase-invoices'
      ? this.elements?.invoicesList
      : this.elements?.list;

    if (!listElement) {
      return;
    }

    // Get subtab-specific empty state messages
    const currentSubtab = this.state.currentSubtab;
    let title, description;

    if (currentSubtab === 'purchase-invoices') {
      title = TranslationUtils.get('vendor.purchase_orders_page.empty.purchase_invoices.title', 'No invoices yet');
      description = TranslationUtils.get('vendor.purchase_orders_page.empty.purchase_invoices.description', 'Invoices will appear here once they are created.');
    } else if (currentSubtab === 'upcoming-experiences' && this.hasActiveFilters()) {
      title = TranslationUtils.get('vendor.purchase_orders_page.search.no_results_title', 'Your search returned no results.');
      description = TranslationUtils.get('vendor.purchase_orders_page.search.no_results_description', 'Check the spelling or try a less specific search term.');
    } else if (currentSubtab === 'upcoming-experiences') {
      title = TranslationUtils.get('vendor.purchase_orders_page.empty.upcoming_experiences.title', 'No purchase orders yet');
      description = TranslationUtils.get('vendor.purchase_orders_page.empty.upcoming_experiences.description', 'Completed bookings will generate purchase orders that appear here.');
    } else {
      // Fallback to default
      title = TranslationUtils.get('vendor.purchase_orders_page.empty.title', 'No purchase orders yet');
      description = TranslationUtils.get('vendor.purchase_orders_page.empty.description', 'Completed bookings will generate purchase orders that appear here.');
    }

    const emptyDiv = document.createElement('div');
    emptyDiv.className = 'vouchers-empty';

    const titleH3 = document.createElement('h3');
    titleH3.className = 'u-s2';
    titleH3.textContent = this.decodeHtmlEntities(title);

    const descriptionP = document.createElement('p');
    descriptionP.className = 'u-p2';
    descriptionP.textContent = this.decodeHtmlEntities(description);

    emptyDiv.appendChild(titleH3);
    emptyDiv.appendChild(descriptionP);

    listElement.innerHTML = '';
    listElement.appendChild(emptyDiv);
    this.renderPagination(true);
    this.syncSelectAll();
    this.updateControlStates();
  }

  renderOrderCard(order) {
    const orderKey = this.getOrderKey(order);
    const isSelected = this.invoiceSelectionIds.has(orderKey);

    // Use invoice_name if available (for completed bookings), otherwise use experience_name
    // Replace hardcoded "Purchase Invoice" prefix from backend with translated label
    let displayName = order?.invoice_name || order?.experience_name || TranslationUtils.get('vendor.purchase_orders_page.list.unknown_invoice', 'Invoice');
    const purchaseInvoiceMatch = displayName.match(/^Purchase Invoice\s*#?\s*(.+)/i);
    if (purchaseInvoiceMatch) {
      displayName = TranslationUtils.get('vendor.purchase_orders_page.list.purchase_order_label', 'Invoice basis #') + purchaseInvoiceMatch[1];
    }
    const invoiceDate = this.formatBookingDate(order?.invoice_date);
    const bookingDate = this.formatBookingDate(order?.booking_date);
    const invoiceReference = order?.invoice_reference || '';
    const statusInfo = this.getStatusInfo(order?.payment_status);
    const amountExTax = this.formatCurrency(order?.vendor_price_ex_tax, order?.currency);
    const amountWithTax = this.formatCurrency(order?.vendor_price_with_tax, order?.currency);
    const orderId = order?.orderId || '';
    const giftCardGid = order?.gift_card_gid || '';
    const voucherCodeLast4 = order?.voucher_code_last4 || '';
    const isUpcomingExperiences = this.state.currentSubtab === 'upcoming-experiences';

    // Add apps/proxy to the URLs - extract path from full URL if needed
    const pdfUrl = order?.pdfUrl ? this.buildProxyUrl(order.pdfUrl) : '';
    const xlsxUrl = order?.xlsxUrl ? this.buildProxyUrl(order.xlsxUrl) : '';

    const cardClasses = [
      'purchase-order-card',
      isUpcomingExperiences ? 'purchase-order-card--selectable' : '',
      isUpcomingExperiences && isSelected ? 'purchase-order-card--selected' : ''
    ].filter(Boolean).join(' ');

    return `
      <article class="${cardClasses}" data-order-id="${this.escapeHtml(orderKey)}">
        <div class="purchase-order-card__checkbox">
          <label class="purchase-order-card__checkbox-label">
            <input type="checkbox" class="purchase-order-checkbox" data-action="toggle-select-order" data-order-id="${this.escapeHtml(orderKey)}" ${isSelected ? 'checked' : ''} style="display: none;">
            <div class="custom-checkbox">
              <div class="checkbox-empty">${SelectionManager.getCheckboxIconHTML('checkbox-empty')}</div>
              <div class="checkbox-solid">${SelectionManager.getCheckboxIconHTML('checkbox-solid')}</div>
            </div>
            <span class="visually-hidden">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.a11y.select_order', 'Select purchase order'))}</span>
          </label>
        </div>
        <div class="purchase-order-card__content">
          <p class="purchase-order-card__title u-s3">${this.escapeHtml(displayName)}</p>
          <dl class="purchase-order-card__meta">
            <div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.status', 'Status'))}</dt><dd><span class="u-s3 purchase-order-card__status purchase-order-card__status--${statusInfo.variant}">${this.escapeHtml(statusInfo.label)}</span></dd></div>
            ${isUpcomingExperiences && order?.booking_date ? `<div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.booking_date', 'Booking date'))}</dt><dd class="u-s3">${this.escapeHtml(bookingDate)}</dd></div>` : ''}
            ${!isUpcomingExperiences && order?.invoice_date ? `<div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.invoice_date', 'Invoice date'))}</dt><dd class="u-s3">${this.escapeHtml(invoiceDate)}</dd></div>` : ''}
            ${invoiceReference ? `<div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.invoice_reference', 'Invoice reference'))}</dt><dd class="u-s3">${this.escapeHtml(invoiceReference)}</dd></div>` : ''}
            ${orderId ? `<div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.order_id', 'Order ID'))}</dt><dd class="u-s3">${this.escapeHtml(orderId)}</dd></div>` : ''}
            ${giftCardGid ? `<div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.voucher_id', 'Voucher ID'))}</dt><dd class="u-s3">${this.escapeHtml(giftCardGid)}</dd></div>` : ''}
            ${voucherCodeLast4 ? `<div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.voucher_code', 'Voucher Code'))}</dt><dd class="u-s3">${this.escapeHtml(voucherCodeLast4)}</dd></div>` : ''}
            ${amountExTax ? `<div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.amount_ex_tax', 'Amount ex tax'))}</dt><dd class="u-s3">${this.escapeHtml(amountExTax)}</dd></div>` : ''}
            ${amountWithTax ? `<div><dt class="grey-secondary-text u-p3">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.amount_with_tax', 'Amount w tax'))}</dt><dd class="u-s3">${this.escapeHtml(amountWithTax)}</dd></div>` : ''}
          </dl>
        </div>
        <div class="purchase-order-card__actions">
          ${pdfUrl ? `<button class="Button Button--PrimaryOnDark" data-action="download-pdf" data-url="${this.escapeHtml(pdfUrl)}">
            <span class="ButtonText u-pb1">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.download_pdf', 'Download PDF'))}</span>
          </button>` : ''}
          ${pdfUrl ? `<button class="Button Button--Secondary" data-action="view-pdf" data-url="${this.escapeHtml(pdfUrl)}">
            <span class="ButtonText u-pb1">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.view_pdf', 'View PDF'))}</span>
          </button>` : ''}
          ${xlsxUrl ? `<button class="Button Button--Secondary" data-action="download-xlsx" data-url="${this.escapeHtml(xlsxUrl)}">
            <span class="ButtonText u-pb1">${this.escapeHtml(TranslationUtils.get('vendor.purchase_orders_page.list.download_summary', 'Download Summary'))}</span>
          </button>` : ''}
        </div>
      </article>
    `;
  }

  renderPagination(hidden = false) {
    if (!this.elements?.pagination) {
      return;
    }

    if (hidden || !this.state.orders.length || this.state.pagination.totalPages <= 1) {
      this.elements.pagination.innerHTML = '';
      return;
    }

    const { page, totalPages, total, limit } = this.state.pagination;
    const visiblePages = this.buildVisiblePages(totalPages, page);

    let paginationHTML = '';

    // Previous button
    if (page > 1) {
      paginationHTML += `
        <li class="mr-m">
          <button class="pagination-arrow" data-pagination-action="prev" aria-label="${this.escapeHtml(TranslationUtils.get('pagination.previous', 'Previous'))}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.625 13.125L2.5 10M2.5 10L5.625 6.875M2.5 10H17.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </li>`;
    }

    // Page numbers
    visiblePages.forEach((pageNum) => {
      if (pageNum === 'ellipsis') {
        paginationHTML += `
        <li class="Pagination--ItemWrapper">
          <span class="Pagination--Item">...</span>
        </li>`;
      } else {
        const isActive = pageNum === page;
        if (isActive) {
          paginationHTML += `
        <li class="Pagination--ItemWrapper">
          <span class="Pagination--Item Pagination--ItemCurrent u-p5" aria-current="page" aria-label="${this.escapeHtml(TranslationUtils.get('pagination.page', 'Page'))} ${pageNum}">${pageNum}</span>
        </li>`;
        } else {
          paginationHTML += `
        <li class="Pagination--ItemWrapper">
          <button class="Pagination--Item link u-p5" data-pagination-page="${pageNum}" aria-label="${this.escapeHtml(TranslationUtils.get('pagination.page', 'Page'))} ${pageNum}">${pageNum}</button>
        </li>`;
        }
      }
    });

    // Next button
    if (page < totalPages) {
      paginationHTML += `
        <li>
          <button class="pagination-arrow" data-pagination-action="next" aria-label="${this.escapeHtml(TranslationUtils.get('pagination.next', 'Next'))}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.375 6.875L17.5 10M17.5 10L14.375 13.125M17.5 10H2.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </li>`;
    }

    // Summary text
    const start = Math.max(1, (page - 1) * limit + 1);
    const end = Math.min(total, page * limit);
    const summaryText = TranslationUtils.get('pagination.showing', 'Showing {{ start }}-{{ end }} of {{ total }}')
      .replace('{{ start }}', start)
      .replace('{{ end }}', end)
      .replace('{{ total }}', total);

    this.elements.pagination.innerHTML = `
      <div class="Pagination-Wrapper">
        <nav class="pagination" role="navigation" aria-label="${this.escapeHtml(TranslationUtils.get('pagination.label', 'Pagination'))}">
          <ul class="Pagination--List" role="list">
            ${paginationHTML}
          </ul>
        </nav>
        <div class="main-collection__product-count u-s4">
          ${this.escapeHtml(summaryText)}
        </div>
      </div>
    `;
  }

  buildVisiblePages(totalPages, currentPage) {
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, index) => index + 1);
    }

    const pages = [1];
    const start = Math.max(2, currentPage - 1);
    const end = Math.min(totalPages - 1, currentPage + 1);

    if (start > 2) {
      pages.push('ellipsis');
    }

    for (let page = start; page <= end; page += 1) {
      pages.push(page);
    }

    if (end < totalPages - 1) {
      pages.push('ellipsis');
    }

    pages.push(totalPages);
    return pages;
  }

  updateControlStates() {
    if (!this.elements?.controls || !this.selectionManager) {
      return;
    }

    const hasSelection = this.hasCrossPageSelection() && !this.state.loading && !this.state.error;
    const hasOrders = this.state.orders.length > 0 && !this.state.loading && !this.state.error;

    // Show/hide the controls bar based on whether there are orders; it stays while
    // filtered so a search that matches nothing can still be changed or reset
    const isFiltered = this.state.currentSubtab === 'upcoming-experiences' && this.hasActiveFilters();
    if (hasOrders || isFiltered) {
      this.elements.controls.setAttribute('aria-hidden', 'false');
    } else {
      this.elements.controls.setAttribute('aria-hidden', 'true');
    }

    const actionButtons = this.elements.controls.querySelectorAll(
      '[data-action="export-selected"], [data-action="email-selected"]'
    );
    actionButtons.forEach((button) => {
      button.disabled = !hasSelection;
    });

    const clearButton = this.elements.clearSelectionButton
      || this.elements.controls.querySelector('[data-action="clear-invoice-selection"]');
    if (clearButton) {
      clearButton.disabled = !hasSelection;
    }

    if (this.elements.selectAll) {
      this.elements.selectAll.disabled = !this.state.orders.length || this.state.loading || this.state.error;
    }
  }

  syncSelectAll() {
    if (!this.elements?.selectAll) {
      return;
    }

    const checkbox = this.elements.selectAll.querySelector('input[type="checkbox"]') || this.elements.selectAll;
    const pageKeys = this.state.orders
      .map((order) => this.getOrderKey(order))
      .filter(Boolean);
    const selectedOnPage = pageKeys.filter((key) => this.invoiceSelectionIds.has(key)).length;
    const allOnPageSelected = pageKeys.length > 0 && selectedOnPage === pageKeys.length;
    const partialOnPage = selectedOnPage > 0 && !allOnPageSelected;

    checkbox.checked = allOnPageSelected;
    checkbox.indeterminate = partialOnPage;
  }

  buildSelectionDetail() {
    const selectedIds = this.invoiceSelectionIds
      ? Array.from(this.invoiceSelectionIds)
      : this.selectionManager.getSelectionState().selectedItems;
    const orders = selectedIds.map((id) => this.findOrder(id)).filter(Boolean);
    const purchaseOrderIds = this.invoiceSelectionPoIds.size > 0
      ? Array.from(this.invoiceSelectionPoIds)
      : selectedIds
        .map((id) => this.normalizePurchaseOrderId(id))
        .filter((id) => id !== null);

    if (this.invoiceSelectionPoIds.size === 0) {
      orders
        .map((order) => this.getBackendPurchaseOrderId(order))
        .map((id) => this.normalizePurchaseOrderId(id))
        .filter((id) => id !== null && !purchaseOrderIds.includes(id))
        .forEach((id) => purchaseOrderIds.push(id));
    }

    return { selectedIds, purchaseOrderIds, orders };
  }

  normalizePurchaseOrderId(value) {
    if (value === undefined || value === null) {
      return null;
    }

    const text = String(value).trim();
    if (!/^\d+$/.test(text)) {
      return null;
    }

    const id = Number(text);
    return Number.isInteger(id) && id > 0 ? id : null;
  }

  getBackendPurchaseOrderId(order) {
    if (!order) {
      return '';
    }

    if (order.purchase_order_id !== undefined && order.purchase_order_id !== null && order.purchase_order_id !== '') {
      return String(order.purchase_order_id);
    }

    if (order.purchaseOrderId !== undefined && order.purchaseOrderId !== null && order.purchaseOrderId !== '') {
      return String(order.purchaseOrderId);
    }

    if (order.id !== undefined && order.id !== null && order.id !== '') {
      return String(order.id);
    }

    return '';
  }

  findOrder(orderId) {
    return this.state.orders.find((order) => this.getOrderKey(order) === orderId);
  }

  getOrderKey(order) {
    if (!order) {
      return '';
    }
    if (order.id !== undefined) {
      return String(order.id);
    }
    if (order.purchase_order_id !== undefined) {
      return String(order.purchase_order_id);
    }
    if (order.shopify_order_gid) {
      return order.shopify_order_gid;
    }
    return '';
  }

  formatInvoiceSelectionLabel(order) {
    if (!order) {
      return '';
    }

    let displayName = order.invoice_name || order.experience_name || '';
    const purchaseInvoiceMatch = displayName.match(/^Purchase Invoice\s*#?\s*(.+)/i);
    if (purchaseInvoiceMatch) {
      displayName = TranslationUtils.get(
        'vendor.purchase_orders_page.list.purchase_order_label',
        'Invoice basis #'
      ) + purchaseInvoiceMatch[1];
    }

    const voucherId = order.gift_card_gid ? String(order.gift_card_gid).trim() : '';

    if (displayName && voucherId) {
      return `${displayName} - ${voucherId}`;
    }

    return displayName || voucherId;
  }

  getOrderDisplayNumber(order) {
    if (order?.display_number) {
      return order.display_number;
    }
    if (order?.po_number) {
      return order.po_number;
    }
    if (order?.id) {
      return order.id;
    }
    const fallback = this.extractIdFromGid(order?.shopify_order_gid);
    return fallback || TranslationUtils.get('vendor.purchase_orders_page.list.unknown_number', 'N/A');
  }

  extractIdFromGid(gid) {
    if (!gid || typeof gid !== 'string') {
      return '';
    }
    const parts = gid.split('/');
    return parts[parts.length - 1] || '';
  }

  formatBookingDate(value) {
    if (!value) {
      return TranslationUtils.get('vendor.purchase_orders_page.list.no_booking_date', 'No booking date');
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return TranslationUtils.get('vendor.purchase_orders_page.list.no_booking_date', 'No booking date');
    }
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${date.getFullYear()}/${month}/${day}`;
  }

  formatCurrency(value, currency = 'SEK') {
    const numericValue = typeof value === 'string' ? Number(value) : value;
    if (!Number.isFinite(numericValue)) {
      return '';
    }

    // Convert to cents/minor currency units for Shopify
    const cents = Math.round(numericValue * 100);

    // Use Shopify's money formatting if available
    if (typeof Shopify !== 'undefined' && Shopify.formatMoney && window.theme?.moneyFormat) {
      return Shopify.formatMoney(cents, window.theme.moneyFormat);
    }

    // Fallback to Intl formatting
    try {
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(numericValue);
    } catch (error) {
      return `${numericValue.toFixed(2)} ${currency}`;
    }
  }

  getStatusInfo(status) {
    if (!status) {
      const label = TranslationUtils.get('vendor.purchase_orders_page.status.unknown', 'Unknown');
      return { label, variant: 'unknown' };
    }

    const normalized = String(status).toLowerCase().replace(/\s+/g, '_');
    const translationKey = `vendor.purchase_orders_page.status.${normalized}`;
    const fallback = this.capitalize(normalized.replace(/_/g, ' '));
    const label = TranslationUtils.get(translationKey, fallback);
    const variant = normalized.replace(/[^a-z0-9-]+/g, '-') || 'unknown';
    return { label, variant };
  }

  capitalize(value) {
    return value.split(' ').map((part) => part.charAt(0).toUpperCase() + part.slice(1)).join(' ');
  }

  escapeHtml(value) {
    if (value === null || value === undefined) {
      return '';
    }
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  decodeHtmlEntities(value) {
    if (value === null || value === undefined) {
      return '';
    }
    const textarea = document.createElement('textarea');
    textarea.innerHTML = String(value);
    return textarea.value;
  }

  buildProxyUrl(url) {
    if (!url) return '';

    try {
      // If it's already a full URL, extract the pathname
      if (url.startsWith('http://') || url.startsWith('https://')) {
        const urlObj = new URL(url);
        return `/apps/proxy${urlObj.pathname}${urlObj.search}`;
      }

      // If it's a relative path, add /apps/proxy prefix
      return `/apps/proxy${url}`;
    } catch (error) {
      console.error('Failed to build proxy URL:', error);
      return url;
    }
  }

  /**
   * Check if PDF actions are available for a purchase order
   * Always true - either PDF exists or can be generated
   * @param {Object} order - Purchase order object
   * @returns {boolean} True if PDF actions are available
   */
  isPdfAvailable(order) {
    if (!order) {
      return false;
    }

    // Always return true - either PDF exists or can be generated
    return true;
  }

  /**
   * Check if PDF already exists (has JWT tokens or pdf_url)
   * @param {Object} order - Purchase order object
   * @returns {boolean} True if PDF already exists
   */
  isPdfGenerated(order) {
    if (!order) {
      return false;
    }

    // Check for JWT tokens (preferred)
    if (order.pdf_access_token && order.pdf_serve_url) {
      return true;
    }

    // Check for pdf_url (legacy or no JWT tokens yet)
    if (order.pdf_url) {
      return true;
    }

    return false;
  }

  /**
   * Get button text based on PDF status
   * @param {Object} order - Purchase order object
   * @param {string} action - 'view' or 'download'
   * @returns {string} Button text
   */
  getPdfButtonText(order, action) {
    const isPdfGenerated = this.isPdfGenerated(order);

    if (action === 'download') {
      return isPdfGenerated
        ? TranslationUtils.get('vendor.purchase_orders_page.buttons.download', 'Download')
        : TranslationUtils.get('vendor.purchase_orders_page.buttons.generate_download', 'Generate & Download');
    } else {
      return isPdfGenerated
        ? TranslationUtils.get('vendor.purchase_orders_page.buttons.view', 'View')
        : TranslationUtils.get('vendor.purchase_orders_page.buttons.generate_view', 'Generate & View');
    }
  }

  // Cache management methods using shared utility
  clearCache() {
    this.cache.clear();
  }

  updateStateWithCachedData(cachedData, page) {
    this.state.orders = cachedData.orders;
    this.state.pagination = cachedData.pagination;
    this.hasLoadedOnce = true;
    this.renderOrdersView();
    this.updateControlStates();
  }

  getCacheStats() {
    return this.cache.getStats();
  }

  cleanupExpiredCache() {
    const cleanedCount = this.cache.cleanupExpired();
  }

  invalidateCache() {
    // Clear cache after any action that might affect purchase orders
    this.cache.clear();
  }

  // Subtab management methods
  initializeFromURL() {
    const urlParams = new URLSearchParams(window.location.search);
    const subtabParam = urlParams.get('purchase-orders');
    const validSubtabs = ['purchase-invoices', 'upcoming-experiences'];

    if (subtabParam && validSubtabs.includes(subtabParam)) {
      this.state.currentSubtab = subtabParam;

    }
  }

  handlePopState(event) {
    const urlParams = new URLSearchParams(window.location.search);
    const subtabParam = urlParams.get('purchase-orders');
    const validSubtabs = ['purchase-invoices', 'upcoming-experiences'];

    if (subtabParam && validSubtabs.includes(subtabParam) && subtabParam !== this.state.currentSubtab) {

      this.state.currentSubtab = subtabParam;

      this.updateActiveSubtab(subtabParam);
      this.updateTopHeaderSubtabs(subtabParam);
      this.updateSubtabContent(subtabParam);

      // Load data for the subtab from URL

      this.loadOrders(1);
    }
  }

  handleChangeSubtab(event) {
    const tab = event.target.closest('[data-action="change-tab"]');
    if (!tab) return;

    const subtab = tab.dataset.tab;
    if (!subtab) return;

    this.activateSubtab(subtab);
  }

  activateSubtab(subtab, page = 1) {
    const validSubtabs = ['purchase-invoices', 'upcoming-experiences'];
    if (!validSubtabs.includes(subtab)) {
      return;
    }

    this.state.currentSubtab = subtab;

    const url = new URL(window.location);
    url.searchParams.set('purchase-orders', subtab);
    window.history.pushState({ subtab }, '', url);

    this.updateActiveSubtab(subtab);
    this.updateTopHeaderSubtabs(subtab);
    this.updateSubtabContent(subtab);
    this.loadOrders(page);
  }

  updateActiveSubtab(subtab) {
    const allTabs = this.querySelectorAll('[data-action="change-tab"]');
    allTabs.forEach(tab => {
      if (tab.dataset.tab === subtab) {
        tab.setAttribute('data-active', 'true');
      } else {
        tab.setAttribute('data-active', 'false');
      }
    });
  }

  updateSubtabContent(subtab) {
    const allContent = this.querySelectorAll('[data-subtab-content]');
    allContent.forEach(content => {
      const contentTab = content.dataset.subtabContent;
      if (contentTab === subtab) {
        content.setAttribute('aria-hidden', 'false');
      } else {
        content.setAttribute('aria-hidden', 'true');
      }
    });
  }

  updateTopHeaderSubtabs(subtab) {
    const allHeaderSections = document.querySelectorAll('[data-tab-header="purchase-orders"][data-subtab-header]');
    allHeaderSections.forEach(section => {
      const headerSubtab = section.dataset.subtabHeader;
      if (headerSubtab === subtab) {
        section.removeAttribute('hidden');
        section.setAttribute('aria-hidden', 'false');
      } else {
        section.setAttribute('hidden', '');
        section.setAttribute('aria-hidden', 'true');
      }
    });
  }
}

customElements.define('account-purchase-orders', AccountPurchaseOrders);

// Controls the top account tabs (vouchers, contact, orders, addresses, loyalty)
class AccountMainTabs extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegateElement = null;
  }

  connectedCallback() {
    this.delegateElement = new Delegate(this.element);
    this.bindEvents();
    this.cleanUpEmptyTranslationTabs();
    this.ensureInitialState();
  }

  bindEvents() {
    // Reuse existing delegation pattern with the same data-action
    this.delegateElement.on('click', '[data-action="change-tab"]', this.handleTopTabChange.bind(this));
  }

  // Hide/remove tabs that have empty translation text
  cleanUpEmptyTranslationTabs() {
    const swiperEl = this.element.querySelector('swiper-init');
    const tabs = Array.from(this.element.querySelectorAll('.account-page__tab'));

    let removed = false;
    tabs.forEach((btn) => {
      const label = (btn.textContent || '').trim();
      if (!label) {
        // Remove entire slide button

        btn.remove();
        removed = true;
      }
    });

    // Reinitialize Swiper if tabs were removed
    if (removed && swiperEl && typeof swiperEl.updateSlides === 'function') {
      swiperEl.updateSlides();
    }
  }

  // Ensure the first available tab is active
  ensureInitialState() {
    const tabs = Array.from(this.element.querySelectorAll('.account-page__tab'));
    if (!tabs.length) {
      return;
    }

    let urlView = new URL(window.location).searchParams.get('view');
    const originalUrlView = urlView; // Keep original for showTabContent

    // Map sub-chat views to the main chat tab
    if (urlView === 'closed-chats' || urlView === 'escalated-chats') {
      urlView = 'active-chats'; // Activate the main chat tab
    }

    // Map calendar subtab views to the main calendar tab
    if (['calendar-view', 'calendar-services', 'calendar-resources', 'calendar-team-members'].includes(urlView)) {
      urlView = 'calendar'; // Activate the main calendar tab
    }

    let activeTab = tabs.find((tab) => tab.dataset.tab === urlView) || tabs.find((tab) => tab.classList.contains('is-active'));
    if (!activeTab) {
      activeTab = tabs[0];
      activeTab.classList.add('is-active');
    }

    tabs.forEach((tab) => {
      const isActive = tab === activeTab;
      tab.classList.toggle('is-active', isActive);
      tab.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });

    if (activeTab.dataset.tab) {
      // Wait for custom elements to be fully initialized before showing content
      requestAnimationFrame(() => {
        // Use original view for sub-tabs, otherwise use the active tab's view
        this.showTabContent(originalUrlView || activeTab.dataset.tab);
      });
    }
  }

  handleTopTabChange(event) {
    const tabButton = event.target.closest('.account-page__tab');
    if (!tabButton) {
      return;
    }

    const tabKey = tabButton.dataset.tab;
    if (!tabKey) {
      return;
    }

    const allTabs = this.element.querySelectorAll('.account-page__tab');
    allTabs.forEach((tab) => {
      tab.classList.remove('is-active');
      tab.setAttribute('aria-selected', tab === tabButton ? 'true' : 'false');
    });
    tabButton.classList.add('is-active');

    const url = new URL(window.location);
    url.searchParams.set('view', tabKey);

    // Clean up voucher-specific parameters when switching away from vouchers
    if (tabKey !== 'vouchers' && tabKey !== 'manage-bookings') {
      if (url.searchParams.has('vouchers')) {
        url.searchParams.delete('vouchers');
      }
    }

    window.history.pushState({ view: tabKey }, '', url);

    this.showTabContent(tabKey);
  }

  showTabContent(tabKey) {
    const sections = document.querySelectorAll('[data-tab-content]');
    sections.forEach((section) => {
      // Skip nested subtab content managed by their own components (e.g. calendar subtabs)
      if (section.closest('vendor-calendar') && section !== section.closest('vendor-calendar')) return;

      const sectionTabContent = section.dataset.tabContent;
      let isMatch = sectionTabContent === tabKey;

      // Special handling for vendor-chat: show for all chat views
      if (sectionTabContent === 'active-chats' && (tabKey === 'closed-chats' || tabKey === 'escalated-chats')) {
        isMatch = true;
      }

      // Special handling for vendor-support: show for all support views
      if (sectionTabContent === 'active-tickets' && tabKey === 'closed-tickets') {
        isMatch = true;
      }

      // Special handling for calendar: show for all calendar subtab views
      if (sectionTabContent === 'calendar' && ['calendar-view', 'calendar-services', 'calendar-resources', 'calendar-team-members'].includes(tabKey)) {
        isMatch = true;
      }

      if (isMatch) {
        section.setAttribute('aria-hidden', 'false');
      } else {
        section.setAttribute('aria-hidden', 'true');
      }
    });

    // Note: Header containers for sub-tabs (like past-bookings, upcoming-bookings)
    // are managed by AccountVouchers.updateHeaderVisibility() and renderContentHeader()
    // Only manage headers that match top-level tab keys (for non-voucher sections)
    const headerContainers = document.querySelectorAll('[data-tab-header]');
    headerContainers.forEach((container) => {
      const headerTabContent = container.dataset.tabHeader;
      const isMatch = headerTabContent === tabKey;

      if (isMatch) {
        container.setAttribute('aria-hidden', 'false');
        // Clear any inline display style that might have been set
        container.style.display = '';
      } else {
        // Only hide if this is a top-level tab header, not a sub-tab header
        // Sub-tab headers (past-bookings, upcoming-bookings, etc.) are managed separately
        const isSubTabHeader = container.closest('[data-tab-content="manage-bookings"]') || container.closest('[data-tab-content="vouchers"]') || container.closest('[data-tab-content="calendar"]');
        if (!isSubTabHeader) {
          container.setAttribute('aria-hidden', 'true');
        }
      }
    });

    if (tabKey === 'vouchers' || tabKey === 'manage-bookings') {
      const vouchersElement = document.querySelector(`account-vouchers[data-tab-content="${tabKey}"]`) || document.querySelector('account-vouchers');
      if (vouchersElement) {
        // Check if the element is properly upgraded and has its methods
        const dispatchEvent = () => {
          if (vouchersElement.handleTabActivated && vouchersElement.delegateElement) {
            vouchersElement.dispatchEvent(new CustomEvent('tab-activated'));
          } else {
            // Element not fully initialized yet, wait a bit more
            setTimeout(dispatchEvent, 10);
          }
        };
        dispatchEvent();
      }
    }

    if (tabKey === 'purchase-orders') {
      const purchaseOrdersElement = document.querySelector(`account-purchase-orders[data-tab-content="${tabKey}"]`) || document.querySelector('account-purchase-orders');
      if (purchaseOrdersElement) {
        // Check if the element is properly upgraded and has its methods
        const dispatchEvent = () => {
          if (purchaseOrdersElement.handleTabActivated && purchaseOrdersElement.delegateElement) {
            purchaseOrdersElement.dispatchEvent(new CustomEvent('tab-activated'));
          } else {
            // Element not fully initialized yet, wait a bit more
            setTimeout(dispatchEvent, 10);
          }
        };
        dispatchEvent();
      }
    }

    // Handle vendor-chat tab activation (active-chats, closed-chats, escalated-chats)
    if (['active-chats', 'closed-chats', 'escalated-chats'].includes(tabKey)) {
      const vendorChatElement = document.querySelector('vendor-chat');
      if (vendorChatElement) {
        vendorChatElement.dispatchEvent(new CustomEvent('tab-activated', {
          detail: { view: tabKey }
        }));
      }
    } else {
      // Disconnect websocket when switching away from chat tabs
      if (typeof VendorWebSocketService !== 'undefined') {
        VendorWebSocketService.disconnect();
      }
    }

    // Handle vendor-support tab activation (active-tickets, closed-tickets)
    if (['active-tickets', 'closed-tickets'].includes(tabKey)) {
      const vendorSupportElement = document.querySelector('vendor-support');
      if (vendorSupportElement) {
        vendorSupportElement.dispatchEvent(new CustomEvent('tab-activated', {
          detail: { view: tabKey }
        }));
      }
    }

    // Handle calendar subtab activation
    if (['calendar', 'calendar-view', 'calendar-services', 'calendar-resources', 'calendar-team-members'].includes(tabKey)) {
      const calendarElement = document.querySelector('vendor-calendar');
      if (calendarElement && calendarElement.handleChangeTab) {
        const subtab = tabKey === 'calendar' ? 'calendar-view' : tabKey;
        calendarElement.handleChangeTab(subtab, false);
      }
    }

    // Handle animation for static tabs (orders, contact, addresses)
    if (tabKey === 'orders' || tabKey === 'contact' || tabKey === 'addresses') {
      let tabElement;
      if (tabKey === 'orders') {
        tabElement = document.querySelector('account-orders[data-tab-content="orders"]');
      } else if (tabKey === 'contact') {
        tabElement = document.querySelector('[data-tab-content="contact"]');
      } else if (tabKey === 'addresses') {
        tabElement = document.querySelector('account-addresses[data-tab-content="addresses"]');
      }

      if (tabElement && window.revolutionAnimation) {
        // Reset and trigger animation for the tab
        window.revolutionAnimation.resetElement(tabElement);
        // Small delay to ensure animation triggers properly
        setTimeout(() => {
          if (window.revolutionAnimation) {
            window.revolutionAnimation.processNewElement(tabElement);
          }
        }, 50);
      }
    }
  }

}

customElements.define('account-main-tabs', AccountMainTabs);

/**
 * AccountAddresses - Simplified version for server-rendered addresses
 * Only handles delete action since addresses are rendered via Liquid
 */
class AccountAddresses extends HTMLElement {
  constructor() {
    super();
    this.element = this;
    this.delegateElement = null;
    this.editModal = null;
    this.editForm = null;
  }

  connectedCallback() {
    this.delegateElement = new Delegate(this.element);
    this.editModal = document.getElementById('modal-address-edit');
    this.editForm = this.editModal?.querySelector('[data-address-form="edit"]');
    this.addModal = document.getElementById('modal-address-new');
    this.addForm = this.addModal?.querySelector('form[action*="/account/addresses"]');

    // Initialize checkbox visibility on page load
    this.initializeCheckboxes();

    // Initialize selectboxes
    this.initializeAddressSelectboxes();

    // Set up mutation observer for dynamic content
    this.setupMutationObserver();

    this.bindEvents();
  }

  disconnectedCallback() {
    if (this.delegateElement) {
      this.delegateElement.off();
    }
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  initializeCheckboxes() {
    // Set initial visibility for add form checkbox
    const addCheckboxContainer = this.addForm?.querySelector('[data-checkbox-container="new"]');



    if (addCheckboxContainer) {
      const hasAddresses = window.customerAddresses && Object.keys(window.customerAddresses).length > 0;



      addCheckboxContainer.style.display = hasAddresses ? 'flex' : 'none';


    } else {
      console.warn('Add checkbox container not found during initialization');
    }

    // Always hide edit form checkbox
    const editCheckboxContainer = this.editForm?.querySelector('[data-checkbox-container="edit"]');
    if (editCheckboxContainer) {
      editCheckboxContainer.style.display = 'none';
    }
  }

  bindEvents() {
    this.delegateElement.on('click', '[data-action="edit-address"]', this.handleEditAddress.bind(this));
    this.delegateElement.on('click', '[data-action="delete-address"]', this.handleDeleteAddress.bind(this));

    // Handle address default checkbox toggle
    this.delegateElement.on('click', '.address-form__checkbox', this.handleCheckboxToggle.bind(this));

    // Handle validation for country select on add new address form
    this.delegateElement.on('click', '[data-action="submit-add-form"]', this.validateCountry.bind(this));

    // Intercept add address button clicks to prepare form before opening
    // Use capture phase to run before other handlers
    document.addEventListener('click', (e) => {
      const button = e.target.closest('[aria-controls="modal-address-new"][aria-label="open"]');
      if (button) {

        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        this.handleOpenAddModal();
      }
    }, true); // Use capture phase

    // Handle form submission for edit modal
    if (this.editForm) {
      const submitButtonMobile = this.editModal.querySelector('.popup-subfooter [data-action="submit-address-form"]');
      const submitButtonDesktop = this.editModal.querySelector('.popup-footer [data-action="submit-address-form"]');
      if (submitButtonMobile && submitButtonDesktop) {
        submitButtonMobile.addEventListener('click', this.handleSubmitEditForm.bind(this));
        submitButtonDesktop.addEventListener('click', this.handleSubmitEditForm.bind(this));
      }
    }

    // Handle form submission for add modal
    if (this.addForm) {
      const submitButtonMobile = this.addModal.querySelector('.popup-subfooter [data-action="submit-add-form"]');
      const submitButtonDesktop = this.addModal.querySelector('.popup-footer [data-action="submit-add-form"]');
      if (submitButtonMobile && submitButtonDesktop) {
        submitButtonMobile.addEventListener('click', this.handleSubmitAddForm.bind(this));
        submitButtonDesktop.addEventListener('click', this.handleSubmitAddForm.bind(this));
      }
    }
  }

  validateCountry(e) {
    e.preventDefault();
    const form = document.querySelector('#address_form_new');
    const select = form.querySelector('[name="address[country]"]');
    const selectedValue = select.value;

    // Check if value is empty, disabled option, or "---"
    if (
      selectedValue === undefined ||
      selectedValue === null ||
      selectedValue === "" ||
      selectedValue === "---" ||
      selectedValue === "{{ 'customer.addresses.country' | t }}") {
      // Set custom validity message
      select.setCustomValidity('Please select a valid country');
    } else {
      // Clear custom validity
      select.setCustomValidity('');
    }

    // Trigger native browser validation popup
    if (!select.checkValidity()) {
      select.reportValidity(); // This shows the native browser popup
      return false;
    }

    return true;
  }

  handleSubmitAddForm(event) {
    event.preventDefault();

    if (!this.addForm) return;

    // First validate country
    if (!this.validateCountry(event)) {
      return;
    }

    // Then validate rest of the form
    if (!this.addForm.checkValidity()) {
      this.addForm.reportValidity();
      return;
    }

    // If all validations pass, submit the form
    this.addForm.submit();
  }

  handleCheckboxToggle(event) {
    const label = event.target.closest('.address-form__checkbox');
    if (!label) return;

    const checkbox = label.querySelector('#address_default_new, #address_default_edit');
    if (checkbox) {
      checkbox.checked = !checkbox.checked;
    }
  }

  initializeAddressSelectboxes() {
    document.querySelectorAll('.address-selectbox').forEach(wrapper => {
      if (!wrapper.dataset.initialized) {
        const select = wrapper.querySelector('select');
        const contentDiv = wrapper.querySelector('.RevolutionSelectbox--Content');
        const titleSpan = wrapper.querySelector('.RevolutionSelectbox--Title');

        if (select && contentDiv && titleSpan) {
          select.classList.add('selectboxInputs');
          contentDiv.innerHTML = '';

          const options = Array.from(select.options);
          options.forEach((option, index) => {
            if (option.value) {
              const button = document.createElement('button');
              button.className = 'RevolutionSelectbox--Item u-p2';
              button.setAttribute('data-value', option.value);
              button.setAttribute('data-action', 'selectbox-change');
              button.setAttribute('data-selectboxid', select.id);
              button.setAttribute('type', 'button');
              button.textContent = option.textContent;

              if (!option.id) {
                option.id = `${select.id}-option-${index}`;
              }

              if (option.selected) {
                button.setAttribute('selected', 'selected');
                button.setAttribute('disabled', 'true');
                titleSpan.textContent = option.textContent;
              }

              contentDiv.appendChild(button);
            }
          });

          if (!titleSpan.textContent && options.length > 0 && options[0].textContent) {
            titleSpan.textContent = options[0].textContent;
          }
        }

        new RevolutionSelectbox(wrapper);
        wrapper.dataset.initialized = 'true';
      }
    });
  }

  setupMutationObserver() {
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.addedNodes.length) {
          this.initializeAddressSelectboxes();
        }
      });
    });

    if (this.editModal) {
      this.observer.observe(this.editModal, { childList: true, subtree: true });
    }
    if (this.addModal) {
      this.observer.observe(this.addModal, { childList: true, subtree: true });
    }
  }

  handleOpenAddModal() {
    if (!this.addForm || !this.addModal) return;

    // Reset form
    this.addForm.reset();

    // Show/hide checkbox based on whether customer has addresses
    const checkboxContainer = this.addForm.querySelector('[data-checkbox-container="new"]');
    if (checkboxContainer) {
      const hasAddresses = window.customerAddresses && Object.keys(window.customerAddresses).length > 0;
      const shouldShow = hasAddresses;





      checkboxContainer.style.display = shouldShow ? 'flex' : 'none';
    } else {
      console.warn('Add Modal - Checkbox container not found');
    }


  }

  handleEditAddress(event) {
    event.preventDefault();
    const button = event.target.closest('[data-action="edit-address"]');
    const addressId = button?.getAttribute('data-address-id');

    if (!addressId || !this.editModal || !this.editForm) return;

    // Get address data from window.customerAddresses
    const addressData = window.customerAddresses?.[addressId];
    if (!addressData) {
      console.error('Address data not found for ID:', addressId);
      return;
    }

    // Populate form fields
    this.populateEditForm(addressData);

    // Set form action to update this specific address
    this.editForm.action = `/account/addresses/${addressId}`;

    // Open the modal
    window.globalPopup?.openPopup(this.editModal);
  }

  populateEditForm(addressData) {
    if (!this.editForm) return;

    // Populate each field using data-field attributes
    const fields = ['first_name', 'last_name', 'phone', 'address1', 'address2', 'city', 'zip', 'country'];

    fields.forEach(fieldName => {
      const input = this.editForm.querySelector(`[data-field="${fieldName}"]`);
      if (input) {
        const value = addressData[fieldName] || '';
        if (input.tagName === 'SELECT') {
          // For select elements, set the selected option
          Array.from(input.options).forEach(option => {
            option.selected = option.value === value || option.text === value;
          });

          // Update RevolutionSelectbox UI
          this.updateSelectboxUI(input);
        } else {
          input.value = value;
        }
      }
    });

    // Always hide checkbox on edit form - there's a separate button to set default
    const checkboxContainer = this.editForm.querySelector('[data-checkbox-container="edit"]');
    if (checkboxContainer) {
      checkboxContainer.style.display = 'none';
    }
  }

  updateSelectboxUI(selectElement) {
    const wrapper = selectElement.closest('.RevolutionSelectbox');
    if (!wrapper) return;

    const titleSpan = wrapper.querySelector('.RevolutionSelectbox--Title');
    const contentDiv = wrapper.querySelector('.RevolutionSelectbox--Content');

    if (!titleSpan || !contentDiv) return;

    // Get the selected option
    const selectedOption = selectElement.options[selectElement.selectedIndex];
    if (!selectedOption) return;

    // Update title text
    titleSpan.textContent = selectedOption.textContent;

    // Update button states in dropdown
    const buttons = contentDiv.querySelectorAll('.RevolutionSelectbox--Item');
    buttons.forEach(button => {
      const buttonValue = button.getAttribute('data-value');
      if (buttonValue === selectedOption.value) {
        button.setAttribute('selected', 'selected');
        button.setAttribute('disabled', 'true');
      } else {
        button.removeAttribute('selected');
        button.removeAttribute('disabled');
      }
    });
  }

  handleSubmitEditForm(event) {
    event.preventDefault();

    if (!this.editForm) return;

    // Validate form
    if (!this.editForm.checkValidity()) {
      this.editForm.reportValidity();
      return;
    }

    this.editForm.submit();
  }

  handleDeleteAddress(event) {
    event.preventDefault();
    const button = event.target.closest('[data-action="delete-address"]');
    const addressId = button?.getAttribute('data-id');

    if (!addressId) return;

    const confirmed = confirm(
      TranslationUtils.get('customer.addresses.delete_confirm', 'Are you sure you want to delete this address?')
    );

    if (!confirmed) return;

    // Use Shopify.postLink for address deletion (follows Printworks pattern)
    Shopify.postLink(`/account/addresses/${addressId}`, {
      parameters: { _method: 'delete' }
    });

    setTimeout(() => {
      const currentUrl = new URL(window.location.href);
      window.location.href = currentUrl.href;
    }, 500);
  }
}

customElements.define('account-addresses', AccountAddresses);

// Account Orders Custom Element
class AccountOrders extends AccountTabsBase {
  constructor() {
    super();
    this.contentContainer = null;
    this.pageSize = 10;
    this.tabPages = {};
  }

  connectedCallback() {
    this.delegateElement = new Delegate(this.element);
    this.contentContainer = this.element.querySelector('.account-tab__content');

    const ordersList = this.element.querySelector('.account-orders__list');
    if (ordersList && ordersList.dataset.ordersPageSize) {
      this.pageSize = parseInt(ordersList.dataset.ordersPageSize, 10) || 10;
    }

    this.initPagination();
    this.bindEvents();
  }

  disconnectedCallback() {
    if (this.delegateElement) {
      this.delegateElement.off();
    }
  }

  initPagination() {
    if (!this.contentContainer) return;

    this.contentContainer.querySelectorAll('[data-orders-content]').forEach(tabContent => {
      const tabName = tabContent.dataset.ordersContent;
      const cards = tabContent.querySelectorAll('.account-order-card');
      const totalItems = cards.length;
      const totalPages = Math.ceil(totalItems / this.pageSize);

      this.tabPages[tabName] = { currentPage: 1, totalPages, totalItems };

      this.applyPageVisibility(tabName);
      this.renderOrdersPagination(tabName);
    });
  }

  bindEvents() {
    this.delegateElement.on('click', '[data-action="change-tab"]', this.handleChangeTab.bind(this));
    this.delegateElement.on('click', '[data-action="orders-prev-page"]', this.handlePrevPage.bind(this));
    this.delegateElement.on('click', '[data-action="orders-next-page"]', this.handleNextPage.bind(this));
    this.delegateElement.on('click', '[data-action="orders-page-number"]', this.handlePageNumber.bind(this));
  }

  handleChangeTab(event) {
    const tab = event.target.closest('[data-action="change-tab"]');
    if (!tab) return;

    const targetTab = tab.dataset.tab;
    if (!targetTab) return;

    this.updateActiveTab(targetTab);

    if (this.contentContainer) {
      this.contentContainer.querySelectorAll('[data-orders-content]').forEach(content => {
        if (content.dataset.ordersContent === targetTab) {
          content.removeAttribute('hidden');
          content.setAttribute('aria-hidden', 'false');
        } else {
          content.setAttribute('hidden', '');
          content.setAttribute('aria-hidden', 'true');
        }
      });
    }
  }

  getActiveTab() {
    const visibleContent = this.contentContainer?.querySelector('[data-orders-content]:not([hidden])');
    return visibleContent ? visibleContent.dataset.ordersContent : 'active-orders';
  }

  handlePrevPage() {
    const tab = this.getActiveTab();
    const state = this.tabPages[tab];
    if (state && state.currentPage > 1) {
      state.currentPage--;
      this.applyPageVisibility(tab);
      this.renderOrdersPagination(tab);
    }
  }

  handleNextPage() {
    const tab = this.getActiveTab();
    const state = this.tabPages[tab];
    if (state && state.currentPage < state.totalPages) {
      state.currentPage++;
      this.applyPageVisibility(tab);
      this.renderOrdersPagination(tab);
    }
  }

  handlePageNumber(event) {
    const btn = event.target.closest('[data-action="orders-page-number"]');
    if (!btn) return;

    const page = parseInt(btn.dataset.page, 10);
    if (!page) return;

    const tab = this.getActiveTab();
    const state = this.tabPages[tab];
    if (state && page >= 1 && page <= state.totalPages) {
      state.currentPage = page;
      this.applyPageVisibility(tab);
      this.renderOrdersPagination(tab);
    }
  }

  applyPageVisibility(tabName) {
    const state = this.tabPages[tabName];
    if (!state) return;

    const tabContent = this.contentContainer?.querySelector(`[data-orders-content="${tabName}"]`);
    if (!tabContent) return;

    const cards = tabContent.querySelectorAll('.account-order-card');
    const startIndex = (state.currentPage - 1) * this.pageSize;
    const endIndex = startIndex + this.pageSize;

    cards.forEach((card, index) => {
      if (index >= startIndex && index < endIndex) {
        card.style.display = '';
      } else {
        card.style.display = 'none';
      }
    });
  }

  renderOrdersPagination(tabName) {
    const state = this.tabPages[tabName];
    if (!state) return;

    const container = this.contentContainer?.querySelector(`[data-orders-pagination="${tabName}"]`);
    if (!container) return;

    if (state.totalPages <= 1) {
      container.innerHTML = '';
      return;
    }

    const { currentPage, totalPages, totalItems } = state;
    const hasPrev = currentPage > 1;
    const hasNext = currentPage < totalPages;
    const pages = this.getVisiblePages(currentPage, totalPages);

    let paginationHTML = '';

    if (hasPrev) {
      paginationHTML += `
        <li class="mr-m">
          <button class="pagination-arrow" data-action="orders-prev-page" aria-label="${TranslationUtils.get('pagination.previous', 'Previous')}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M5.625 13.125L2.5 10M2.5 10L5.625 6.875M2.5 10H17.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </li>`;
    }

    pages.forEach(pageNum => {
      if (pageNum === '...') {
        paginationHTML += `
        <li class="Pagination--ItemWrapper">
          <span class="Pagination--Item">...</span>
        </li>`;
      } else {
        const isCurrent = pageNum === currentPage;
        if (isCurrent) {
          paginationHTML += `
        <li class="Pagination--ItemWrapper">
          <span class="Pagination--Item Pagination--ItemCurrent u-p5" aria-current="page" aria-label="${TranslationUtils.get('pagination.page', 'Page')} ${pageNum}">${pageNum}</span>
        </li>`;
        } else {
          paginationHTML += `
        <li class="Pagination--ItemWrapper">
          <button class="Pagination--Item link u-p5" data-action="orders-page-number" data-page="${pageNum}" aria-label="${TranslationUtils.get('pagination.page', 'Page')} ${pageNum}">${pageNum}</button>
        </li>`;
        }
      }
    });

    if (hasNext) {
      paginationHTML += `
        <li>
          <button class="pagination-arrow" data-action="orders-next-page" aria-label="${TranslationUtils.get('pagination.next', 'Next')}">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M14.375 6.875L17.5 10M17.5 10L14.375 13.125M17.5 10H2.5" stroke="currentColor" stroke-width="1.25" stroke-linecap="round" stroke-linejoin="round" />
            </svg>
          </button>
        </li>`;
    }

    const endItem = Math.min(currentPage * this.pageSize, totalItems);
    const infoText = TranslationUtils.get('pagination.product_count', '{current_count} out of {complete_count}')
      .replace('{current_count}', endItem)
      .replace('{complete_count}', totalItems);

    container.innerHTML = `
      <div class="Pagination-Wrapper">
        <nav class="pagination" role="navigation" aria-label="${TranslationUtils.get('pagination.label', 'Pagination')}">
          <ul class="Pagination--List" role="list">
            ${paginationHTML}
          </ul>
        </nav>
        <div class="main-collection__product-count u-s4">
          ${infoText}
        </div>
      </div>
    `;
  }

  getVisiblePages(currentPage, totalPages) {
    const pages = [];
    const maxVisible = 5;

    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 4) {
          pages.push('...');
        }
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...');
        pages.push(totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...');
        pages.push(currentPage - 1, currentPage, currentPage + 1);
        pages.push('...');
        pages.push(totalPages);
      }
    }

    return pages;
  }
}

if (!customElements.get('account-orders')) {
  customElements.define('account-orders', AccountOrders);
}

